import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getCompany, recalc, translateSummary, shareUpload, companyPlan, thesisRefreshRun } from "@/lib/api";
import { api } from "@/lib/api";
import { fmtNum, fmtPct, fmtPrice, fmtPctSigned, fmtCompact, ratioColor, signalLabel } from "@/lib/format";
import LocaleNumberInput from "@/components/LocaleNumberInput";
import { saveToWatchlist, removeFromWatchlist, getWatchlistEntry } from "@/lib/storage";
import { getPortfolioEntry, savePortfolioOverrides, removePosition } from "@/lib/portfolio";
import { computeCustomRatios, autoInputsFromData, valuesEqual, computeOverrides } from "@/lib/customRatios";
import { MetricsRadar } from "@/components/MetricsRadar";
import { useThresholds } from "@/lib/useThresholds";
import { useFx } from "@/lib/fx";
import { useAuth } from "@/lib/auth";
import HoverTip from "@/components/HoverTip";
import { CompanyLogo, useCompanyIdentity, siteLabel, companyColorProps } from "@/components/CompanyIdentity";
import CurrencySelector from "@/components/CurrencySelector";
import CompanyQualCard from "@/components/thesis/CompanyQualCard";
import MetricHistoryChart, { metricDragProps, useMetricHistory, cagrSeries, marketCapSeries, fmtBig, PALETTE, Y_AXIS_W, axisTicks, EdgeTick, useWidth, useIsDark } from "@/components/MetricHistoryChart";
import { Star, RefreshCw, AlertCircle, Save, X, Briefcase, Maximize2, ArrowLeftRight, Download, Loader2, TrendingUp, Info, ExternalLink, Sparkles, GripVertical, BarChart3, LineChart as LineChartIcon } from "lucide-react";
import { toast } from "sonner";
import { downloadSvgJpg, getSvgJpgBlob } from "@/lib/chartExport";
import ShareMenu from "@/components/ShareMenu";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, ComposedChart } from "recharts";

const ratioRows = [
    ["Trailing P/E", "trailing_pe", null, "Precio entre beneficio neto últimos 12m. Mide cuántos años de beneficios actuales pagas por la acción. < 15 suele ser barato, 15-25 normal, > 30 caro. Ojo: muy dependiente del sector y del momento del ciclo."],
    ["Forward P/E", "forward_pe", null, "Precio entre beneficio esperado a 12m vista. Más útil que el trailing si la empresa va a crecer/decrecer mucho. Usa las estimaciones de analistas (revisa Forward P/E vs Trailing para ver si esperan mejora)."],
    ["PEG Ratio", "peg_ratio", null, "Forward P/E dividido por el crecimiento esperado de beneficios. < 1 sugiere infravalorado vs su crecimiento, 1-2 razonable, > 2 caro. Inventado por Peter Lynch."],
    ["P/B", "price_to_book", null, "Precio entre valor contable (patrimonio neto / acciones). < 1 cotiza por debajo del valor contable. Útil en bancos e industria pesada; poco útil en tech (mucho intangible no contabilizado)."],
    ["P/S", "price_to_sales", null, "Precio entre ventas por acción. Útil cuando no hay beneficio (early stage, ciclos bajos). < 1 barato, 1-3 normal, > 10 muy caro salvo crecimiento extremo."],
    ["EV/EBITDA", "ev_to_ebitda", null, "Enterprise Value (mcap + deuda neta) entre EBITDA. Más justo que P/E porque normaliza estructura de capital e impuestos. < 8 barato, 8-15 normal, > 20 caro."],
    ["EV/Revenue", "ev_to_revenue", null, "Enterprise Value entre ventas. Variante de P/S que incluye la deuda. Útil para empresas con poca rentabilidad o pre-beneficio."],
    ["ROE", "roe", "pct", "Return on Equity: beneficio neto / patrimonio. Cuánto rinde el capital propio. > 15% bueno, > 20% excelente. Cuidado: un ROE alto con mucha deuda puede ser frágil."],
    ["ROA", "roa", "pct", "Return on Assets: beneficio neto / activos totales. Mide eficiencia del balance completo. > 5% decente, > 10% muy bueno. Mejor que ROE para comparar empresas con apalancamientos distintos."],
    ["ROIC", "roic", "pct", "Return on Invested Capital: NOPAT (EBIT tras impuestos) / capital invertido (patrimonio + deuda − caja). Mide el retorno del capital realmente puesto a trabajar. > 10% crea valor si supera el coste del capital (WACC ~8-10%); > 15% excelente. Calculado a partir de los estados financieros."],
    ["ROCE", "roce", "pct", "Return on Capital Employed: EBIT / capital empleado (activos totales − pasivo corriente). Similar al ROIC pero antes de impuestos y sobre todo el capital operativo. > 15% muy bueno. Útil en industriales y capital-intensivas. Calculado a partir del balance."],
    ["Profit margin", "profit_margin", "pct", "Beneficio neto / ingresos. > 10% saludable, > 20% excelente. Negativos = pérdidas. Compara siempre dentro del mismo sector (retail ~3%, software ~20%)."],
    ["Debt/Equity", "debt_to_equity", null, "Deuda total / patrimonio. < 0,5 conservador, 0,5-1,5 normal, > 2 apalancado. Depende mucho del sector (utilities y bancos toleran más)."],
    ["Current ratio", "current_ratio", null, "Activo corriente / pasivo corriente. Liquidez a corto plazo. > 1,5 saludable, < 1 riesgo. > 3 puede indicar caja parada sin uso productivo."],
    ["Dividend yield", "dividend_yield", "pct", "Dividendo anual / precio. 0% en growth, 2-4% normal, > 6% sospechoso (puede ser trampa de valor o dividendo en peligro)."],
    ["Beta", "beta", null, "Volatilidad relativa al mercado. 1 = se mueve como el índice, < 1 menos volátil (defensivas), > 1 más volátil (tech, ciclícas). Beta histórico no predice futuro perfectamente."],
    ["Analyst target", "target_mean_price", null, "Precio objetivo medio de los analistas que cubren el valor. Útil como referencia pero ten en cuenta que suelen ser optimistas y reaccionar tarde a malas noticias."],
];

// Growth metrics — especially relevant for small high-growth companies that are
// not yet profitable. Source: data.growth_metrics (computed in backend).
const growthRows = [
    ["Crec. Ingresos (YoY)", "revenue_growth_yoy", "pct", "Crecimiento de ingresos del último año fiscal sobre el anterior. Para growth real ≥ 20% es bueno, ≥ 40% excelente. < 0% es contracción."],
    ["CAGR Ingresos 3y (histórico)", "revenue_cagr_3y_hist", "pct", "Crecimiento anual compuesto histórico de los últimos años. Más estable que el YoY puntual; suaviza altibajos. Compara con el YoY para ver si la empresa acelera o desacelera."],
    ["Margen FCF (TTM)", "fcf_margin_ttm", "pct", "Free Cash Flow / Ingresos. Mide cuánto efectivo libre genera cada euro de ventas. > 15% sano, > 25% excepcional. Negativo = quema caja (típico en early-stage)."],
    ["Rule of 40", "rule_of_40", "rule40", "Suma de Crec. Ingresos % + Margen FCF %. Indicador clásico de salud para SaaS y growth: < 20 débil, 20-40 mejorable, > 40 saludable, > 60 excepcional. Equilibra crecimiento y rentabilidad."],
];

// The company's figures (left column of the ratios, 16-oct-2026): the latest reported 12
// months (or fiscal year) from /metric-history, the price from the ficha. Draggable into
// the history chart like the ratios.
const figureRows = [
    ["Precio", "price", "price", "Último cierre de la acción. En el gráfico es un botón fijo, activo por defecto: el cierre del mes de cada fecha."],
    ["Ingresos", "revenue", "money", "Ingresos de los últimos 12 meses publicados (cifra de negocios; en bancos, su margen bruto: intereses netos + comisiones + dividendos)."],
    ["Free Cash Flow", "fcf", "money", "Flujo de caja libre de los últimos 12 meses: flujo operativo − CapEx (en bancos, el beneficio repartido)."],
    ["Beneficio neto", "net_income", "money", "Beneficio neto atribuido a los accionistas de los últimos 12 meses."],
    ["Resultado operativo", "op_income", "money", "Beneficio operativo (antes de intereses e impuestos) de los últimos 12 meses; en bancos, el beneficio antes de impuestos."],
    ["Gasto en I+D", "rnd", "money", "Gasto en investigación y desarrollo de los últimos 12 meses (si la empresa lo publica en su cuenta de resultados)."],
    ["CapEx", "capex", "money", "Inversión en inmovilizado (compras de propiedades, planta y equipo) de los últimos 12 meses."],
    ["Flujo operativo (FCO)", "ocf", "money", "Flujo de caja de las operaciones de los últimos 12 meses."],
    ["Nº de acciones", "shares", "shares", "Acciones diluidas medias del último periodo publicado (en ADS para las extranjeras que cotizan con certificados)."],
    ["Deuda neta", "net_debt", "money", "Deuda financiera (con convertibles) + arrendamientos fuera del flujo operativo − efectivo e inversiones a corto, en la última fecha publicada. Negativa = caja neta. No aplica a bancos."],
];

// Metrics that can be dragged into the history chart, with their unit. Forward-looking
// ones (Forward P/E, PEG, analyst target) and Beta have no reported history.
const NO_HISTORY = new Set(["forward_pe", "peg_ratio", "beta", "target_mean_price"]);
const METRIC_CATALOG = [
    ...figureRows.map(([label, key, unit]) => ({ key, label, unit, group: "Cifras de la empresa" })),
    ...ratioRows.filter(([, key]) => !NO_HISTORY.has(key))
        .map(([label, key, fmt]) => ({ key, label, unit: fmt === "pct" ? "pct" : "x", group: "Ratios clásicos" })),
    { key: "gross_margin", label: "Gross margin", unit: "pct", group: "Ratios clásicos" },
    { key: "operating_margin", label: "Operating margin", unit: "pct", group: "Ratios clásicos" },
    ...growthRows.map(([label, key, fmt]) => ({ key, label, unit: fmt === "rule40" ? "pts" : fmt === "pct" ? "pct" : "x", group: "Crecimiento y rentabilidad futura" })),
    // The formula's components («Inputs y proyecciones», 17-oct-2026): their reported
    // history, and for the +2-year ones a dashed stretch to the projected value.
    { key: "revenue_2y", label: "Ingresos (+ proyección 2a)", unit: "money", source: "revenue", group: "Inputs y proyecciones" },
    { key: "fcf_2y", label: "FCF (+ proyección 2a)", unit: "money", source: "fcf", group: "Inputs y proyecciones" },
    { key: "market_cap", label: "Capitalización", unit: "money", derive: marketCapSeries, group: "Inputs y proyecciones" },
    { key: "revenue_cagr_4y", label: "CAGR ingresos 4a", unit: "pct", derive: (b) => cagrSeries(b, "revenue"), group: "Inputs y proyecciones" },
    { key: "fcf_cagr_4y", label: "CAGR FCF 4a", unit: "pct", derive: (b) => cagrSeries(b, "fcf"), group: "Inputs y proyecciones" },
];
// Input of the formula → the series it drags into the chart (the others are already in
// the catalog under their own key).
const INPUT_METRIC = {
    revenue_2y: "revenue_2y", fcf_2y: "fcf_2y", shares_outstanding: "shares", gross_margin: "gross_margin",
    operating_margin: "operating_margin", net_debt: "net_debt", market_cap: "market_cap",
    revenue_cagr_4y: "revenue_cagr_4y", fcf_cagr_4y: "fcf_cagr_4y", current_price: "price",
};
const METRIC_KEYS = new Set(METRIC_CATALOG.map((m) => m.key));

const marginRowsInfo = {
    gross_margin: "Ingresos − coste directo del producto, en %. Mide eficiencia productiva. > 40% suele ser bueno (escala, marca, software). En retail/manufactura puede ser <25% sin que sea mala señal.",
    operating_margin: "Beneficio operativo (antes de impuestos e intereses) / ingresos. Mide eficiencia del negocio core. > 15% sólido, > 25% excelente. Negativo = pierde dinero operando.",
};

// Common Yahoo Finance exchange codes → human-readable name.
// Lookup is case-insensitive and matches the full code returned by yfinance.
const EXCHANGE_NAMES = {
    NMS: "NASDAQ Global Select Market",
    NGM: "NASDAQ Global Market",
    NCM: "NASDAQ Capital Market",
    NAS: "NASDAQ",
    NASDAQ: "NASDAQ",
    NYQ: "New York Stock Exchange (NYSE)",
    NYE: "New York Stock Exchange (NYSE)",
    ASE: "NYSE American (antes AMEX)",
    PCX: "NYSE Arca",
    BATS: "Cboe BZX Exchange",
    BTS: "Cboe BZX Exchange",
    OTC: "OTC Markets (extrabursátil EE. UU.)",
    OQB: "OTCQB Venture Market",
    OQX: "OTCQX Best Market",
    PNK: "Pink Sheets (OTC)",
    LSE: "London Stock Exchange",
    LON: "London Stock Exchange",
    IOB: "International Order Book (LSE)",
    MCE: "Bolsa de Madrid (BME)",
    BME: "Bolsas y Mercados Españoles",
    GER: "Deutsche Börse Xetra",
    XETR: "Deutsche Börse Xetra",
    FRA: "Bolsa de Fráncfort",
    AMS: "Euronext Amsterdam",
    PAR: "Euronext París",
    EBR: "Euronext Bruselas",
    LIS: "Euronext Lisboa",
    MIL: "Borsa Italiana (Milán)",
    BIT: "Borsa Italiana (Milán)",
    SWX: "SIX Swiss Exchange",
    EBS: "SIX Swiss Exchange",
    VIE: "Wiener Börse (Viena)",
    STO: "Nasdaq Estocolmo",
    HEL: "Nasdaq Helsinki",
    CPH: "Nasdaq Copenhague",
    OSL: "Oslo Børs",
    WSE: "Bolsa de Varsovia",
    ATH: "Bolsa de Atenas",
    IST: "Bolsa de Estambul",
    JNB: "Johannesburg Stock Exchange",
    MOEX: "Bolsa de Moscú",
    BUE: "Bolsa de Buenos Aires",
    SAO: "B3 (Bolsa de São Paulo)",
    MEX: "Bolsa Mexicana de Valores",
    SGO: "Bolsa de Santiago de Chile",
    TOR: "Toronto Stock Exchange",
    TSX: "Toronto Stock Exchange",
    VAN: "TSX Venture Exchange",
    CNQ: "Canadian Securities Exchange",
    NEO: "NEO Exchange (Canadá)",
    ASX: "Australian Securities Exchange",
    NZE: "New Zealand Stock Exchange",
    TSE: "Tokyo Stock Exchange",
    TYO: "Tokyo Stock Exchange",
    OSE: "Osaka Exchange",
    HKG: "Bolsa de Hong Kong",
    HKE: "Bolsa de Hong Kong",
    SHH: "Bolsa de Shanghái",
    SHZ: "Bolsa de Shenzhen",
    KSC: "KOSPI (Bolsa de Seúl)",
    KOE: "KOSDAQ",
    TAI: "Bolsa de Taiwán",
    SES: "Bolsa de Singapur",
    KLS: "Bolsa de Malasia",
    JKT: "Bolsa de Indonesia",
    BSE: "Bombay Stock Exchange",
    NSI: "National Stock Exchange of India",
    BKK: "Bolsa de Tailandia",
    TLV: "Bolsa de Tel Aviv",
    SAU: "Tadawul (Bolsa Saudí)",
    DFM: "Bolsa de Dubái",
};
const exchangeFullName = (code) => {
    if (!code) return null;
    const key = String(code).toUpperCase().trim();
    return EXCHANGE_NAMES[key] || null;
};

// Health colour for a given ratio key + value. Returns {color, label} or null
// when no rule applies (e.g. Beta, Analyst target — they don't have a clean good/bad axis).
const G = "#1D7044", A = "#D97706", R = "#B32A22";
const ratioHealth = (key, v) => {
    if (v == null || isNaN(v)) return null;
    switch (key) {
        case "trailing_pe":
        case "forward_pe":
            if (v < 0) return { color: R, label: "Negativo (pérdidas)" };
            if (v < 15) return { color: G, label: "Barato" };
            if (v < 25) return { color: A, label: "Normal" };
            return { color: R, label: "Caro" };
        case "peg_ratio":
            if (v <= 0) return { color: R, label: "No utilizable" };
            if (v < 1) return { color: G, label: "Infravalorado vs crecimiento" };
            if (v < 2) return { color: A, label: "Razonable" };
            return { color: R, label: "Caro vs crecimiento" };
        case "price_to_book":
            if (v < 0) return { color: R, label: "Patrimonio negativo" };
            if (v < 1) return { color: G, label: "Por debajo de book value" };
            if (v < 3) return { color: A, label: "Normal" };
            return { color: R, label: "Alto" };
        case "price_to_sales":
            if (v < 1) return { color: G, label: "Barato" };
            if (v < 5) return { color: A, label: "Normal" };
            return { color: R, label: "Caro" };
        case "ev_to_ebitda":
            if (v < 0) return { color: R, label: "EBITDA negativo" };
            if (v < 8) return { color: G, label: "Barato" };
            if (v < 15) return { color: A, label: "Normal" };
            return { color: R, label: "Caro" };
        case "ev_to_revenue":
            if (v < 2) return { color: G, label: "Barato" };
            if (v < 10) return { color: A, label: "Normal" };
            return { color: R, label: "Caro" };
        case "roe":
            if (v < 0) return { color: R, label: "Pérdidas" };
            if (v < 0.08) return { color: R, label: "Bajo" };
            if (v < 0.15) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "roa":
            if (v < 0) return { color: R, label: "Pérdidas" };
            if (v < 0.02) return { color: R, label: "Bajo" };
            if (v < 0.05) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "roic":
            if (v < 0) return { color: R, label: "Destruye valor" };
            if (v < 0.07) return { color: R, label: "Bajo" };
            if (v < 0.15) return { color: A, label: "Aceptable" };
            return { color: G, label: "Excelente" };
        case "roce":
            if (v < 0) return { color: R, label: "Pérdidas" };
            if (v < 0.10) return { color: R, label: "Bajo" };
            if (v < 0.15) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "profit_margin":
            if (v < 0) return { color: R, label: "Pérdidas" };
            if (v < 0.05) return { color: A, label: "Bajo" };
            if (v < 0.10) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "debt_to_equity":
            // yfinance returns this as % (e.g. 150 means 1.5). Normalize.
            { const vv = v > 10 ? v / 100 : v;
            if (vv < 0.5) return { color: G, label: "Conservador" };
            if (vv < 1.5) return { color: A, label: "Normal" };
            return { color: R, label: "Apalancado" }; }
        case "current_ratio":
            if (v < 1) return { color: R, label: "Riesgo liquidez" };
            if (v < 1.5) return { color: A, label: "Justo" };
            if (v <= 3) return { color: G, label: "Saludable" };
            return { color: A, label: "Caja parada" };
        case "dividend_yield":
            if (v === 0) return null;
            if (v < 0.02) return { color: A, label: "Bajo" };
            if (v <= 0.05) return { color: G, label: "Saludable" };
            if (v <= 0.07) return { color: A, label: "Alto" };
            return { color: R, label: "Posible trampa" };
        case "gross_margin":
            if (v < 0) return { color: R, label: "Negativo" };
            if (v < 0.20) return { color: R, label: "Bajo" };
            if (v < 0.40) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "operating_margin":
            if (v < 0) return { color: R, label: "Pérdidas operativas" };
            if (v < 0.05) return { color: A, label: "Bajo" };
            if (v < 0.15) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "revenue_growth_yoy":
        case "revenue_cagr_3y_hist":
            if (v < 0) return { color: R, label: "Contracción" };
            if (v < 0.05) return { color: A, label: "Bajo" };
            if (v < 0.20) return { color: A, label: "Aceptable" };
            return { color: G, label: "Alto" };
        case "fcf_margin_ttm":
            if (v < 0) return { color: R, label: "Quema caja" };
            if (v < 0.05) return { color: A, label: "Bajo" };
            if (v < 0.15) return { color: A, label: "Aceptable" };
            return { color: G, label: "Bueno" };
        case "rule_of_40":
            if (v < 20) return { color: R, label: "Débil" };
            if (v < 40) return { color: A, label: "Mejorable" };
            if (v < 60) return { color: G, label: "Saludable" };
            return { color: G, label: "Excepcional" };
        default:
            return null;
    }
};

export default function Company() {
    const { ticker } = useParams();
    const identity = useCompanyIdentity(ticker);  // website + logo for the header
    // The user's company thesis for the ticker link: undefined = loading, null = could not check.
    const [plan, setPlan] = useState(undefined);
    const [data, setData] = useState(null);
    const { user } = useAuth();
    const [qualRefreshKey, setQualRefreshKey] = useState(0);
    const [qualProfile, setQualProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Watchlist state
    const [wlEntry, setWlEntry] = useState(null); // {ticker, mode, overrides, saved_at} or null
    const [pfEntry, setPfEntry] = useState(null); // portfolio position (also carries optional overrides)
    const [confirmOverwrite, setConfirmOverwrite] = useState(false);
    const [confirmOverwritePortfolio, setConfirmOverwritePortfolio] = useState(false);
    const [confirmRefresh, setConfirmRefresh] = useState(false);

    // Inputs lifecycle
    const [autoInputs, setAutoInputs] = useState(null);    // pure Yahoo values
    const [inputs, setInputs] = useState(null);            // current shown values (auto + saved overrides + session edits)
    const [sessionEdits, setSessionEdits] = useState({});  // {field: true} for fields edited this session
    const [customRatios, setCustomRatios] = useState(null);
    const [summaryEs, setSummaryEs] = useState(null); // translated description (null = not yet loaded)
    const [translating, setTranslating] = useState(false);
    const [monthlyPrice, setMonthlyPrice] = useState([]); // [{month: "2024-01", open, high, low, close}]
    const [qHist, setQHist] = useState(null);              // quarterly TTM payload (lazy, fetched on first toggle)
    const [qHistLoading, setQHistLoading] = useState(false);
    useThresholds(); // subscribe to threshold changes so colours/labels live-update
    const { display: displayCur, convert: fxConvert } = useFx();

    const hasSessionEdits = Object.keys(sessionEdits).length > 0;

    const load = useCallback(async (refresh = false, ignoreOverrides = false) => {
        try {
            setLoading(true); setError(null);
            const d = await getCompany(ticker, refresh);
            const auto = autoInputsFromData(d);
            const entry = ignoreOverrides ? null : getWatchlistEntry(ticker);
            const pf = ignoreOverrides ? null : getPortfolioEntry(ticker);
            setWlEntry(ignoreOverrides ? (getWatchlistEntry(ticker) || null) : entry);
            setPfEntry(ignoreOverrides ? (getPortfolioEntry(ticker) || null) : pf);
            // Apply saved overrides on top of auto values. Watchlist takes priority
            // over portfolio when both have overrides (user-facing convention).
            const merged = { ...auto };
            const savedOverrides = (entry && entry.overrides) || (pf && pf.overrides) || null;
            if (savedOverrides) {
                for (const [k, v] of Object.entries(savedOverrides)) {
                    if (k === "current_price") continue;
                    merged[k] = v;
                }
            }
            setData(d);
            setAutoInputs(auto);
            setInputs(merged);
            setSessionEdits({});
            setCustomRatios(computeCustomRatios(merged));
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Error al cargar");
        } finally { setLoading(false); }
    }, [ticker]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!user || !ticker) { setPlan(undefined); return undefined; }
        let alive = true;
        setPlan(undefined);
        companyPlan(ticker).then((d) => { if (alive) setPlan(d); }).catch(() => { if (alive) setPlan(null); });
        return () => { alive = false; };
    }, [user, ticker, qualRefreshKey]);

    // Fetch Spanish translation lazily once the company is loaded and has a summary.
    // The backend caches per ticker+source_hash so subsequent visits are instant.
    useEffect(() => {
        if (!data?.long_business_summary || !data?.ticker) return;
        let cancelled = false;
        setSummaryEs(null);
        setTranslating(true);
        translateSummary(data.ticker)
            .then(r => { if (!cancelled) setSummaryEs(r?.summary_es || null); })
            .catch(() => { /* fall back silently to the English version */ })
            .finally(() => { if (!cancelled) setTranslating(false); });
        return () => { cancelled = true; };
    }, [data?.ticker, data?.long_business_summary]);

    // Monthly prices for the header candles once the company is loaded. /price-history
    // replaces /ratio-history (POC/POV history chart retired 16-oct-2026); the fallback
    // covers a backend that doesn't serve it yet.
    useEffect(() => {
        if (!data?.ticker) return;
        let cancelled = false;
        setMonthlyPrice([]);
        setQHist(null); // quarterly cache belongs to the previous ticker
        const tk = encodeURIComponent(data.ticker);
        api.get(`/company/${tk}/price-history`)
            .catch((e) => { if (e?.response?.status === 404) return api.get(`/company/${tk}/ratio-history`); throw e; })
            .then(r => { if (!cancelled) setMonthlyPrice(r.data?.monthly_price || []); })
            .catch(() => { /* silently ignore — the header chart just hides */ });
        return () => { cancelled = true; };
    }, [data?.ticker]);

    // Lazy fetch of quarterly TTM data, triggered the first time a chart is toggled to TTM.
    const requestQuarterly = useCallback(() => {
        if (!data?.ticker || qHist || qHistLoading) return;
        setQHistLoading(true);
        api.get(`/company/${encodeURIComponent(data.ticker)}/quarterly-history`)
            .then(r => setQHist(r.data || null))
            .catch(() => toast.error("No se pudieron cargar los datos trimestrales"))
            .finally(() => setQHistLoading(false));
    }, [data?.ticker, qHist, qHistLoading]);

    // Navigation guard for unsaved session edits.
    // - Internal links: intercept anchor clicks; show modal.
    // - Tab close / external nav: beforeunload (below).
    const navigate = useNavigate();
    const [pendingNav, setPendingNav] = useState(null);

    useEffect(() => {
        if (!hasSessionEdits) return;
        const handler = (e) => {
            const anchor = e.target.closest && e.target.closest("a");
            if (!anchor) return;
            const href = anchor.getAttribute("href");
            if (!href) return;
            if (href.startsWith("http") || href.startsWith("#") || href.startsWith("mailto")) return;
            if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            if (href === window.location.pathname) return;
            e.preventDefault();
            e.stopPropagation();
            setPendingNav(href);
        };
        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
    }, [hasSessionEdits]);

    // beforeunload (tab close, refresh, external nav)
    useEffect(() => {
        const handler = (e) => {
            if (hasSessionEdits) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasSessionEdits]);

    const handleRefresh = () => {
        // Refresh re-fetches from Yahoo AND discards any saved overrides for this view.
        // If there's anything to lose, ask first.
        const hasSavedOverrides = wlEntry && wlEntry.overrides && Object.keys(wlEntry.overrides).length > 0;
        if (hasSessionEdits || hasSavedOverrides) {
            setConfirmRefresh(true);
            return;
        }
        doRefresh();
    };

    const doRefresh = async () => {
        setRefreshing(true);
        // If the ticker has saved overrides, downgrade the watchlist entry to "auto" (overrides removed).
        const hasSavedOverrides = wlEntry && wlEntry.overrides && Object.keys(wlEntry.overrides).length > 0;
        if (hasSavedOverrides) {
            saveToWatchlist(ticker, null); // resets entry to mode=auto, no overrides
        }
        await load(true, true); // refresh from Yahoo + ignore overrides
        // Refresh also recomputes the frozen TAM Scores across the company's theses so
        // the fresh fundamentals travel both ways (company ↔ theses).
        if (user) {
            try { await thesisRefreshRun({ ticker }); setQualRefreshKey((k) => k + 1); } catch { /* ignore */ }
        }
        setRefreshing(false);
        setConfirmRefresh(false);
        toast.success("Datos restaurados desde Yahoo");
    };

    // "Save snapshot" — captures current overrides (diff vs auto) and stores in watchlist
    const handleSaveToWatchlist = () => {
        if (!inputs || !autoInputs) return;
        const overrides = computeOverrides(inputs, autoInputs);
        // If already in watchlist, ask before overwriting
        if (wlEntry && (wlEntry.overrides || hasSessionEdits)) {
            setConfirmOverwrite(true);
            return;
        }
        const list = saveToWatchlist(ticker, overrides);
        const newEntry = list.find(e => e.ticker === ticker.toUpperCase());
        setWlEntry(newEntry);
        setSessionEdits({});
        toast.success(Object.keys(overrides).length ? "Guardada con tus overrides" : "Añadida a Nivel 2");
    };

    const doConfirmOverwrite = () => {
        const overrides = computeOverrides(inputs, autoInputs);
        const list = saveToWatchlist(ticker, overrides);
        const newEntry = list.find(e => e.ticker === ticker.toUpperCase());
        setWlEntry(newEntry);
        setSessionEdits({});
        setConfirmOverwrite(false);
        toast.success("Nivel 2 actualizado");
    };

    const handleRemoveFromWatchlist = () => {
        removeFromWatchlist(ticker);
        setWlEntry(null);
        toast("Quitada de Nivel 2");
    };

    const handleSaveToPortfolio = () => {
        const overrides = computeOverrides(inputs, autoInputs);
        // If already in portfolio with manual snapshot or session edits, prompt overwrite
        if (pfEntry && (pfEntry.overrides || hasSessionEdits)) {
            setConfirmOverwritePortfolio(true);
            return;
        }
        const list = savePortfolioOverrides(ticker, Object.keys(overrides).length ? overrides : null);
        const newEntry = list.find(e => (e.ticker || "").toUpperCase() === ticker.toUpperCase());
        setPfEntry(newEntry);
        setSessionEdits({});
        toast.success(Object.keys(overrides).length ? "Añadida a Nivel 1 con tus overrides" : "Añadida a Nivel 1");
    };

    const doConfirmOverwritePortfolio = () => {
        const overrides = computeOverrides(inputs, autoInputs);
        const list = savePortfolioOverrides(ticker, Object.keys(overrides).length ? overrides : null);
        const newEntry = list.find(e => (e.ticker || "").toUpperCase() === ticker.toUpperCase());
        setPfEntry(newEntry);
        setSessionEdits({});
        setConfirmOverwritePortfolio(false);
        toast.success("Nivel 1 actualizado");
    };

    const handleRemoveFromPortfolio = () => {
        removePosition(ticker);
        setPfEntry(null);
        toast("Quitada de Nivel 1");
    };

    const updateInput = (key, num) => {
        setInputs(prev => {
            const next = { ...prev, [key]: num };
            // Track as session edit only if it differs from the loaded state
            const savedFromWl = wlEntry?.overrides?.[key];
            const savedFromPf = pfEntry?.overrides?.[key];
            const loaded = (savedFromWl !== undefined) ? savedFromWl
                         : (savedFromPf !== undefined) ? savedFromPf
                         : autoInputs?.[key];
            setSessionEdits(prevEdits => {
                const ne = { ...prevEdits };
                if (!valuesEqual(num, loaded)) ne[key] = true;
                else delete ne[key];
                return ne;
            });
            setCustomRatios(computeCustomRatios(next));
            return next;
        });
    };

    const handleRecalc = async () => {
        // Recompute via backend for parity check (and to keep breakdown in sync)
        try {
            const r = await recalc(ticker, inputs);
            setCustomRatios(r);
            toast.success("Ratios recalculados");
        } catch (e) { toast.error("Error al recalcular"); }
    };

    const handleReset = () => {
        if (!data || !autoInputs) return;
        // Reset to the "loaded" state: auto + saved overrides (watchlist preferred)
        const merged = { ...autoInputs };
        const savedOverrides = wlEntry?.overrides || pfEntry?.overrides || null;
        if (savedOverrides) {
            for (const [k, v] of Object.entries(savedOverrides)) {
                if (k === "current_price") continue;
                merged[k] = v;
            }
        }
        setInputs(merged);
        setSessionEdits({});
        setCustomRatios(computeCustomRatios(merged));
    };

    // Determine status of each input field for color coding
    const fieldStatus = (key) => {
        if (sessionEdits[key]) return "session"; // unsaved session edit — amber
        if (wlEntry?.overrides && wlEntry.overrides[key] !== undefined) return "saved";
        if (pfEntry?.overrides && pfEntry.overrides[key] !== undefined) return "saved";
        return "auto"; // Yahoo default — neutral
    };

    if (loading) return <div className="py-20 text-center font-mono" data-testid="loading">Cargando {ticker}…</div>;
    if (error) return (
        <div className="border border-[#B32A22] bg-white p-8" data-testid="error-state">
            <AlertCircle className="text-[#B32A22] mb-2" />
            <div className="font-serif text-2xl mb-2">No se pudo cargar {ticker}</div>
            <div className="text-sm text-[#4A4A4A] mb-4">{error}</div>
            <Link to="/" className="btn-ghost">← Volver al inicio</Link>
        </div>
    );
    if (!data) return null;

    // «Nuestro» without usable SEC data shows no ratio (rather than Gratis's, which rests
    // on Yahoo's analysts): the note above the cards says why.
    const ownUnavailable = data.data_source?.requested === "own" && data.data_source?.used !== "own";
    const cr = ownUnavailable ? {} : (customRatios || {});
    const nativeCur = data.currency || "USD";
    // Effective currency for ALL price/monetary displays
    const cur = (displayCur === "NATIVE" || !displayCur) ? nativeCur : displayCur;
    // Convert a value from native currency to display currency; null-safe.
    const convertCur = (v) => (v == null || isNaN(v)) ? v : fxConvert(v, nativeCur);

    const nowY = new Date().getFullYear();
    // Projection horizon = last reported ANNUAL fiscal year + 2 (same anchor the charts use),
    // so the label always matches the +2y point on the revenue/FCF charts.
    const lastAnnualYear = (() => {
        const h = data?.revenue_history;
        if (h && h.length) { const y = parseInt(h[h.length - 1].date.slice(0, 4), 10); if (y) return y; }
        return nowY - 1;
    })();
    const projTargetYear = lastAnnualYear + 2;
    const _ap = data?.auto_projections || {};
    const ttmTargetQ = _ap.ttm_target_quarter;   // e.g. "2028Q1"
    // CAGR 4y footnotes: exactly 4 years, from 2 years before the latest reported quarter
    // (or fiscal close, on the annual horizon) to the +2y target — the same window as the
    // POC/POV history chart. For FCF the fallback path (non-positive start) anchors on the
    // latest FCF, 2y span.
    const _flags = _ap.flags || {};
    const _fcfBaseSrc = _ap.cagr_breakdown?.base_source;
    const _fcfCagrTtmBase = _flags.fcf_cagr_fallback && _fcfBaseSrc === "ttm_yahoo";
    const _cw = _ap.cagr_window || {};
    const _cwFrom = _cw.start_date ? monthLabels(_cw.start_date.slice(0, 7)).tip : `FY${nowY - 3}`;
    const _cwTo = _cw.target_date ? monthLabels(_cw.target_date.slice(0, 7)).tip : `FY${projTargetYear}`;
    const revCagrNote = `de ${_cwFrom} a ${_cwTo}`;
    // Date the POC/POV aim at: the +2-year horizon of the projections they are built on
    // (Nuestro / Pago give their own; Gratis, the CAGR window's end).
    const _targetIso = data?.data_source?.detail?.target_date || _cw.target_date || null;
    const _baseIso = data?.data_source?.detail?.base_date || data?.data_source?.detail?.last_reported_quarter || _cw.base_date || null;
    const targetDateLabel = _targetIso ? monthLabels(_targetIso.slice(0, 7)).tip : null;
    // The formula's +2-year inputs at that date, for the metric charts' dashed stretch.
    const chartProjection = _targetIso ? { date: _targetIso.slice(0, 10), values: {
        revenue_2y: inputs?.revenue_2y, fcf_2y: inputs?.fcf_2y,
        revenue_cagr_4y: inputs?.revenue_cagr_4y, fcf_cagr_4y: inputs?.fcf_cagr_4y } } : null;
    const targetDateTip = targetDateLabel
        ? `Fecha en que se cumpliría el objetivo según el cálculo: el POC y el POV salen de las proyecciones a +2 años (ingresos, FCF y crecimientos), es decir, 2 años después de las últimas cifras publicadas${_baseIso ? ` (${monthLabels(_baseIso.slice(0, 7)).tip})` : ""}. Si cambias a mano las proyecciones, la fecha sigue siendo la misma.`
        : null;
    // FCF CAGR with ±5 % floors and the shift (26-oct-2026): auto_projections.fcf_cagr_rule.
    const _fr = _ap.fcf_cagr_rule || null;
    const _frShifted = !!(_fr && (_fr.shift_start > 0 || _fr.shift_end > 0));
    const _frAmt = (v) => v == null ? "—" : fmtCompact(v / 1e9);
    const fcfCagrNote = _fcfCagrTtmBase ? `Base TTM → ${_cwTo} (2 años)`
        : `de ${_cwFrom} a ${_cwTo}${_fr?.group === "B" ? " · pérdidas" : _frShifted ? " · suelo 5 %" : ""}`;
    const revCagrTip = `Crecimiento compuesto anual (CAGR) a 4 años exactos de los ingresos: (ingresos proyectados a ${_cwTo} / ingresos de 12 meses a ${_cwFrom})^(1/4) − 1.\nEl inicio es 2 años antes del último trimestre publicado (interpolado entre cierres anuales) y el final, la proyección a +2 años: la misma ventana que usa el histórico POC/POV, para que sus puntos y la cifra de hoy sean comparables. No se recorta: fuera de −50 %/+100 % anual aparece un aviso con el tope sugerido.`;
    const fcfCagrTip = _fcfCagrTtmBase
        ? `CAGR del Free Cash Flow — MÉTODO FALLBACK (span 2 años).\nBase: FCF de los ÚLTIMOS 12 MESES (TTM, Yahoo), porque el FCF de hace 2 años era ≤0 y no permite el cálculo estándar a 4 años.\nObjetivo: proyección a ${_cwTo} (FCF proyectado 2y).\n= (FCF proyectado 2y / FCF TTM)^(1/2) − 1. Fuera de −50 %/+100 % anual, aviso con el tope sugerido.`
        : _fr?.group === "B"
        ? `CAGR del Free Cash Flow con FCF a ${_cwTo} negativo: se comparan las pérdidas al revés, (pérdida de 12 meses a ${_cwFrom} ÷ pérdida a ${_cwTo})^(1/4) − 1, para que unas pérdidas que encogen suban el factor y unas que crecen lo bajen.\nSuelos: la partida cuenta como mucho −5 % de los ingresos de entonces y la pérdida final como mínimo el 5 % de los ingresos a +2 años (si no, una pérdida cercana a 0 dispararía el factor); lo que un suelo mueve un extremo se aplica también al otro, para conservar el cambio real del FCF.\nAquí: partida ${_frAmt(_fr.fcf_start)} → se usa ${_frAmt(_fr.start_used)}; final ${_frAmt(_fr.fcf_end)} → se usa ${_frAmt(_fr.end_used)}.`
        : `Crecimiento compuesto anual (CAGR) a 4 años exactos del Free Cash Flow: (FCF proyectado a ${_cwTo} / FCF de 12 meses a ${_cwFrom})^(1/4) − 1.\nMisma ventana que los ingresos. El FCF de partida cuenta como mínimo el 5 % de los ingresos de entonces (un año de FCF casi nulo o negativo dispararía el crecimiento); si se sube hasta ese suelo, el FCF +2a sube lo mismo, para conservar el cambio real.${_frShifted ? `\nAquí: FCF de partida ${_frAmt(_fr.fcf_start)} → suelo ${_frAmt(_fr.start_used)}; FCF +2a ${_frAmt(_fr.fcf_end)} + ${_frAmt(_fr.shift_start)} = ${_frAmt(_fr.end_used)}.` : ""}\nFuera de −50 %/+100 % anual, aviso con el tope sugerido.`;

    // Determine if user has edited the 2y projection vs the auto value (small epsilon to avoid float jitter)
    const isEdited = (a, b) => {
        if (a == null || b == null) return false;
        if (a === 0 && b === 0) return false;
        const diff = Math.abs(a - b);
        const scale = Math.max(Math.abs(a), Math.abs(b), 1);
        return diff / scale > 0.001;
    };
    const revEdited = isEdited(inputs?.revenue_2y, data.auto_projections.revenue_2y);
    const fcfEdited = isEdited(inputs?.fcf_2y, data.auto_projections.fcf_2y);

    // Geometric midpoint (smooth compound growth) for the intermediate year.
    // Falls back to linear midpoint if either value is non-positive.
    const interpolate = (a, b) => {
        if (a == null || b == null) return null;
        if (a > 0 && b > 0) return Math.sqrt(a * b);
        return (a + b) / 2;
    };

    const buildChartData = (history, proj1Auto, proj2Auto, userProj2, userEdited, bridge = true) => {
        if (!history || history.length === 0) return [];
        const out = history.map(p => ({
            year: p.date.slice(0, 4),
            historical: p.value / 1e9,
            projection: null,
            kind: "real",
            value: p.value / 1e9,
        }));
        const last = out[out.length - 1];
        if (bridge) last.projection = last.historical;
        const lastYear = parseInt(last.year, 10) || new Date().getFullYear();
        const lastVal = last.historical;

        // Use user value when edited; otherwise the auto projection.
        const proj2 = userEdited ? userProj2 : proj2Auto;
        // For +1y: if user edited the +2y, interpolate between last real and user proj2 to avoid a visual jump.
        // Otherwise use the model's auto +1y projection.
        const proj1 = userEdited
            ? (lastVal != null && proj2 != null ? interpolate(lastVal, proj2 / 1e9) : null)
            : (proj1Auto != null ? proj1Auto / 1e9 : null);

        if (proj1 != null && !isNaN(proj1)) {
            out.push({ year: String(lastYear + 1) + "E", historical: null, projection: proj1, kind: "proj", value: proj1 });
        }
        if (proj2 != null && !isNaN(proj2)) {
            const v = userEdited ? proj2 / 1e9 : proj2 / 1e9;
            out.push({ year: String(lastYear + 2) + "E", historical: null, projection: v, kind: "proj", value: v });
        }
        return out;
    };

    const revChart = buildChartData(data.revenue_history, data.auto_projections.revenue_1y, data.auto_projections.revenue_2y, inputs?.revenue_2y, revEdited, true);
    const fcfChart = buildChartData(data.fcf_history, data.auto_projections.fcf_1y, data.auto_projections.fcf_2y, inputs?.fcf_2y, fcfEdited, false);

    // ---- Quarterly TTM chart builder ----
    // Backend supplies real TTM quarters + approx backfill (interpolated from annual data).
    // Forward path is built here, quarterly, from the last TTM point towards the user's
    // (or model's) 1y/2y projections, so manual edits of revenue_2y/fcf_2y are respected.
    const QUARTER_MS = 91.3 * 24 * 3600 * 1000;
    const nextQLabel = (label) => {
        const y = parseInt(label.slice(0, 4), 10);
        const q = parseInt(label.slice(5), 10);
        return q === 4 ? `${y + 1}Q1` : `${y}Q${q + 1}`;
    };
    const buildQuarterlyChart = (ttmSeries, annualHistory, proj1Auto, proj2Auto, userProj2, userEdited, horizonMode = "annual") => {
        if (!ttmSeries || ttmSeries.length === 0) return [];
        const out = ttmSeries.map(p => ({
            year: p.label,
            historical: p.kind === "real" ? p.value / 1e9 : null,
            approx: p.kind === "approx" ? p.value / 1e9 : null,
            projection: null,
            kind: p.kind,
            value: p.value / 1e9,
        }));
        // Bridge the approx line into the first real point so the series looks continuous.
        const firstRealIdx = out.findIndex(p => p.kind === "real");
        if (firstRealIdx > 0) out[firstRealIdx].approx = out[firstRealIdx].historical;
        const last = out[out.length - 1];
        last.projection = last.value;

        const lastDate = new Date(ttmSeries[ttmSeries.length - 1].date);
        const lastVal = last.value;
        const annLastDate = annualHistory && annualHistory.length
            ? new Date(annualHistory[annualHistory.length - 1].date)
            : lastDate;
        const proj2 = userEdited ? userProj2 : proj2Auto;
        const proj1 = userEdited
            ? (lastVal != null && proj2 != null ? interpolate(lastVal, proj2 / 1e9) : null)
            : (proj1Auto != null ? proj1Auto / 1e9 : null);
        const p2 = proj2 != null ? proj2 / 1e9 : null;
        if (proj1 == null || isNaN(proj1)) return out;

        // Projection-target anchor depends on the active horizon so the +2y point lands
        // where the footnote says it does:
        //  · TTM   → last reported TTM quarter + 1y/+2y  (e.g. Q1 2026 → +2y = 2028Q1)
        //  · ANUAL → fiscal-year-end + 1y/+2y            (e.g. FY2025 → +2y = 2027Q4)
        const anchorDate = horizonMode === "ttm" ? lastDate : annLastDate;
        const t1 = new Date(anchorDate); t1.setFullYear(t1.getFullYear() + 1);
        const t2 = new Date(anchorDate); t2.setFullYear(t2.getFullYear() + 2);
        const nQ1 = Math.max(1, Math.round((t1 - lastDate) / QUARTER_MS));
        const nQ2 = Math.max(nQ1 + 1, Math.round((t2 - lastDate) / QUARTER_MS));
        const geomStep = (a, b, k, n) => (a > 0 && b > 0) ? a * Math.pow(b / a, k / n) : a + (b - a) * (k / n);

        let label = last.year;
        for (let k = 1; k <= nQ1; k++) {
            label = nextQLabel(label);
            const v = geomStep(lastVal, proj1, k, nQ1);
            out.push({ year: label + "E", historical: null, approx: null, projection: v, kind: "proj", value: v });
        }
        if (p2 != null && !isNaN(p2)) {
            for (let k = 1; k <= nQ2 - nQ1; k++) {
                label = nextQLabel(label);
                const v = geomStep(proj1, p2, k, nQ2 - nQ1);
                out.push({ year: label + "E", historical: null, approx: null, projection: v, kind: "proj", value: v });
            }
        }
        return out;
    };
    // Active horizon per metric → drives where the quarterly chart lands its +2y point.
    const _within = (a, b) => a != null && b != null && Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) < 0.001;
    const revHorizon = (data.auto_projections.revenue_2y_ttm != null && _within(inputs?.revenue_2y, data.auto_projections.revenue_2y_ttm)) ? "ttm" : "annual";
    const _cbF = data.auto_projections.cagr_breakdown;
    const _gF = (_cbF && _cbF.growth_pct != null) ? _cbF.growth_pct : 0;
    const _canSwapF = _cbF && _cbF.growth_pct != null && _cbF.fcf_ttm && _cbF.latest_annual && _cbF.latest_annual > 0;
    const _pTtmF = _canSwapF ? _cbF.fcf_ttm * Math.pow(1 + _gF, 2) : null;
    const fcfHorizon = (_pTtmF != null && _within(inputs?.fcf_2y, _pTtmF)) ? "ttm" : "annual";
    // Human-readable active method for the chart header badges (auto-updates with the
    // TTM/ANUAL/BU toggles in "Inputs y proyecciones").
    const revMethodLabel = (data.auto_projections.revenue_2y_ttm != null && _within(inputs?.revenue_2y, data.auto_projections.revenue_2y_ttm))
        ? "TTM"
        : (data.auto_projections.revenue_2y_annual != null && _within(inputs?.revenue_2y, data.auto_projections.revenue_2y_annual)) ? "Anual" : "Manual";
    let fcfMethodLabel = "Manual";
    {
        const curF = inputs?.fcf_2y;
        const mth = data.auto_projections.projection_method;
        const buV = (mth === "bottom-up" && data.auto_projections.bottom_up_breakdown) ? data.auto_projections.fcf_2y : null;
        const buRejV = data.auto_projections.bottom_up_rejected_alternative?.fcf_2y;
        const pAnnF = _canSwapF ? _cbF.latest_annual * Math.pow(1 + _gF, 2) : null;
        const anV = (mth === "analyst-ni" || mth === "fcf-margin" || mth === "bank-ni") ? data.auto_projections.fcf_2y : null;
        if (anV != null && _within(curF, anV)) fcfMethodLabel = mth === "analyst-ni" ? "AN" : mth === "bank-ni" ? "Banco" : "Margen";
        else if (buV != null && _within(curF, buV)) fcfMethodLabel = "BU";
        else if (buRejV != null && _within(curF, buRejV)) fcfMethodLabel = "BU+";
        else if (_pTtmF != null && _within(curF, _pTtmF)) fcfMethodLabel = "TTM";
        else if (pAnnF != null && _within(curF, pAnnF)) fcfMethodLabel = "Anual";
    }
    // Clicking the chart "Método" badge toggles the horizon (TTM ⇄ Anual) and writes it
    // back into the ficha inputs, so the whole card (chart, footnote, ratios) recalculates.
    const revTtmVal = data.auto_projections.revenue_2y_ttm;
    const revAnnVal = data.auto_projections.revenue_2y_annual;
    const revCanToggle = revTtmVal != null && revAnnVal != null;
    const onToggleRev = () => updateInput("revenue_2y", revMethodLabel === "TTM" ? revAnnVal : revTtmVal);
    const fcfTtmVal = _pTtmF;
    const fcfAnnVal = _canSwapF ? _cbF.latest_annual * Math.pow(1 + _gF, 2) : null;
    const fcfCanToggle = fcfTtmVal != null && fcfAnnVal != null;
    const onToggleFcf = () => updateInput("fcf_2y", fcfMethodLabel === "TTM" ? fcfAnnVal : fcfTtmVal);
    const revChartQ = qHist ? buildQuarterlyChart(qHist.revenue_ttm, data.revenue_history, data.auto_projections.revenue_1y, data.auto_projections.revenue_2y, inputs?.revenue_2y, revEdited, revHorizon) : null;
    const fcfChartQ = qHist ? buildQuarterlyChart(qHist.fcf_ttm, data.fcf_history, data.auto_projections.fcf_1y, data.auto_projections.fcf_2y, inputs?.fcf_2y, fcfEdited, fcfHorizon) : null;

    // Detect anomalies in POC/POV and explain them based on the actual inputs.
    // Each item also contributes suggested corrections (clipping rules) that the
    // user can apply in one click. Corrections are deduped by field, keeping the
    // first one (anomalies are checked in order of severity).
    const { pocPovAnomalies, pocPovCorrections } = (() => {
        const anomalies = [];
        const fmtUsd = (v) => v == null ? "—" : fmtCompact(v / 1e9); // inputs are in units, fmtCompact takes billions
        const corrByField = new Map(); // key (the field, unless given) -> {key, field, from, to, reason}
        const addCorr = (field, from, to, reason, key = field) => {
            if (corrByField.has(key)) return;
            corrByField.set(key, { key, field, from, to, reason });
        };
        if (!cr || !inputs || cr.poc == null || cr.pov == null) {
            return { pocPovAnomalies: anomalies, pocPovCorrections: [] };
        }
        const poc = cr.poc, pov = cr.pov;
        const b = cr.breakdown || {};
        const gm = inputs.gross_margin;
        const om = inputs.operating_margin;
        const revC = inputs.revenue_cagr_4y;
        const fcfC = inputs.fcf_cagr_4y;
        const rev2y = inputs.revenue_2y;
        const xRaw = b.x_raw_pct;

        // Manufacturer with a finance arm (services.captive_finance): Gratis and Pago keep
        // the group's net debt, the bank's included; offer the industrial one (or 0) as
        // the correction, ahead of the generic one below. «Nuestro» already uses it.
        const cf = data?.captive_finance;
        if (cf && !cf.applied && cf.net_debt != null && inputs.net_debt != null && inputs.market_cap > 0
            && Math.abs(inputs.net_debt - cf.net_debt) > 0.05 * inputs.market_cap) {
            const read = cf.status === "read";
            anomalies.push({
                title: "Deuda neta con la de su financiera",
                detail: `Fabricante con financiera propia (préstamos y alquileres a clientes, que es un banco): la deuda neta del grupo (${fmtUsd(inputs.net_debt)}) incluye la del banco, que es su materia prima y no deuda del negocio industrial.`,
                bullets: [read
                    ? `Su parte industrial${cf.segment ? ` («${cf.segment}»)` : ""}, sin la financiera, tiene a ${cf.date} ${cf.net_debt <= 0 ? `caja neta de ${fmtUsd(-cf.net_debt)}` : `deuda neta de ${fmtUsd(cf.net_debt)}`} según sus resultados (leído con IA${cf.origin === "sec" ? " del comunicado en la SEC" : " de su web"}). Es lo que usa Nuestro.`
                    : `No se ha podido leer la de su parte industrial${cf.pending ? " (se está buscando)" : ""}: la corrección propone deuda neta 0 (neutral), como Nuestro.`,
                    ...(cf.finance ? [captiveFinanceText(cf.finance, cf.currency).trim()] : [])],
            });
            addCorr("net_debt", inputs.net_debt, cf.net_debt, read
                ? "Deuda neta de la parte industrial, sin la financiera (la que publica la empresa)."
                : "Deuda neta 0: la del grupo incluye la de su financiera y la industrial no se ha podido leer.");
        }

        // POC ≤ 0 — pinpoint which factor flips the sign.
        if (poc <= 0) {
            const reasons = [];
            if (rev2y != null && rev2y < 0) {
                reasons.push(`Ingresos proyectados a 2y son negativos (${fmtUsd(rev2y)}). El primer factor de la fórmula (Ingresos/acción) ya nace negativo.`);
                // No safe auto-fix for negative revenue; user must decide.
            }
            if (gm != null && (1 + gm) < 0) {
                reasons.push(`Margen bruto = ${(gm * 100).toFixed(1)}% → factor (1 + margen) = ${(1 + gm).toFixed(2)} es negativo.`);
                addCorr("gross_margin", gm, 0, "Clip margen bruto a 0% (neutraliza el factor sin invertirlo).");
            }
            if (xRaw != null && xRaw < -100) {
                reasons.push(`(FCF 2y − Deuda neta) / MCap = ${xRaw.toFixed(1)}%. Como es < −100%, el factor ajustado (1 + x/100 = ${(1 + xRaw / 100).toFixed(2)}) es negativo. Suele indicar deuda neta muy alta o FCF muy negativo respecto a la capitalización.`);
                // Heuristic: reduce net_debt so that x_raw becomes -50% (penaliza pero no anula).
                if (inputs.fcf_2y != null && inputs.market_cap != null) {
                    const safeNetDebt = inputs.fcf_2y + 0.5 * inputs.market_cap;
                    if (Number.isFinite(safeNetDebt)) {
                        addCorr("net_debt", inputs.net_debt, safeNetDebt, "Recorta la deuda neta para que x_raw = −50% (penaliza pero no colapsa).");
                    }
                }
            }
            if (revC != null && (1 + revC) < 0) {
                reasons.push(`CAGR ingresos 4y = ${(revC * 100).toFixed(1)}% → factor (1 + CAGR) negativo.`);
                addCorr("revenue_cagr_4y", revC, -0.30, "Clip CAGR ingresos a −30% (suelo razonable para empresas en deterioro).");
            }
            if (fcfC != null && (1 + fcfC) < 0) {
                reasons.push(`CAGR FCF 4y = ${(fcfC * 100).toFixed(1)}% → factor (1 + CAGR) negativo.`);
                addCorr("fcf_cagr_4y", fcfC, -0.30, "Clip CAGR FCF a −30% (suelo razonable).");
            }
            if (!reasons.length) reasons.push("La combinación de factores deja un POC ≤ 0. Revisa manualmente Ingresos 2y, márgenes y CAGRs.");
            anomalies.push({
                title: `POC ≤ 0 → ${fmtPrice(convertCur(poc), cur)}`,
                detail: "El precio objetivo de compra no es utilizable porque uno o más factores de la fórmula están colapsando el resultado:",
                bullets: reasons,
            });
        }

        // POV < POC — operating margin is dragging POV below POC.
        if (poc > 0 && pov > 0 && pov < poc) {
            const yPct = (om ?? 0) * 100;
            anomalies.push({
                title: `POV (${fmtPrice(convertCur(pov), cur)}) < POC (${fmtPrice(convertCur(poc), cur)})`,
                detail: "Tu precio objetivo de venta queda por debajo del de compra, lo que invierte la lógica esperada. Causa concreta según la fórmula:",
                bullets: [
                    `Margen operativo = ${yPct.toFixed(2)}% → factor ajustado y = ${b.y_factor != null ? b.y_factor.toFixed(3) : "—"} (cuando y < 0%, el factor se aplica como 1 + y/100, que es < 1, así que POV = POC × y_factor termina menor que POC).`,
                    "Sugerencia: si crees que la empresa va a recuperar márgenes, edita manualmente 'Margen operativo' a un valor positivo para que POV vuelva a quedar por encima de POC.",
                ],
            });
            addCorr("operating_margin", om, 0, "Clip margen operativo a 0% (neutraliza el factor, POV = POC).");
        }

        // POV ≤ 0 with POC > 0 — rare, only if operating margin < -100%
        if (poc > 0 && pov <= 0) {
            const yPct = (om ?? 0) * 100;
            anomalies.push({
                title: `POV ≤ 0 → ${fmtPrice(convertCur(pov), cur)}`,
                detail: "El precio objetivo de venta colapsa por culpa del margen operativo:",
                bullets: [
                    `Margen operativo = ${yPct.toFixed(2)}%. Como es < −100%, el factor (1 + y/100) es ≤ 0 y arrastra POV a territorio negativo.`,
                    "Edita manualmente 'Margen operativo' a un valor más realista (ej. la media histórica del sector) para obtener un POV válido.",
                ],
            });
            addCorr("operating_margin", om, 0, "Clip margen operativo a 0% (neutraliza el factor, POV = POC).");
        }

        // Growth outside [−50 %, +100 %] a year: never capped automatically (the analysts
        // may be right) — explained here, with the bounded value as a suggested correction.
        const GW_LO = -0.5, GW_HI = 1.0;
        const outOfRange = (g) => g != null && (g < GW_LO || g > GW_HI);
        const bound = (g) => Math.max(GW_LO, Math.min(GW_HI, g));
        const pctS = (g) => `${g >= 0 ? "+" : ""}${(g * 100).toFixed(0)} %`;
        const cycleNote = "Posible pico de ciclo: en sectores cíclicos (memoria y semiconductores, materias primas, transporte…) los analistas tienden a proyectar el momento alto del ciclo. Si el ciclo gira, los ingresos y sobre todo los márgenes caen rápido, y un POC calculado con estas previsiones sobrevaloraría la empresa. Contrástalo con lo que pasó en ciclos anteriores (gráficos de ingresos y FCF).";
        const apA = data.auto_projections || {};
        const cwA = apA.cagr_window || {};
        const bigBase = (start, end) => start != null && end != null && start > 0 && end / start > 16;
        if (outOfRange(revC)) {
            const bullets = [];
            if (bigBase(cwA.revenue_start, rev2y)) bullets.push(`Parte de unos ingresos muy bajos hace 2 años (${fmtUsd(cwA.revenue_start)}): el CAGR refleja ese punto de partida, no un ritmo sostenible.`);
            if (revC > GW_HI) bullets.push(cycleNote);
            bullets.push(`Sugerencia: aplica el tope (${pctS(bound(revC))} anual) o revisa a mano los datos.`);
            anomalies.push({
                title: `CAGR ingresos 4 años = ${pctS(revC)} anual (fuera de −50 % / +100 %)`,
                detail: revC > GW_HI ? "Un crecimiento así multiplica los ingresos por más de 16 en 4 años. Puede ser real, pero conviene entender por qué:" : "Una caída así divide los ingresos por más de 16 en 4 años:",
                bullets,
            });
            addCorr("revenue_cagr_4y", revC, bound(revC), `Tope del CAGR de ingresos en ${pctS(bound(revC))} anual.`);
        }
        if (outOfRange(fcfC)) {
            const bullets = [];
            const fcfStartUsed = apA.fcf_cagr_rule?.group === "A" ? apA.fcf_cagr_rule.start_used : cwA.fcf_start;
            if (fcfStartUsed != null && fcfStartUsed > 0 && inputs.fcf_2y != null && inputs.fcf_2y / fcfStartUsed > 16) bullets.push(`Parte de un FCF bajo hace 2 años (${fmtUsd(fcfStartUsed)}${apA.fcf_cagr_rule?.shift_start > 0 ? ", ya con el suelo del 5 % de los ingresos" : ""}): el CAGR refleja ese punto de partida (p. ej. un año de inversión fuerte), no un ritmo sostenible.`);
            if (fcfC > GW_HI) bullets.push(cycleNote);
            bullets.push(`Sugerencia: aplica el tope (${pctS(bound(fcfC))} anual) o revisa a mano los datos.`);
            anomalies.push({
                title: `CAGR FCF 4 años = ${pctS(fcfC)} anual (fuera de −50 % / +100 %)`,
                detail: "El factor (1 + CAGR FCF) multiplica directamente el POC:",
                bullets,
            });
            addCorr("fcf_cagr_4y", fcfC, bound(fcfC), `Tope del CAGR de FCF en ${pctS(bound(fcfC))} anual.`);
        }
        // Revenue +2y: analysts' growth (Gratis: Yahoo; Pago: FMP).
        const dsA = data.data_source || {};
        const rgbA = apA.revenue_growth_breakdown || {};
        const revG = dsA.used === "fmp" ? dsA.detail?.revenue_growth_analysts : (rgbA.growth_ttm ?? rgbA.raw_growth);
        const revBase = dsA.used === "fmp" ? dsA.detail?.revenue_ttm : (rgbA.total_revenue_ttm || rgbA.latest_revenue);
        if (dsA.used !== "own" && outOfRange(revG) && revBase && rev2y != null) {
            const bounded = revBase * Math.pow(1 + bound(revG), 2);
            anomalies.push({
                title: `Los analistas prevén ingresos a ${pctS(revG)} anual los próximos 2 años`,
                detail: "Fuera de −50 % / +100 % anual. No se recorta: puede ser correcto, pero es una previsión muy agresiva.",
                bullets: [
                    ...(revG > GW_HI ? [cycleNote] : []),
                    `Con el tope, los ingresos a +2 años serían ${fmtUsd(bounded)} en lugar de ${fmtUsd(rev2y)}.`,
                ],
            });
            addCorr("revenue_2y", rev2y, bounded, `Ingresos +2a con crecimiento limitado a ${pctS(bound(revG))} anual.`);
        }
        // FCF +2y: analysts' net-income growth or implied margins out of range.
        const fpA = apA.fcf_projection || {};
        if ((fpA.ni_growth_extreme || fpA.margin_extreme) && fpA.fcf_2y_bounded != null && inputs.fcf_2y != null) {
            const bullets = [];
            if (fpA.ni_growth_extreme) bullets.push(`Crecimiento del beneficio previsto: ${pctS(fpA.ni_growth)} anual (fuera de −50 % / +100 %).`);
            if (fpA.margin_extreme) bullets.push("El beneficio o el flujo operativo a +2 años superarían el 60 % / 75 % de los ingresos: más de lo que suele sostener un negocio.");
            if ((fpA.ni_growth || 0) > GW_HI || fpA.margin_extreme) bullets.push(cycleNote);
            bullets.push(`Con los límites aplicados, el FCF a +2 años sería ${fmtUsd(fpA.fcf_2y_bounded)} en lugar de ${fmtUsd(inputs.fcf_2y)}.`);
            anomalies.push({ title: "Previsión de FCF a +2 años muy agresiva", detail: "No se recorta automáticamente:", bullets });
            addCorr("fcf_2y", inputs.fcf_2y, fpA.fcf_2y_bounded, "FCF +2a con crecimiento del beneficio y márgenes limitados.");
        }
        // Investment wave: the analysts' +2y profit already carries the extra depreciation
        // of today's capex, which we also subtract as capex (services/fcf_projection.py).
        const iw = fpA.investment_wave;
        if (dsA.used !== "own" && iw && iw.fcf_2y_with_da != null && inputs.fcf_2y != null) {
            anomalies.push({
                title: "Ola de inversión: el FCF a +2 años puede estar infravalorado",
                detail: `El CapEx (${fmtUsd(iw.capex_ttm)} en 12 meses) es ${(iw.capex_ttm / iw.da_ttm).toFixed(1)}× la amortización (${fmtUsd(iw.da_ttm)}). Los analistas ya descuentan de su beneficio a +2 años la amortización que generará esa inversión, y el cálculo resta además el CapEx: parte de la inversión cuenta dos veces.`,
                bullets: [
                    `Vida útil media de sus activos (inmovilizado bruto ÷ amortización anual, SEC): ${iw.life.toFixed(1)} años → 2 años de inversión por encima de la amortización añaden ${fmtUsd(iw.extra_da)} de amortización anual.`,
                    `De eso, ${fmtUsd(iw.da_scaled)} ya se suman al crecer la amortización con los ingresos; el resto (${fmtUsd(iw.add)}) se suma al FCF a +2 años en la corrección sugerida.`,
                    "Es una estimación: depende de la vida útil y de que los analistas hayan incluido esa amortización. Aplícala solo si te parece razonable.",
                ],
            });
            addCorr("fcf_2y", inputs.fcf_2y, iw.fcf_2y_with_da, `FCF +2a + amortización extra de la ola de inversión (${fmtUsd(iw.add)}).`, "fcf_2y:wave");
        }
        // The one automatic bound: gross margin > 100 % (a data error).
        if (b.gross_margin_capped) {
            anomalies.push({
                title: `Margen bruto = ${(gm * 100).toFixed(1)} % (imposible) → se usa 100 %`,
                detail: "El beneficio bruto no puede superar a los ingresos: es un error del dato de origen. La fórmula lo limita automáticamente a 100 %.",
                bullets: ["Revisa el margen bruto a mano con las cuentas de la empresa y corrígelo si procede."],
            });
        }

        // Revenue above the market cap with a thin margin (16-oct-2026): only part of it counts.
        if (b.sales_factor != null && b.sales_factor < 0.999 && b.sales_to_mcap != null) {
            const s2m = b.sales_to_mcap;
            const counted = s2m * b.sales_factor;
            const fmt1 = (v) => v.toFixed(1).replace(".", ",");
            anomalies.push({
                title: `Ingresos por encima de la capitalización con margen bajo → cuentan ${fmt1(counted)} de ${fmt1(s2m)} veces`,
                detail: "La fórmula parte de los ingresos por acción, y en un negocio de margen bajo que cotiza por debajo de sus ventas (fabricantes de coches, distribuidores) un euro de ventas vale mucho menos que en las empresas para las que se pensó. Se aplica sola:",
                bullets: [
                    `Ingresos a +2 años: ${fmt1(s2m)} veces la capitalización, con un margen operativo del ${om != null ? (om * 100).toFixed(1).replace(".", ",") : "—"} %.`,
                    `Hasta 1 vez la capitalización cuentan enteros; por encima, cada unidad cuenta en proporción al margen operativo frente al 10 % (entre un 5 % y el 100 %). Aquí cuentan ${fmt1(counted)} veces: el POC queda en el ${(b.sales_factor * 100).toFixed(0)} % del que daría la fórmula sin esta corrección.`,
                    "No hay salto: más ingresos nunca bajan el POC, solo suben menos. Con un margen operativo del 10 % o más no se aplica.",
                ],
            });
        }
        // Extreme upside (>1000%) — opportunity or value trap.
        const rc = cr.ratio_compra_pct, rv = cr.ratio_venta_pct;
        const extremeRC = rc != null && rc > 1000;
        const extremeRV = rv != null && rv > 1000;
        // Out of scale (14-oct-2026): the result stays, with what drives it. POC > 20× the
        // price or an x yield over 30 % (Renault, Micron in its boom).
        const price = inputs.current_price;
        const pocTimes = price > 0 ? poc / price : null;
        if ((pocTimes != null && pocTimes > 20) || (xRaw != null && xRaw > 30)) {
            const bullets = [];
            const mcap = inputs.market_cap;
            if (xRaw != null && xRaw > 30) {
                bullets.push(`x real del ${xRaw.toFixed(0)} %: el FCF a +2 años menos la deuda neta equivale a ${(xRaw / 100).toFixed(1).replace(".", ",")} veces lo que vale la empresa en bolsa. x se queda cerca de su techo (6), así que no explica todo el resultado; revisa si la caja incluye la de un banco o financiera propia, o si falta deuda.`);
            }
            if (rev2y != null && mcap > 0 && rev2y / mcap >= 3) {
                bullets.push(`Factura a +2 años ${(rev2y / mcap).toFixed(1).replace(".", ",")} veces lo que vale en bolsa${om != null ? `, con un margen operativo del ${(om * 100).toFixed(1).replace(".", ",")} %` : ""}. La fórmula parte de los ingresos por acción: un negocio de márgenes bajos que cotiza a una fracción de sus ventas sale con un POC muy alto aunque x esté contenido${b.sales_factor != null && b.sales_factor < 1 ? " (ya se cuenta solo una parte de esos ingresos: ver el aviso de ingresos por encima de la capitalización)" : ""}.`);
            }
            const g = (1 + (revC || 0)) * (1 + (fcfC || 0));
            if (g > 3) {
                bullets.push(`Crecimientos a 4 años muy altos (ingresos ${((revC || 0) * 100).toFixed(0)} % y FCF ${((fcfC || 0) * 100).toFixed(0)} % al año): juntos multiplican el POC por ${g.toFixed(1).replace(".", ",")}. Si la empresa está en un pico de ciclo, no se mantendrán.`);
            }
            bullets.push("El resultado se mantiene, pero está fuera del rango en el que la fórmula suele moverse (de media, unas 3 veces el precio): úsalo con mucha cautela.");
            anomalies.push({
                title: `Valoración fuera de escala${pocTimes != null ? ` (POC = ${pocTimes.toFixed(0)} veces el precio)` : ""}`,
                detail: "La fórmula da un resultado extremo. Lo que lo explica en esta empresa:",
                bullets,
            });
        }
        if (extremeRC || extremeRV) {
            const which = extremeRC && extremeRV ? "Ratio Compra y Ratio Venta" : extremeRC ? "Ratio Compra" : "Ratio Venta";
            anomalies.push({
                title: `${which} > +1000%`,
                detail: "Un upside extremadamente elevado puede tener dos lecturas opuestas. Antes de actuar, pregúntate cuál crees que aplica aquí:",
                bullets: [
                    "🟢 Oportunidad real: el mercado está penalizando temporalmente a una empresa con buenos fundamentales (sector deprimido, evento puntual, noticia negativa exagerada). En este caso, los datos automáticos sí reflejan el negocio y los CAGRs son fiables.",
                    "🔴 Trampa de valor: el mercado está descontando un deterioro estructural que la fórmula no capta (deuda escondida, regulación, disrupción del producto, contabilidad agresiva, riesgo de quiebra). Los datos del pasado mienten sobre el futuro.",
                    "Sugerencia: revisa los avisos sobre proyecciones automáticas, compara con el precio objetivo medio de analistas (sección 'Ratios clásicos'), y si los CAGRs son anormalmente altos, recórtalos manualmente para ver si el upside aguanta.",
                ],
            });
        }
        // Symmetric: extreme downside (<-1000%) — likely garbage inputs.
        if ((rc != null && rc < -1000) || (rv != null && rv < -1000)) {
            anomalies.push({
                title: `Ratio < −1000%`,
                detail: "Un downside extremo casi siempre indica que algún input está descalibrado (no es una señal de venta real):",
                bullets: [
                    "Lo más habitual: el POC es negativo o casi cero por culpa de un factor colapsado. Mira las anomalías de arriba para identificar el factor.",
                    "Acción: usa 'Auto-corregir' o edita manualmente los inputs sospechosos antes de tomar conclusiones.",
                ],
            });
        }

        return { pocPovAnomalies: anomalies, pocPovCorrections: Array.from(corrByField.values()) };
    })();

    // One button per suggestion: the user picks which corrections to apply.
    const applyCorrection = (c) => {
        setInputs(prev => {
            const next = { ...prev, [c.field]: c.to };
            setSessionEdits(prevEdits => {
                const ne = { ...prevEdits };
                const loaded = wlEntry?.overrides?.[c.field] !== undefined ? wlEntry.overrides[c.field] : autoInputs?.[c.field];
                if (!valuesEqual(c.to, loaded)) ne[c.field] = true;
                else delete ne[c.field];
                return ne;
            });
            setCustomRatios(computeCustomRatios(next));
            return next;
        });
        toast.success(`${fieldLabels[c.field] || c.field} auto-corregido. Revisa, edita o guarda.`);
    };

    // Pre-revenue gross margin artifact detection (own standalone panel above
    // the POC/POV anomalies). Trigger: revenue < 5 M$ AND gross_margin ≥ 95%.
    const grossMarginPreRevenue = (() => {
        const gm = inputs?.gross_margin;
        const revHist = data.revenue_history || [];
        const latestRevenue = revHist.length ? revHist[revHist.length - 1].value : null;
        if (gm == null || gm < 0.95) return null;
        if (latestRevenue == null || latestRevenue >= 5_000_000) return null;
        return { gm, latestRevenue, from: gm, to: 0.5 };
    })();

    const applyGrossMarginFix = () => {
        if (!grossMarginPreRevenue) return;
        const { to } = grossMarginPreRevenue;
        setInputs(prev => {
            const next = { ...prev, gross_margin: to };
            setSessionEdits(prevEdits => {
                const ne = { ...prevEdits };
                const loaded = wlEntry?.overrides?.gross_margin !== undefined ? wlEntry.overrides.gross_margin : autoInputs?.gross_margin;
                if (!valuesEqual(to, loaded)) ne.gross_margin = true;
                else delete ne.gross_margin;
                return ne;
            });
            setCustomRatios(computeCustomRatios(next));
            return next;
        });
        toast.success("Margen bruto auto-corregido al 50%. Revisa, edita o guarda.");
    };

    // Map field key to its display label (used in the corrections preview)
    const fieldLabels = {
        gross_margin: "Margen bruto",
        operating_margin: "Margen operativo",
        revenue_cagr_4y: "CAGR ingresos 4y",
        fcf_cagr_4y: "CAGR FCF 4y",
        net_debt: "Deuda neta",
        revenue_2y: "Ingresos proyectados 2y",
        fcf_2y: "FCF proyectado 2y",
    };
    const fmtCorrValue = (field, v) => {
        if (v == null || isNaN(v)) return "—";
        if (field === "gross_margin" || field === "operating_margin" || field === "revenue_cagr_4y" || field === "fcf_cagr_4y") return `${(v * 100).toFixed(2)}%`;
        return fmtCompact(v / 1e9); // monetary inputs are in units; fmtCompact takes billions
    };

    return (
        <div data-testid="company-page">
            {/* Header */}
            <div className="border border-black bg-white p-6 mb-6" data-testid="company-header">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <HoverTip text={`${exchangeFullName(data.exchange) || data.exchange}${data.currency ? ` · Moneda: ${data.currency}` : ""}${data.sector ? ` · Sector: ${data.sector}` : ""}`}>
                            <div className="overline text-[#4A4A4A] cursor-help" data-testid="exchange-code">
                                {data.exchange}
                                {" · "}{data.currency}{data.sector ? ` · ${data.sector}` : ""}
                            </div>
                        </HoverTip>
                        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-1 flex items-center gap-[0.25em] min-w-0">
                            <CompanyLogo logoUrl={identity?.logo_url} logoWidth={identity?.logo_width} brandColor={identity?.brand_color} ticker={data.ticker} className="w-[0.9em] h-[0.9em]" initialsClass="text-[0.4em]" testid="company-logo" />
                            {identity?.website ? (
                                <HoverTip text={`Abrir la web de la empresa (${siteLabel(identity.website)}) en una pestaña nueva.`}>
                                    <a href={identity.website} target="_blank" rel="noopener noreferrer" className="hover:underline decoration-2 underline-offset-4 inline-flex items-center gap-[0.2em]" data-testid="company-website">
                                        <span className="[overflow-wrap:anywhere]" data-testid="company-name">{data.name}</span>
                                        <ExternalLink className="w-[0.4em] h-[0.4em] shrink-0 opacity-50" aria-hidden />
                                    </a>
                                </HoverTip>
                            ) : <span className="[overflow-wrap:anywhere] min-w-0" data-testid="company-name">{data.name}</span>}
                        </h1>
                        {/* Ticker in the company's own colour (dominant colour of its logo; our navy if
                            unclear); links to the user's company thesis (plan) when there is one. */}
                        {(() => { const cc = companyColorProps(identity?.brand_color); return (
                        <div className={`font-mono text-lg mt-1 ${cc.className}`} style={cc.style}>
                            {plan?.plan_id ? (
                                <HoverTip text={`Abrir tu tesis de ${data.name || data.ticker}: el plan de la empresa, sus motores (drivers) y su puntuación.${plan.complete === false ? " Aún está en desarrollo (quedan motores por desarrollar)." : ""}`}>
                                    <Link to={`/thesis/${plan.plan_id}`} className="hover:underline underline-offset-4" data-testid="company-ticker">{data.ticker}</Link>
                                </HoverTip>
                            ) : (
                                <HoverTip text={!user
                                    ? `Inicia sesión para ver o crear tu tesis de ${data.name || data.ticker}.`
                                    : plan === undefined
                                        ? `Comprobando si tienes una tesis de ${data.name || data.ticker}…`
                                        : plan === null
                                            ? `No se ha podido comprobar si tienes una tesis de ${data.name || data.ticker}. Recarga la página para reintentarlo.`
                                            : `Aún no tienes una tesis de ${data.name || data.ticker}, así que no hay enlace. Puedes crearla desde la página Tesis.`}>
                                    <span className="cursor-help" data-testid="company-ticker">{data.ticker}</span>
                                </HoverTip>
                            )}
                        </div>
                        ); })()}
                    </div>
                    <div className="text-right">
                        <div className="overline text-[#4A4A4A]">Precio actual</div>
                        <div className="font-mono text-4xl sm:text-5xl font-medium" data-testid="company-price">{fmtPrice(convertCur(data.current_price), cur)}</div>
                        {data.price_date && (
                            <div className="text-[10px] text-[#4A4A4A] font-mono mt-0.5" data-testid="company-price-date">
                                Cierre {new Date(`${data.price_date}T00:00:00`).toLocaleDateString("es-ES")}
                            </div>
                        )}
                        <div className="text-xs text-[#4A4A4A] font-mono mt-1">MCap {fmtNum(convertCur(data.market_cap))} {cur !== nativeCur && <span className="text-[10px]">({cur})</span>}</div>
                        {nativeCur !== "USD" && (
                            <div className="mt-1.5 flex justify-end">
                                <CurrencySelector />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex gap-4 mt-4 items-start flex-wrap lg:flex-nowrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex gap-2 flex-wrap items-center">
                            {pfEntry ? (
                                <>
                                    <button
                                        onClick={handleSaveToPortfolio}
                                        className={hasSessionEdits ? "btn-primary flex items-center gap-2" : "btn-ghost flex items-center gap-2"}
                                        data-testid="portfolio-save"
                                        title={hasSessionEdits ? "Guardar cambios actuales en Nivel 1" : "Ya en Nivel 1"}
                                    >
                                        <Save size={14} /> {hasSessionEdits ? "Guardar en Nivel 1" : "En Nivel 1"}
                                    </button>
                                    <button onClick={handleRemoveFromPortfolio} className="btn-ghost flex items-center gap-2" data-testid="portfolio-remove-btn">
                                        <X size={14} /> Quitar de Nivel 1
                                    </button>
                                    {pfEntry.mode === "manual" && (
                                        <span className="overline px-2 py-1 border border-[#1D7044] text-[#1D7044] bg-white" data-testid="manual-badge-pf">MANUAL</span>
                                    )}
                                </>
                            ) : (
                                <button onClick={handleSaveToPortfolio} className="btn-ghost flex items-center gap-2" data-testid="portfolio-add-btn">
                                    <Briefcase size={14} /> Añadir a Nivel 1
                                </button>
                            )}

                            {wlEntry ? (
                                <>
                                    <button
                                        onClick={handleSaveToWatchlist}
                                        className={hasSessionEdits ? "btn-primary flex items-center gap-2" : "btn-ghost flex items-center gap-2"}
                                        data-testid="watchlist-save"
                                        title={hasSessionEdits ? "Guardar cambios actuales en Nivel 2" : "Ya guardada"}
                                    >
                                        <Save size={14} /> {hasSessionEdits ? "Guardar en Nivel 2" : "En Nivel 2"}
                                    </button>
                                    <button onClick={handleRemoveFromWatchlist} className="btn-ghost flex items-center gap-2" data-testid="watchlist-remove">
                                        <X size={14} /> Quitar de Nivel 2
                                    </button>
                                    {wlEntry.mode === "manual" && (
                                        <span className="overline px-2 py-1 border border-[#1D7044] text-[#1D7044] bg-white" data-testid="manual-badge">MANUAL</span>
                                    )}
                                </>
                            ) : (
                                <button onClick={handleSaveToWatchlist} className="btn-ghost flex items-center gap-2" data-testid="watchlist-add">
                                    <Star size={14} /> Añadir a Nivel 2
                                </button>
                            )}

                            <button onClick={handleRefresh} className="btn-ghost flex items-center gap-2" data-testid="refresh-button" disabled={refreshing}>
                                <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refrescar
                            </button>
                            {hasSessionEdits && (
                                <span className="text-xs font-mono text-[#D97706]" data-testid="unsaved-indicator" title="Pulsa 'Guardar en Nivel 1' o 'Guardar en Nivel 2' para conservar estos cambios">
                                    ● Cambios sin guardar, añade a Nivel 1 o Nivel 2
                                </span>
                            )}
                        </div>
                        {data.long_business_summary && (
                            <div className="text-sm text-[#4A4A4A] max-w-3xl mt-4" data-testid="business-summary">
                                {summaryEs ? (
                                    <p>{summaryEs}</p>
                                ) : translating ? (
                                    <>
                                        <p className="italic">{data.long_business_summary}</p>
                                        <p className="text-[10px] font-mono text-[#4A4A4A]/70 mt-1">Traduciendo al español…</p>
                                    </>
                                ) : (
                                    <p>{data.long_business_summary}</p>
                                )}
                            </div>
                        )}
                        {(!user || (plan && !plan.plan_id) || (plan?.plan_id && !plan.complete)) && (
                            <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3 max-w-3xl" data-testid="company-thesis-cta-wrap">
                                <button type="button"
                                    onClick={() => navigate(plan?.plan_id ? `/thesis/${plan.plan_id}` : `/thesis?company=${encodeURIComponent(data.ticker)}`)}
                                    className="btn-primary !px-6 !py-3 !text-sm w-full sm:w-auto shrink-0 flex items-center justify-center gap-2"
                                    data-testid="company-thesis-cta">
                                    <Sparkles size={16} /> {plan?.plan_id ? "Completar la tesis" : "Generar plan de tesis"}
                                </button>
                                <span className="text-xs text-[#4A4A4A]">
                                    {plan?.plan_id
                                        ? "Tu plan de tesis de esta empresa está a medias: faltan tesis de tendencia por generar."
                                        : <>Analiza con IA sus <strong>motores de crecimiento</strong>: una tesis de tendencia por motor, con su score, su TAM y su rol en la cadena de valor.{user ? "" : " Necesitas iniciar sesión."}</>}
                                </span>
                            </div>
                        )}
                    </div>
                    {monthlyPrice.length > 0 && (
                        <PriceHeaderChart ticker={data.ticker} name={data.name}
                            months={monthlyPrice.map(s => ({
                                ...s,
                                open: convertCur(s.open), high: convertCur(s.high),
                                low: convertCur(s.low), close: convertCur(s.close),
                            }))} currency={cur} />
                    )}
                </div>
            </div>

            {/* Qualitative thesis bridge (Thesis Engine ↔ quant dashboard) */}
            <CompanyQualCard ticker={data.ticker} hideCta refreshKey={qualRefreshKey} onProfile={setQualProfile} />

            <DataSourceNote ds={data.data_source} fs={data.fundamentals_source} currency={nativeCur} industry={data.industry} />

            {/* Hero KPIs - Ratio Compra & Venta + Perfil financiero + Combinado total */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-black mb-6" data-testid="hero-kpis">
                <div className="bg-white p-6 md:border-r border-black border-b md:border-b-0" data-testid="ratio-compra-card">
                    <div className="overline text-[#4A4A4A]">Ratio de Compra</div>
                    <div className="font-mono text-5xl sm:text-6xl font-medium mt-2" style={{ color: ratioColor(cr.ratio_compra_pct) }} data-testid="ratio-compra-value">
                        {fmtPctSigned(cr.ratio_compra_pct)}
                    </div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <span className="overline px-2 py-1 border border-black" style={{ color: ratioColor(cr.ratio_compra_pct) }} data-testid="signal-compra">{signalLabel(cr.ratio_compra_pct)}</span>
                        <HoverTip text="POC = Precio Objetivo de Compra. Precio al que la acción te parecería barata según tu fórmula.">
                            <span className="text-lg font-mono font-semibold text-black underline decoration-dotted underline-offset-2 cursor-help" data-testid="poc-label">POC {cr.poc != null ? fmtPrice(convertCur(cr.poc), cur) : "—"}</span>
                        </HoverTip>
                    </div>
                    <div className="text-xs text-[#4A4A4A] mt-3">
                        Upside hasta el precio objetivo de compra{targetDateLabel ? "," : "."}
                        {targetDateLabel && (
                            <HoverTip text={targetDateTip}>
                                <span className="underline decoration-dotted underline-offset-2 cursor-help" data-testid="target-date-compra"> objetivo a <span className="font-semibold text-[#111]">{targetDateLabel}</span>.</span>
                            </HoverTip>
                        )}
                    </div>
                </div>
                <div className="bg-white p-6 md:border-r border-black border-b md:border-b-0" data-testid="ratio-venta-card">
                    <div className="overline text-[#4A4A4A]">Ratio de Venta</div>
                    <div className="font-mono text-5xl sm:text-6xl font-medium mt-2" style={{ color: ratioColor(cr.ratio_venta_pct, "venta") }} data-testid="ratio-venta-value">
                        {fmtPctSigned(cr.ratio_venta_pct)}
                    </div>
                    <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <span className="overline px-2 py-1 border border-black" style={{ color: ratioColor(cr.ratio_venta_pct, "venta") }} data-testid="signal-venta">{signalLabel(cr.ratio_venta_pct, "venta")}</span>
                        <HoverTip text="POV = Precio Objetivo de Venta. Precio al que la acción te parecería cara según tu fórmula.">
                            <span className="text-lg font-mono font-semibold text-black underline decoration-dotted underline-offset-2 cursor-help" data-testid="pov-label">POV {cr.pov != null ? fmtPrice(convertCur(cr.pov), cur) : "—"}</span>
                        </HoverTip>
                    </div>
                    <div className="text-xs text-[#4A4A4A] mt-3">
                        Upside hasta el precio objetivo de venta{targetDateLabel ? "," : "."}
                        {targetDateLabel && (
                            <HoverTip text={targetDateTip}>
                                <span className="underline decoration-dotted underline-offset-2 cursor-help" data-testid="target-date-venta"> objetivo a <span className="font-semibold text-[#111]">{targetDateLabel}</span>.</span>
                            </HoverTip>
                        )}
                    </div>
                </div>
                <div className="bg-white p-6 border-t border-black md:border-t-0 md:border-r" data-testid="metrics-radar-card">
                    <MetricsRadar inputs={inputs} currency={data.currency} />
                </div>
                <div className="bg-white p-6 border-t border-black md:border-t-0 flex flex-col items-center justify-center text-center" data-testid="combinado-total-card">
                    <HoverTip text="Combinado total (0–100%): índice global que une calidad + potencial + validación KPI (vía el Combinado cualitativo) y la valoración (Compra/Venta). El coeficiente KPI cuenta una sola vez.">
                        <div className="overline text-[#4A4A4A] cursor-help">Combinado total</div>
                    </HoverTip>
                    <div className="font-mono text-5xl sm:text-6xl font-medium mt-2" style={{ color: qualProfile?.combined == null ? "#9CA3AF" : "#052049" }} data-testid="combinado-total-value">
                        {qualProfile?.combined != null ? `${qualProfile.combined}%` : "—"}
                    </div>
                    <div className="text-xs text-[#4A4A4A] mt-3">Calidad + potencial + KPI + valoración.</div>
                </div>
            </div>

            {cr.missing_inputs && cr.missing_inputs.length > 0 && (
                <div className="border border-[#D97706] bg-white p-3 mb-6 text-xs font-mono flex items-center gap-2" data-testid="missing-inputs-warning">
                    <AlertCircle size={14} className="text-[#D97706]" />
                    Faltan datos para calcular: {cr.missing_inputs.join(", ")}. Edita manualmente los inputs abajo.
                </div>
            )}

            {(pocPovAnomalies.length > 0 || grossMarginPreRevenue) && (
                <div className="border border-[#B32A22] bg-white p-4 mb-6" data-testid="poc-pov-anomalies">
                    <div className="flex items-center gap-2 mb-3">
                        <AlertCircle size={16} className="text-[#B32A22]" />
                        <div className="overline text-[#B32A22]">Precio objetivo anómalo — revisa los inputs</div>
                    </div>
                    <div className="space-y-3">
                        {pocPovAnomalies.map((a, i) => (
                            <div key={i} data-testid={`anomaly-${i}`} className="border-l-2 border-[#B32A22] pl-3">
                                <div className="font-mono text-sm font-semibold text-black">{a.title}</div>
                                <div className="text-xs text-[#4A4A4A] mt-1">{a.detail}</div>
                                <ul className="text-xs text-[#4A4A4A] mt-1 list-disc pl-5 space-y-1 font-sans">
                                    {a.bullets.map((b, j) => <li key={j}>{b}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                    {grossMarginPreRevenue && (
                        <div className="mt-4 pt-3 border-t border-black/10" data-testid="auto-correct-gross-margin-section">
                            <div className="mb-2">
                                <div className="overline text-[#1D7044]">Auto-corregir</div>
                                <div className="text-xs text-[#4A4A4A]">Yahoo reporta un margen bruto del {(grossMarginPreRevenue.gm * 100).toFixed(1)}% con ingresos de solo {fmtCompact(grossMarginPreRevenue.latestRevenue / 1e9)} (último año fiscal). Cuando los ingresos son tan pequeños, la mayoría de costes caen por debajo de la línea de beneficio bruto y el ratio sale inflado — es casi siempre un artefacto contable. El factor (1 + GM) = {(1 + grossMarginPreRevenue.gm).toFixed(2)} multiplica POC por ~2× sin sustento operativo real. Auto-corregimos a 50% (semiconductor / hardware pre-revenue típico). Quedará marcado como edición sin guardar para que revises antes de guardarlo en Nivel 2.</div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 items-center">
                                <div className="border border-black/10 bg-[#FAF6EE] p-2 text-xs font-mono" data-testid="correction-gross-margin">
                                    <div className="font-sans text-[#4A4A4A]">Margen bruto</div>
                                    <div className="mt-1">
                                        <span className="text-[#B32A22]">{(grossMarginPreRevenue.from * 100).toFixed(2)}%</span>
                                        <span className="mx-1 text-[#4A4A4A]">→</span>
                                        <span className="text-[#1D7044]">{(grossMarginPreRevenue.to * 100).toFixed(2)}%</span>
                                    </div>
                                    <div className="text-[10px] text-[#4A4A4A] mt-1 font-sans">Clip margen bruto a 50% (heurística para empresas casi sin ingresos con GM reportado ≥ 95% por Yahoo).</div>
                                </div>
                                <div className="flex justify-end">
                                    <button onClick={applyGrossMarginFix} className="btn-primary whitespace-nowrap" data-testid="auto-correct-gross-margin-apply">
                                        Aplicar 1 corrección
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {pocPovCorrections.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-black/10" data-testid="auto-correct-section">
                            <div className="mb-2">
                                <div className="overline text-[#1D7044]">Auto-corregir</div>
                                <div className="text-xs text-[#4A4A4A]">Aplica cada sugerencia por separado, solo las que te parezcan razonables. Quedará marcado como edición sin guardar para que revises antes de guardarlo en Nivel 2.</div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                                {pocPovCorrections.map((c, idx) => {
                                    const applied = valuesEqual(inputs?.[c.field], c.to);
                                    return (
                                        <div key={c.key} className="border border-black/10 bg-[#FAF6EE] p-2 text-xs font-mono flex flex-col" data-testid={`correction-${idx}`}>
                                            <div className="font-sans text-[#4A4A4A]">{fieldLabels[c.field] || c.field}</div>
                                            <div className="mt-1 break-words">
                                                <span className="text-[#B32A22]">{fmtCorrValue(c.field, c.from)}</span>
                                                <span className="mx-1 text-[#4A4A4A]">→</span>
                                                <span className="text-[#1D7044]">{fmtCorrValue(c.field, c.to)}</span>
                                            </div>
                                            <div className="text-[10px] text-[#4A4A4A] mt-1 font-sans">{c.reason}</div>
                                            <div className="flex justify-end mt-2 sm:mt-auto sm:pt-2">
                                                <button onClick={() => applyCorrection(c)} disabled={applied}
                                                    className={applied ? "btn-ghost whitespace-nowrap opacity-50 cursor-default" : "btn-primary whitespace-nowrap"}
                                                    data-testid={`auto-correct-apply-${c.key}`}>
                                                    {applied ? "Aplicada" : "Aplicar"}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {data.auto_projections?.flags && (() => {
                const f = data.auto_projections.flags;
                const warnings = [];
                if (f.revenue_analyst_suspicious) warnings.push("La estimación de ingresos de analistas parece anómala (muy distinta del último año real). Revisa Ingresos 2y.");
                if (f.revenue_growth_extreme) warnings.push("El crecimiento de ingresos previsto por los analistas está fuera de −50 %/+100 % anual. No se recorta: revisa el aviso en el panel de anomalías.");
                // The old regression over the FCF history (before Sept. 2026) only feeds the
                // TTM/ANUAL alternative buttons now: its warnings only when it is the method in use.
                const legacyFcf = data.auto_projections.projection_method === "historical-cagr";
                if (legacyFcf && f.fcf_history_has_negatives) warnings.push("El histórico de FCF contiene años negativos. La proyección usa un fallback simplificado.");
                if (legacyFcf && f.fcf_projection_capped) warnings.push("El crecimiento histórico del FCF se ha capado para evitar extrapolaciones extremas.");
                if (f.fcf_cagr_fallback) warnings.push("CAGR del FCF a 4 años calculado por fallback (no por la fórmula estándar 2y atrás → 2y adelante).");
                if (f.revenue_cagr_fallback) warnings.push("CAGR de ingresos a 4 años calculado por fallback.");
                if (f.fcf_bottom_up_ni_suspicious) {
                    const buRej = data.auto_projections.bottom_up_rejected_alternative;
                    if (buRej && buRej.fcf_2y != null) {
                        warnings.push(`La estimación de Net Income de analistas parece anómala (crecimiento >150% o <−50%). La proyección de FCF se ha hecho con la regresión lineal histórica como precaución. Si crees que la empresa es cíclica saliendo del valle (recuperación legítima), puedes aplicar manualmente la proyección de analistas con el botón [BU+] junto a "FCF proyectado 2y" abajo.`);
                    } else {
                        warnings.push("La estimación de Net Income de analistas parecía anómala (crecimiento >150% o <−50%). La proyección de FCF se ha hecho con la regresión lineal histórica como precaución. El valor de analistas también está fuera de un rango plausible, así que no se ofrece como opción manual.");
                    }
                }

                // TTM stale: explicit warning when the backend dropped Yahoo's TTM FCF and
                // switched to the last annual value as the base for the CAGR projection.
                const cb = data.auto_projections.cagr_breakdown;
                if (cb?.base_source === "annual_latest_yahoo_ttm_stale") {
                    const fmtBn = (n) => n == null ? "—" : (Math.abs(n) >= 1e9 ? `${(n/1e9).toFixed(2)}B` : Math.abs(n) >= 1e6 ? `${(n/1e6).toFixed(0)}M` : `${n.toFixed(0)}`);
                    const gap = (cb.fcf_ttm && cb.latest_annual) ? Math.round((1 - cb.fcf_ttm / cb.latest_annual) * 100) : null;
                    warnings.push(`Yahoo TTM FCF (${fmtBn(cb.fcf_ttm)}) cae ${gap != null ? gap + "% " : ""}por debajo del último FCF anual (${fmtBn(cb.latest_annual)}). Suele indicar dato Yahoo desactualizado o agregación trimestral incompleta. Por seguridad usamos el ANUAL como base por defecto; puedes cambiar a TTM con el botón si confías en el dato.`);
                }

                if (!warnings.length) return null;
                return (
                    <div className="border border-[#D97706] bg-[#FFF8E1] p-4 mb-6" data-testid="projection-warnings">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertCircle size={18} className="text-[#D97706]" />
                            <div className="overline text-[#D97706] text-sm">Avisos sobre las proyecciones automáticas</div>
                        </div>
                        <ul className="text-[13px] leading-snug text-[#3A3A3A] space-y-1.5 list-disc pl-5 font-sans">
                            {warnings.map((w, i) => <li key={i} data-testid={`warning-${i}`}>{w}</li>)}
                        </ul>
                        <div className="text-xs text-[#4A4A4A] mt-3 font-mono">
                            Recomendación: revisa los inputs abajo y ajústalos a tu criterio antes de tomar decisiones.
                        </div>
                    </div>
                );
            })()}

            {/* Inputs grid */}
            <div className="border border-black bg-white mb-6" data-testid="inputs-section">
                <div className="p-4 border-b border-black flex justify-between items-center">
                    <div>
                        <div className="overline text-[#B32A22]">Tus fórmulas</div>
                        <div className="font-serif text-2xl">Inputs y proyecciones</div>
                    </div>
                    <div className="flex gap-2">
                        {hasSessionEdits && <button onClick={handleReset} className="btn-ghost" data-testid="reset-inputs">Descartar cambios</button>}
                        <button onClick={handleRecalc} className="btn-primary" data-testid="recalc-button">Recalcular</button>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                        // [label, key, isPercent, magnitudeUnit, helperText, calcExplanation]
                        ["Ingresos proyectados 2y", "revenue_2y", false, true, "",
                            `Ingresos estimados a 2 años. Elige el horizonte con los botones TTM/ANUAL:\n• TTM (por defecto): parte de los ingresos TTM actuales (últimos 12 meses, Yahoo) y proyecta 2 años completos → objetivo ≈ TTM del último trimestre reportado + 2 años. Fórmula: TTM × (1+g)².\n• ANUAL: ancla en el último año fiscal completo + 2 años (mismo punto '+2y' del gráfico anual), a partir de la estimación de analistas.\nEs editable; si lo cambias, se recalcula POC/POV.`],
                        ["FCF proyectado 2y", "fcf_2y", false, true, "",
                            `Free Cash Flow estimado a 2 años. Método principal BOTTOM-UP: FCF = Flujo Operativo − CapEx, con estimaciones de analistas (NI × ratio OCF/NI histórica) e intensidad de CapEx. Si no es viable, FALLBACK por regresión del FCF histórico. Con los botones BU / TTM / ANUAL eliges la base. Es el input que más mueve la valoración.`],
                        ["Acciones en circulación", "shares_outstanding", false, true, "",
                            "Número de acciones en circulación (Yahoo). Se usa para convertir magnitudes totales (ingresos, FCF, deuda) en cifras por acción y así obtener el precio objetivo."],
                        ["Margen bruto", "gross_margin", true, false, "",
                            "(Ingresos − Coste de ventas) / Ingresos, de los últimos 12 meses (TTM, Yahoo). Mide cuánto queda tras el coste directo de producir."],
                        ["Margen operativo", "operating_margin", true, false, "",
                            "Beneficio operativo (EBIT) / Ingresos, TTM (Yahoo). Se usa para ajustar el POV respecto al POC: cuanto mayor el margen operativo, más recorrido admite el precio."],
                        ["Deuda neta", "net_debt", false, true, "",
                            "Deuda total − (caja + inversiones a corto). Positiva = deuda neta (resta valor); negativa = caja neta (suma valor). Fuente: balance de Yahoo."],
                        ["Capitalización", "market_cap", false, true, "",
                            "Valor de mercado del capital = Precio de la acción × acciones en circulación (Yahoo)."],
                        ["CAGR ingresos 4y", "revenue_cagr_4y", true, false, revCagrNote, revCagrTip],
                        ["CAGR FCF 4y", "fcf_cagr_4y", true, false, fcfCagrNote, fcfCagrTip],
                        ["Precio acción", "current_price", false, false, cur,
                            "Último cierre diario de la acción (Marketstack). Es el precio que se compara con el POC y el POV para obtener los Ratios de Compra y de Venta."],
                    ].map(([label, key, isPercent, isMagnitude, hint, calc]) => {
                        const status = fieldStatus(key);
                        const statusColor = status === "session" ? "#D97706" : status === "saved" ? "#1D7044" : "var(--text-primary)";
                        const statusLabel = status === "session" ? "Editado, sin guardar" : status === "saved" ? "Guardado por ti" : "Auto (Yahoo)";
                        const statusDot = status === "session" ? "●" : status === "saved" ? "●" : "○";
                        // Per user's spec: % suffix removed for margins & CAGRs; magnitude inputs
                        // are displayed in compact form (M/B/T) when not focused, expanding to the
                        // full number on focus for precise editing.
                        const v = inputs?.[key];
                        // Badge with the projection method for FCF 2y so the user knows whether the
                        // number came from the bottom-up analyst-based model or the historical
                        // CAGR fallback.
                        const isFcfRow = key === "fcf_2y";
                        const method = data?.auto_projections?.projection_method;
                        const bu = data?.auto_projections?.bottom_up_breakdown;
                        const buRejected = data?.auto_projections?.bottom_up_rejected_alternative;
                        const methodBadge = (isFcfRow && method) ? (() => {
                            const cb = data?.auto_projections?.cagr_breakdown;
                            // Format helpers for the tooltip
                            const fmtBn = (n) => n == null ? "—" : (Math.abs(n) >= 1e9 ? `$${(n/1e9).toFixed(2)}B` : Math.abs(n) >= 1e6 ? `$${(n/1e6).toFixed(0)}M` : `$${n.toFixed(0)}`);
                            const buText = `Método: BOTTOM-UP (analistas + intensidad CapEx)\n\nFCF +1y = OCF +1y − CapEx +1y\nOCF +1y = NI estimado por analistas × ratio OCF/NI histórica (${bu?.ocf_to_ni_ratio}x)\nCapEx +1y = intensidad CapEx/Ventas histórica (${((bu?.capex_intensity || 0) * 100).toFixed(2)}%) × Ingresos +1y\n\nAjustes aplicados:\n• Tendencia reciente: ×${bu?.trend_factor}\n• Momento de earnings: ×${bu?.earnings_mod}\n• Apalancamiento: ×${bu?.leverage_mod}`;
                            const baseSourceLabel = {
                                "ttm_yahoo": "TTM Yahoo (últimos 12 meses)",
                                "annual_latest_yahoo_ttm_stale": "último anual (TTM Yahoo era inconsistente)",
                                "annual_latest": "último anual",
                            }[cb?.base_source] || cb?.base_source || "desconocido";
                            const cagrText = cb
                                ? `Método: REGRESIÓN LINEAL (fallback)\n\nPaso 1 — Base usada: ${fmtBn(cb.base_value)} (fuente: ${baseSourceLabel})\n   • TTM Yahoo:        ${fmtBn(cb.fcf_ttm)}\n   • Último FCF anual: ${fmtBn(cb.latest_annual)}\n\nPaso 2 — Regresión lineal sobre ${cb.years_used || cb.positive_years_used || "—"} año(s) del histórico (incluye años negativos).\n   Pendiente: ${cb.slope_per_year != null ? fmtBn(cb.slope_per_year) + "/año" : "—"}\n   Tasa de crecimiento implícita: ${cb.growth_pct != null ? `${(cb.growth_pct*100).toFixed(1)}% anual` : "—"} (pendiente / |base|, capada a ±30/50%)\n\nPaso 3 — Proyección:\n   FCF +1y = base × (1 + g)\n   FCF +2y = base × (1 + g)²\n   (si la base es negativa, se usa proyección aditiva: base + n·pendiente)\n\nNota: este método se usa cuando los datos de analistas de NI no permiten el modelo bottom-up (NI negativo, histórico corto, NI suspicious, etc.).`
                                : "Método: REGRESIÓN LINEAL\nUsamos la regresión lineal sobre el FCF histórico aplicada al último FCF disponible.";

                            const buRejectedText = buRejected ? `Método: BOTTOM-UP ANALISTAS (rechazado por guard-rail)\n\nEl guard-rail rechazó la proyección automática porque los analistas esperan un crecimiento de NI de ${buRejected.ni_growth_implied_pct != null ? `${(buRejected.ni_growth_implied_pct*100).toFixed(0)}%` : "—"} (umbral: ±150%/-50%).\n\nFCF +1y = ${fmtBn(buRejected.fcf_1y)}\nFCF +2y = ${fmtBn(buRejected.fcf_2y)}\n\nÚSALO si:\n• Crees que la empresa es cíclica saliendo del valle (recuperación legítima)\n• Confías en las estimaciones de analistas para los próximos 12 meses\n\nNO LO USES si:\n• Sospechas que los datos de Yahoo están corruptos (FY incorrecto, mismatch de share count)\n• La proyección parece desproporcionada respecto al histórico` : "";

                            // Compute alternative base projections so the user can swap with one
                            // click. Available whenever the historical regression can be computed —
                            // even for bottom-up companies (offered as 2nd/3rd manual options).
                            const canSwap = cb && cb.growth_pct != null && cb.fcf_ttm && cb.latest_annual && cb.latest_annual > 0;
                            const g = canSwap ? cb.growth_pct : 0;
                            const projFromTtm = canSwap ? cb.fcf_ttm * Math.pow(1 + g, 2) : null;
                            const projFromAnnual = canSwap ? cb.latest_annual * Math.pow(1 + g, 2) : null;
                            const currentVal = inputs?.fcf_2y;
                            const within = (a, b) => a != null && b != null && Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) < 0.001;
                            const isUsingTtm = canSwap && within(currentVal, projFromTtm);
                            const isUsingAnnual = canSwap && within(currentVal, projFromAnnual);

                            const buFcfPlus2y = (method === "bottom-up" && bu) ? data?.auto_projections?.fcf_2y : null;
                            const isUsingBu = buFcfPlus2y != null && within(currentVal, buFcfPlus2y);
                            const isUsingBuRejected = buRejected?.fcf_2y != null && within(currentVal, buRejected.fcf_2y);
                            // Shared method (same formula in Gratis and Pago; services/fcf_projection.py).
                            const fp = data?.auto_projections?.fcf_projection;
                            const anVal = (method === "analyst-ni" || method === "fcf-margin" || method === "own-model" || method === "bank-ni") && fp ? data?.auto_projections?.fcf_2y : null;
                            const isUsingAn = anVal != null && within(currentVal, anVal);
                            const pctF = (x) => x == null ? "—" : `${(x * 100).toFixed(1)}%`;
                            const srcLbl = { edgar: "SEC EDGAR (cifras) + Yahoo (crecimiento de analistas)", yahoo: "Yahoo", fmp: "FMP" }[fp?.source] || fp?.source || "—";
                            const ownText = fp && method === "own-model"
                                ? `Método: PROYECCIÓN PROPIA (modo Nuestro, sin analistas)\n\nMargen FCF +2a = margen FCF hoy + (margen operativo +2a − margen operativo hoy) × (1 − ${pctF(fp.tax)} impuestos)\n\n• Margen FCF últimos 12 meses: ${pctF(fp.fcf_margin_ttm)}\n• Margen operativo${fp.amort_excluded ? " (sin la amortización de intangibles adquiridos, que no es caja)" : ""}: hoy ${pctF(fp.op_margin_ttm)} → +2a ${pctF(fp.op_margin_2y)} (tiende a su mediana de ${fp.op_margin_years} ejercicios, ${pctF(fp.op_margin_median)}; cada año conserva el ${pctF(fp.psi)} de la distancia${fp.qual != null ? `: 70 % + 10 % × tu combinado cualitativo ${Math.round(fp.qual)} %` : ", tasa base"})\n• Ingresos +2a: ${fmtBn(data?.auto_projections?.revenue_2y)}\n\n= FCF +2a: ${fmtBn(anVal)}${fp.fcf_noncash_drop ? `\n• El margen operativo de hoy está por debajo de su mediana pero el margen FCF supera el suyo (${pctF(fp.fcf_margin_median)}) en más de un punto: la caída fue contable, no de caja, así que solo cuenta la mejora por encima de la mediana` : ""}\n\nLas partidas que no son caja (amortizaciones, pagos en acciones) y el CapEx mantienen su peso actual sobre los ingresos; solo se mueve la rentabilidad operativa. Detalle completo (guía de la empresa, modelo y ajuste IA) bajo «Fuente de datos».`
                                : "";
                            const bankText = fp && method === "bank-ni"
                                ? `Método: BANCO (beneficio que reparte)\n\nEn un banco, prestar y captar depósitos va dentro del flujo operativo, así que su FCF no significa nada: se usa el beneficio neto atribuido × la parte que reparte (dividendos + recompras, media de 2 años).\n\n• Beneficio neto últimos 12 meses: ${fmtBn(fp.net_income_ttm)}\n• Parte que reparte: ${pctF(fp.payout)}${fp.payout_assumed ? " (no etiquetada: se supone un 50 %)" : ""}\n• Crecimiento previsto: ${pctF(fp.growth)} anual (${fp.growth_basis === "eps" ? "BPA de los analistas" : "ingresos de los analistas, sin previsión de BPA"})\n\n= «FCF» +2a: ${fmtBn(anVal)}\nFuente: ${srcLbl}.`
                                : "";
                            const anText = !fp ? "" : method === "bank-ni" ? bankText : method === "own-model" ? ownText : method === "analyst-ni"
                                ? `Método: BENEFICIO DE ANALISTAS → CAJA (el mismo en Gratis y Pago; solo cambian los datos)\n\nFCF +2a = Beneficio neto +2a + partidas no monetarias +2a − CapEx +2a\n\n• Beneficio neto últimos 12 meses: ${fp.ni_basis === "clean" ? `${fmtBn(fp.ni_base)} limpio (publicado ${fmtBn(fp.ni_ttm)} sin el resultado no operativo —plusvalías de participaciones, intereses— tras impuestos)` : fmtBn(fp.ni_ttm)}\n• Crecimiento previsto por los analistas: ${pctF(fp.ni_growth)} anual${fp.ni_growth_extreme ? " (fuera de −50 %/+100 %: ver avisos)" : ""}\n• Beneficio neto +2a: ${fmtBn(fp.ni_2y)}\n• Partidas no monetarias (flujo operativo − beneficio: amortizaciones, pagos en acciones, circulante${fp.ni_basis === "clean" ? ", parte en caja del resultado no operativo" : ""}) de los últimos 12 meses: ${pctF(fp.noncash_intensity)} de los ingresos → +2a ${fmtBn(fp.noncash_2y)} → flujo operativo +2a ${fmtBn(fp.ocf_2y)}\n• CapEx actual: ${pctF(fp.capex_intensity)} de los ingresos → CapEx +2a ${fmtBn(fp.capex_2y)} (sobre ingresos +2a de ${fmtBn(fp.revenue_2y)})${fp.margin_extreme ? "\n• Aviso: el beneficio o el flujo operativo +2a superan el 60 % / 75 % de los ingresos +2a (ver avisos)." : ""}\n\n= FCF +2a: ${fmtBn(anVal)}\n\nEl crecimiento se mide sobre la senda de previsiones de los analistas (de hoy a +2 años), no contra el beneficio publicado, para que los extraordinarios no lo distorsionen. Las partidas no monetarias y el CapEx escalan con los ingresos (el tamaño del negocio), no con el beneficio: así un beneficio de pico de ciclo no se multiplica por la conversión a caja de años de valle. El CapEx usa la intensidad ACTUAL, así que una empresa en plena ola de inversión la sigue pagando${fp.investment_wave ? " (ver aviso de ola de inversión)" : ""}.\nFuente: ${srcLbl}.`
                                : `Método: MARGEN DE FCF ACTUAL\n\nSin beneficio positivo o sin previsiones de beneficio de los analistas, se aplica el margen de FCF de los últimos 12 meses (${pctF(fp.fcf_margin)}) a los ingresos a +2 años (${fmtBn(fp.revenue_2y)}).\n\n= FCF +2a: ${fmtBn(anVal)}\nFuente: ${srcLbl}.`;

                            // Build a uniform [BU?] [BU+?] [TTM] [ANUAL] toolbar.
                            return (
                                <span className="inline-flex items-center gap-1 ml-2">
                                    {anVal != null && (
                                        <HoverTip dense maxWidth={380} text={anText}>
                                            <button
                                                type="button"
                                                onClick={() => updateInput("fcf_2y", anVal)}
                                                className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                style={{
                                                    background: isUsingAn ? "#1D7044" : "transparent",
                                                    color: isUsingAn ? "white" : "#1D7044",
                                                    borderColor: "#1D7044",
                                                }}
                                                data-testid="fcf-method-an"
                                                aria-pressed={isUsingAn}
                                            >{method === "analyst-ni" ? "AN" : method === "own-model" ? "NU" : method === "bank-ni" ? "BN" : "MG"}</button>
                                        </HoverTip>
                                    )}
                                    {method === "bottom-up" && buFcfPlus2y != null && (
                                        <HoverTip dense maxWidth={380} text={buText}>
                                            <button
                                                type="button"
                                                onClick={() => updateInput("fcf_2y", buFcfPlus2y)}
                                                className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                style={{
                                                    background: isUsingBu ? "#1D7044" : "transparent",
                                                    color: isUsingBu ? "white" : "#1D7044",
                                                    borderColor: "#1D7044",
                                                }}
                                                data-testid="fcf-method-badge"
                                                aria-pressed={isUsingBu}
                                            >BU</button>
                                        </HoverTip>
                                    )}
                                    {buRejected && buRejected.fcf_2y != null && (
                                        <HoverTip dense maxWidth={380} text={buRejectedText}>
                                            <button
                                                type="button"
                                                onClick={() => updateInput("fcf_2y", buRejected.fcf_2y)}
                                                className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                style={{
                                                    background: isUsingBuRejected ? "#D97706" : "transparent",
                                                    color: isUsingBuRejected ? "white" : "#D97706",
                                                    borderColor: "#D97706",
                                                }}
                                                data-testid="fcf-method-bu-plus"
                                                aria-pressed={isUsingBuRejected}
                                            >BU+</button>
                                        </HoverTip>
                                    )}
                                    {canSwap && (
                                        <>
                                            <HoverTip dense maxWidth={380} text={`HORIZONTE TTM · últimos 12 meses → +2 años\nFCF TTM × (1+g)² = ${fmtBn(cb.fcf_ttm)} × (1+${(g * 100).toFixed(1)}%)² = ${fmtBn(projFromTtm)}\n(base TTM: ${fmtBn(cb.fcf_ttm)}; objetivo ≈ TTM del último trimestre + 2 años)\n──────────\n${cagrText}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => updateInput("fcf_2y", projFromTtm)}
                                                    className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                    style={{
                                                        background: isUsingTtm ? "#052049" : "transparent",
                                                        color: isUsingTtm ? "white" : "#052049",
                                                        borderColor: "#052049",
                                                    }}
                                                    data-testid="fcf-base-ttm"
                                                    aria-pressed={isUsingTtm}
                                                >TTM</button>
                                            </HoverTip>
                                            <HoverTip dense maxWidth={380} text={`HORIZONTE ANUAL · último año fiscal completo → +2 años\nFCF anual × (1+g)² = ${fmtBn(cb.latest_annual)} × (1+${(g * 100).toFixed(1)}%)² = ${fmtBn(projFromAnnual)}\n(base: último FCF anual = ${fmtBn(cb.latest_annual)}; objetivo FY completo + 2 años)\n──────────\n${cagrText}`}>
                                                <button
                                                    type="button"
                                                    onClick={() => updateInput("fcf_2y", projFromAnnual)}
                                                    className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                    style={{
                                                        background: isUsingAnnual ? "#052049" : "transparent",
                                                        color: isUsingAnnual ? "white" : "#052049",
                                                        borderColor: "#052049",
                                                    }}
                                                    data-testid="fcf-base-annual"
                                                    aria-pressed={isUsingAnnual}
                                                >ANUAL</button>
                                            </HoverTip>
                                        </>
                                    )}
                                </span>
                            );
                        })() : null;
                        // Revenue 2y horizon toggle: TTM (trailing-12m anchor) vs ANUAL
                        // (last complete fiscal year anchor). Both come pre-computed from the
                        // backend under auto_projections. Only rendered for the revenue_2y row.
                        const isRevRow = key === "revenue_2y";
                        const revBadge = isRevRow ? (() => {
                            const ap = data?.auto_projections;
                            const rTtm = ap?.revenue_2y_ttm;
                            const rAnnual = ap?.revenue_2y_annual;
                            if (rTtm == null && rAnnual == null) return null;
                            const fmtBn = (n) => n == null ? "—" : (Math.abs(n) >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : Math.abs(n) >= 1e6 ? `$${(n / 1e6).toFixed(0)}M` : `$${n.toFixed(0)}`);
                            const currentVal = inputs?.revenue_2y;
                            const within = (a, b) => a != null && b != null && Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) < 0.001;
                            const isUsingTtm = within(currentVal, rTtm);
                            const isUsingAnnual = within(currentVal, rAnnual);
                            // Method section (how the forward growth g is derived) — mirrors
                            // the FCF tooltip so both toggles share the same structure.
                            const gb = ap?.revenue_growth_breakdown || {};
                            const gPct = gb.growth_fwd != null ? `${(gb.growth_fwd * 100).toFixed(1)}%` : "—";
                            const ownD = data?.data_source?.used === "own" ? (data.data_source.detail || {}) : null;
                            const pctR = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;
                            const revMethod = ownD
                                ? `CÓMO SE CALCULA (modo Nuestro, sin analistas)\n${ownD.guidance_used ? `Parte de la guía de la empresa: ingresos de 12 meses ${fmtBn(ownD.anchor?.revenue)} a ${ownD.anchor?.date}.` : `Parte de los ingresos de los últimos 12 meses (${fmtBn(ownD.revenue_ttm)}).`}\nCrecimiento de partida (${ownD.g0_years === 2 ? "media anual de los 2 últimos años" : "último año"}): ${pctR(ownD.g0)}${ownD.g0_years === 2 && ownD.g0_1y != null ? ` (solo el último año: ${pctR(ownD.g0_1y)})` : ""}. Cada año conserva el ${Math.round((ownD.phi || 0) * 100)}% de lo que excede del ${pctR(ownD.g_lt)} de largo plazo${ownD.qual != null ? ` (= tu combinado cualitativo, ${Math.round(ownD.qual)}%)` : " (tasa base: sin combinado cualitativo)"}:\naño 1 ${pctR(ownD.growth_years?.[0])}, año 2 ${pctR(ownD.growth_years?.[1])}${ownD.ai?.status === "ok" ? " (incluye el ajuste IA)" : ""}.`
                                : gb.source === "analyst_implied"
                                    ? `CÓMO SE CALCULA g (crecimiento anual)\nMétodo: crecimiento implícito de analistas.\ng = (Ingresos analistas +1y / Ingresos último año) − 1\n= (${fmtBn(gb.revenue_plus1y)} / ${fmtBn(gb.latest_revenue)}) − 1 = ${gPct}${gb.capped ? "\n(capado a −30%/+50%)" : ""}`
                                    : gb.source === "revenue_growth_yoy"
                                        ? `CÓMO SE CALCULA g (crecimiento anual)\nMétodo: crecimiento interanual de ingresos (Yahoo).\ng = ${gPct}${gb.capped ? " (capado a −30%/+50%)" : ""}`
                                        : `CÓMO SE CALCULA g (crecimiento anual)\nMétodo: CAGR histórico de ingresos (fallback, sin estimación de analistas).\ng = ${gPct}${gb.capped ? " (capado a −30%/+50%)" : ""}`;
                            const DIV = "\n──────────\n";
                            return (
                                <span className="inline-flex items-center gap-1 ml-2">
                                    {rTtm != null && (
                                        <HoverTip dense maxWidth={360} text={`HORIZONTE TTM · últimos 12 meses → +2 años\nIngresos TTM × (1+g)² = ${fmtBn(rTtm)}\n(base TTM: ${fmtBn(gb.total_revenue_ttm)}; objetivo ≈ TTM del último trimestre + 2 años)${DIV}${revMethod}`}>
                                            <button
                                                type="button"
                                                onClick={() => updateInput("revenue_2y", rTtm)}
                                                className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                style={{ background: isUsingTtm ? "#052049" : "transparent", color: isUsingTtm ? "white" : "#052049", borderColor: "#052049" }}
                                                data-testid="revenue-base-ttm"
                                                aria-pressed={isUsingTtm}
                                            >TTM</button>
                                        </HoverTip>
                                    )}
                                    {rAnnual != null && (
                                        <HoverTip dense maxWidth={360} text={`HORIZONTE ANUAL · último año fiscal completo → +2 años\n= ${fmtBn(rAnnual)}\n(ancla en el último año fiscal completo + 2 años, mismo punto '+2y' del gráfico anual, vía estimación de analistas)${DIV}${revMethod}`}>
                                            <button
                                                type="button"
                                                onClick={() => updateInput("revenue_2y", rAnnual)}
                                                className="overline px-1.5 py-0.5 border text-[9px] hover:opacity-70"
                                                style={{ background: isUsingAnnual ? "#052049" : "transparent", color: isUsingAnnual ? "white" : "#052049", borderColor: "#052049" }}
                                                data-testid="revenue-base-annual"
                                                aria-pressed={isUsingAnnual}
                                            >ANUAL</button>
                                        </HoverTip>
                                    )}
                                </span>
                            );
                        })() : null;
                        // Projection-horizon footnote for the 2y rows — reflects the active base
                        // (TTM → "objetivo ≈ TTM {quarter}"; annual/BU → "objetivo FY{year}").
                        let horizonNote = hint;
                        if (key === "revenue_2y" || key === "fcf_2y") {
                            const ap2 = data?.auto_projections || {};
                            const withinN = (a, b) => a != null && b != null && Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) < 0.001;
                            const tq = ap2.ttm_target_quarter;
                            const ttmLbl = tq ? `objetivo ≈ TTM ${tq}` : "objetivo ≈ TTM (último trim. + 2 años)";
                            if (key === "revenue_2y") {
                                const cv = inputs?.revenue_2y;
                                if (ap2.revenue_2y_ttm != null && withinN(cv, ap2.revenue_2y_ttm)) horizonNote = ttmLbl;
                                else if (ap2.revenue_2y_annual != null && withinN(cv, ap2.revenue_2y_annual)) horizonNote = `objetivo FY${projTargetYear}`;
                                else horizonNote = "";
                            } else {
                                const cbb = ap2.cagr_breakdown;
                                const gg = (cbb && cbb.growth_pct != null) ? cbb.growth_pct : 0;
                                const canSwap2 = cbb && cbb.growth_pct != null && cbb.fcf_ttm && cbb.latest_annual && cbb.latest_annual > 0;
                                const pTtm = canSwap2 ? cbb.fcf_ttm * Math.pow(1 + gg, 2) : null;
                                horizonNote = (pTtm != null && withinN(inputs?.fcf_2y, pTtm)) ? ttmLbl : `objetivo FY${projTargetYear}`;
                            }
                        }
                        return (
                            <div key={key} className="p-4 grid-cell group">
                                {/* The header row drags the component into the metric chart (not the field, so text selection still works). */}
                                <div className="flex items-center justify-between mb-1 cursor-grab active:cursor-grabbing" {...metricDragProps(INPUT_METRIC[key])} data-testid={`input-drag-${key}`}>
                                    <label className="overline text-[#4A4A4A] flex items-center">
                                        <GripVertical size={12} className="hidden lg:inline-block -ml-3.5 mr-0.5 opacity-0 group-hover:opacity-50" aria-hidden />
                                        <HoverTip text={calc} maxWidth={340}>
                                            <span className="cursor-help border-b border-dotted border-[#9A9A9A]" data-testid={`input-label-${key}`}>{label}</span>
                                        </HoverTip>
                                        {methodBadge}{revBadge}
                                    </label>
                                    <span className="text-xs font-mono" style={{ color: statusColor }} title={statusLabel} data-testid={`input-status-${key}`}>{statusDot}</span>
                                </div>
                                <LocaleNumberInput
                                    className="input-paper text-base"
                                    style={{ color: statusColor, fontStyle: status === "session" ? "italic" : "normal", fontWeight: status === "saved" ? 600 : 400 }}
                                    value={v}
                                    percent={isPercent}
                                    compact={isMagnitude}
                                    onChange={(num) => updateInput(key, num)}
                                    data-testid={`input-${key}`}
                                />
                                <div className="text-[10px] text-[#4A4A4A] mt-1 font-mono min-h-[14px]">{horizonNote}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Figures + growth (left), classic ratios (middle), revenue/FCF charts (right); three columns on wide screens.
                The three columns end at the same height: the last card of the first two stretches and the
                charts spread over the third (visual harmony, CLAUDE.md). */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,0.95fr)_minmax(0,1.45fr)] gap-6 mb-6">
                <div className="flex flex-col min-w-0">
                    <CompanyFigures ticker={data.ticker} price={data.current_price} currency={data.currency} />
                    {data.growth_metrics && (
                        <div className="border border-black border-t-0 bg-white flex-1" data-testid="growth-card">
                            <div className="p-4 border-b border-black bg-[#FAF6EE]">
                                <div className="overline text-[#4A4A4A]">Crecimiento y rentabilidad futura</div>
                                <div className="font-serif text-xl">Para growth y empresas no rentables</div>
                            </div>
                            <table className="w-full text-sm" data-testid="growth-ratios">
                                <tbody>
                                    {growthRows.map(([label, key, fmt, info]) => {
                                        const v = data.growth_metrics?.[key];
                                        let display = "—";
                                        if (v != null && !isNaN(v)) {
                                            if (fmt === "pct") display = fmtPct(v);
                                            else if (fmt === "rule40") display = `${v.toFixed(1)}`;
                                            else display = fmtNum(v);
                                        }
                                        return <RatioRow key={key} rowKey={key} label={label} info={info} display={display} value={v} />;
                                    })}
                                    <BreakevenRow label="Breakeven operativo (estimado)"
                                                  year={data.growth_metrics?.breakeven_year_op}
                                                  isProfitable={data.growth_metrics?.is_profitable_op}
                                                  history={data.operating_income_history}
                                                  info="Año estimado en que el beneficio operativo cruzaría 0 según la trayectoria histórica reciente (regresión lineal sobre los últimos años). Si la empresa ya es rentable se muestra ese año; si la tendencia no converge, se indica 'No converge'." />
                                    <BreakevenRow label="Breakeven FCF (estimado)"
                                                  year={data.growth_metrics?.breakeven_year_fcf}
                                                  isProfitable={data.growth_metrics?.is_profitable_fcf}
                                                  history={data.fcf_history}
                                                  info="Año estimado en que el Free Cash Flow cruzaría 0 según la trayectoria reciente. Más relevante que el beneficio operativo para empresas con mucho intangible (software): un FCF positivo es la verdadera señal de autosuficiencia." />
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="border border-black bg-white min-w-0" data-testid="classic-ratios">
                    <div className="p-4 border-b border-black">
                        <div className="overline text-[#4A4A4A]">Ratios clásicos</div>
                        <div className="font-serif text-2xl">Valoración estándar</div>
                    </div>
                    <table className="w-full text-sm">
                        <tbody>
                            {ratioRows.map(([label, key, fmt, info]) => {
                                const v = data.classic_ratios?.[key];
                                const display = v == null ? "—" : fmt === "pct" ? fmtPct(v) : fmtNum(v);
                                const rowInfo = key === "dividend_yield" ? dividendSourceText(data.dividend_yield_source, info) : info;
                                return <RatioRow key={key} rowKey={key} label={label} info={rowInfo} display={display} value={v} />;
                            })}
                            <RatioRow rowKey="gross_margin" label="Gross margin" info={marginRowsInfo.gross_margin} display={fmtPct(data.gross_margin)} value={data.gross_margin} />
                            <RatioRow rowKey="operating_margin" label="Operating margin" info={marginRowsInfo.operating_margin} display={fmtPct(data.operating_margin)} value={data.operating_margin} />
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-6 min-w-0 lg:col-span-2 xl:col-span-1">
                    <ChartBlock title="Ingresos históricos" data={revChart} qData={revChartQ} qLoading={qHistLoading} onRequestQuarterly={requestQuarterly} unit="B" color="#052049" testid="revenue-chart" userEdited={revEdited} method={revMethodLabel} onMethodToggle={revCanToggle ? onToggleRev : null} />
                    <ChartBlock title="Free Cash Flow histórico" data={fcfChart} qData={fcfChartQ} qLoading={qHistLoading} onRequestQuarterly={requestQuarterly} unit="B" color="#1D7044" type="bar" testid="fcf-chart" userEdited={fcfEdited} method={fcfMethodLabel} onMethodToggle={fcfCanToggle ? onToggleFcf : null} />
                </div>
            </div>

            {/* Three customisable metric charts side by side (6 metrics each) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-testid="metric-charts">
                {[1, 2].map((id) => <div key={id} className="min-w-0"><MetricHistoryChart ticker={data.ticker} catalog={METRIC_CATALOG} chartId={id} projection={chartProjection} /></div>)}
            </div>

            {/* Breakdown */}
            {cr.breakdown && (
                <div className="border border-black bg-white p-4" data-testid="breakdown-section">
                    <div className="overline text-[#4A4A4A] mb-2">Desglose del cálculo POC / POV</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 text-sm font-mono">
                        <Stat label="Rev/Acción 2y"
                              value={fmtNum(cr.breakdown.rev_per_share_2y)}
                              tooltip={`Ingresos 2y / Acciones en circulación = ${fmtNum(inputs?.revenue_2y || 0)} / ${fmtNum(inputs?.shares_outstanding || 0)}`} />
                        <Stat label="× Margen bruto"
                              value={fmtNum(cr.breakdown.margin_factor)}
                              tooltip={`1 + Margen bruto = 1 + ${((inputs?.gross_margin || 0) * 100).toFixed(2)}%`} />
                        <Stat label="× (FCF-NetDebt)/MCap"
                              value={cr.breakdown.x_raw_pct != null ? fmtNum(cr.breakdown.x_raw_pct) : "—"}
                              tooltip={`((FCF 2y − Deuda neta) / Capitalización) × 100 = ((${fmtNum(inputs?.fcf_2y || 0)} − ${fmtNum(inputs?.net_debt || 0)}) / ${fmtNum(inputs?.market_cap || 0)}) × 100`} />
                        <Stat label="× (FCF-NetDebt)/MCap (ajustado)"
                              value={fmtNum(cr.breakdown.fcf_minus_netdebt_over_mcap_pct)}
                              tooltip={`Ajuste sobre el factor anterior:\n• si x < 0  → 1 + x/100  (penaliza pero no anula)\n• si 0 ≤ x ≤ 1  → 1  (neutralidad financiera)\n• si 1 < x ≤ 4  → x  (sin cambio)\n• si x > 4  → sigue subiendo, cada vez menos, sin llegar nunca a 6 (5 → 4,8; 8 → 5,7; 10 → 5,9). Un FCF y una caja muy altos frente a lo que vale la empresa suben el POC, pero también suelen indicar caja sin reinvertir o sin remunerar al accionista, así que no lo multiplican sin límite`} />
                        <Stat label="× CAGR Ingresos 4y"
                              value={fmtNum(cr.breakdown.rev_growth_factor)}
                              tooltip={`1 + CAGR ingresos 4y = 1 + ${((inputs?.revenue_cagr_4y || 0) * 100).toFixed(2)}%`} />
                        <Stat label="× CAGR FCF 4y"
                              value={fmtNum(cr.breakdown.fcf_growth_factor)}
                              tooltip={`1 + CAGR FCF 4y = 1 + ${((inputs?.fcf_cagr_4y || 0) * 100).toFixed(2)}%`} />
                        <Stat label="× Margen operativo"
                              value={fmtNum(1 + (inputs?.operating_margin || 0))}
                              tooltip={`1 + Margen operativo = 1 + ${((inputs?.operating_margin || 0) * 100).toFixed(2)}% (coeficiente sin ajustar)`} />
                        <Stat label="× Margen operativo (ajustado)"
                              value={cr.breakdown.y_factor != null ? fmtNum(cr.breakdown.y_factor) : "—"}
                              tooltip={`Ajuste sobre el margen operativo (factor en POV = POC × este factor):\n• si y < 0%  → 1 + y/100  (penaliza pero no anula)\n• si 0% ≤ y ≤ 1%  → 1  (neutralidad)\n• si y > 1%  → 1 + y/100  (sin cambio)`} />
                    </div>
                </div>
            )}

            {inputs && cr.poc != null && (
                <SensitivityMatrix inputs={inputs} basePoc={cr.poc} basePov={cr.pov} basePrice={inputs.current_price} currency={cur} convertCur={convertCur} />
            )}

            {/* Navigation guard modal */}
            {pendingNav && (
                <Modal title="Cambios sin guardar" testid="nav-guard-modal">
                    <p className="text-sm text-[#4A4A4A] mb-4">
                        Tienes cambios manuales en los inputs que no se han guardado en el Nivel 2. Si sales ahora se perderán.
                    </p>
                    <p className="text-xs font-mono text-[#4A4A4A] mb-6">
                        Tip: pulsa "Guardar cambios" en la cabecera para añadir esta acción al Nivel 2 con tus valores actuales.
                    </p>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setPendingNav(null)} className="btn-ghost" data-testid="nav-guard-stay">Quedarme aquí</button>
                        <button onClick={() => { const target = pendingNav; setPendingNav(null); setSessionEdits({}); navigate(target); }} className="btn-primary" data-testid="nav-guard-leave">Salir igualmente</button>
                    </div>
                </Modal>
            )}

            {/* Confirm overwrite modal — Watchlist */}
            {confirmOverwrite && (
                <Modal title="Sobrescribir snapshot guardado" testid="overwrite-modal">
                    <p className="text-sm text-[#4A4A4A] mb-4">
                        Esta acción ya está en tu Nivel 2 con valores guardados.
                        Vas a sobrescribir ese snapshot con los valores actuales (incluidos los cambios sin guardar).
                    </p>
                    <p className="text-xs font-mono text-[#4A4A4A] mb-6">
                        ¿Continuar?
                    </p>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setConfirmOverwrite(false)} className="btn-ghost" data-testid="overwrite-cancel">Cancelar</button>
                        <button onClick={doConfirmOverwrite} className="btn-primary" data-testid="overwrite-confirm">Sobrescribir</button>
                    </div>
                </Modal>
            )}

            {/* Confirm overwrite modal — Portfolio */}
            {confirmOverwritePortfolio && (
                <Modal title="Sobrescribir snapshot en Nivel 1" testid="overwrite-portfolio-modal">
                    <p className="text-sm text-[#4A4A4A] mb-4">
                        Esta acción ya está en tu Nivel 1 con valores guardados.
                        Vas a sobrescribir ese snapshot con los valores actuales (incluidos los cambios sin guardar).
                    </p>
                    <p className="text-xs font-mono text-[#4A4A4A] mb-6">¿Continuar?</p>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setConfirmOverwritePortfolio(false)} className="btn-ghost" data-testid="overwrite-portfolio-cancel">Cancelar</button>
                        <button onClick={doConfirmOverwritePortfolio} className="btn-primary" data-testid="overwrite-portfolio-confirm">Sobrescribir</button>
                    </div>
                </Modal>
            )}

            {/* Confirm refresh (discard overrides) modal */}
            {confirmRefresh && (() => {
                const hasWlOverrides = wlEntry && wlEntry.overrides && Object.keys(wlEntry.overrides).length > 0;
                const hasPfOverrides = pfEntry && pfEntry.overrides && Object.keys(pfEntry.overrides).length > 0;
                return (
                    <Modal title="Volver a los datos de Yahoo" testid="refresh-modal">
                        <p className="text-sm text-[#4A4A4A] mb-3">
                            Refrescar recarga los fundamentales actualizados desde Yahoo Finance y descarta cualquier valor manual aplicado a esta vista.
                        </p>
                        {hasWlOverrides && (
                            <p className="text-sm text-[#B32A22] mb-3" data-testid="refresh-warning-saved">
                                ⚠ Esta acción está en tu Nivel 2 en modo MANUAL. Los valores guardados se eliminarán y volverá a modo AUTO. El ticker seguirá en el Nivel 2.
                            </p>
                        )}
                        {hasPfOverrides && (
                            <p className="text-sm text-[#B32A22] mb-3" data-testid="refresh-warning-pf">
                                ⚠ Esta acción está en tu Nivel 1 en modo MANUAL. Los valores guardados se eliminarán y volverá a modo AUTO. La posición se mantiene.
                            </p>
                        )}
                        {hasSessionEdits && (
                            <p className="text-sm text-[#D97706] mb-3" data-testid="refresh-warning-session">
                                ⚠ Tienes cambios sin guardar. Pulsa cancelar y luego "Guardar en Nivel 2" o "Guardar en Nivel 1" para conservarlos.
                            </p>
                        )}
                        <p className="text-xs font-mono text-[#4A4A4A] mb-6">¿Continuar?</p>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setConfirmRefresh(false)} className="btn-ghost" data-testid="refresh-cancel">Cancelar</button>
                            <button onClick={doRefresh} className="btn-primary" data-testid="refresh-confirm">Volver a Yahoo</button>
                        </div>
                    </Modal>
                );
            })()}
        </div>
    );
}

function Modal({ title, children, testid }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid} style={{ background: "rgba(17,17,17,0.4)" }}>
            <div className="bg-white border border-black w-full max-w-md p-6">
                <div className="overline text-[#B32A22] mb-2">Aviso</div>
                <h2 className="font-serif text-2xl mb-4">{title}</h2>
                {children}
            </div>
        </div>
    );
}

// Where the dividend yield comes from («Nuestro»: services.dividends — licensable data).
function dividendSourceText(src, info) {
    if (!src) return info;
    const day = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "";
    const origin = { esef: "informes oficiales europeos", dart: "informes oficiales coreanos (DART)", cvm: "informes oficiales brasileños (CVM)" }[src.origin] || "la SEC";
    const how = src.source === "official"
        ? (src.stopped_after
            ? `0 %: según ${origin}, no paga dividendos desde ${day(src.stopped_after)}.`
            : `Dividendos pagados en los 12 meses publicados hasta ${day(src.as_of)} (${origin}) ÷ capitalización de hoy.`)
        : src.source === "marketstack"
            ? `Dividendos por acción de los últimos 12 meses (${src.payments || 0} pagos, Marketstack${src.listing ? `, de su cotización en EE. UU. ${src.listing}` : ""}) ÷ último cierre.${src.official_tags_until ? ` Las cuentas oficiales solo etiquetan sus dividendos hasta ${day(src.official_tags_until)}.` : ""}`
            : "Sin dato de dividendos en las cuentas oficiales ni en Marketstack.";
    return `${info}\n\nCálculo: ${how}`;
}

// Left column: the company's latest reported figures (see figureRows).
function CompanyFigures({ ticker, price, currency }) {
    const hist = useMetricHistory(ticker);
    const data = hist?.status === "ok" ? hist.data : null;
    const block = data?.ttm?.dates?.length ? data.ttm : data?.annual;
    const latest = (key) => {
        const vs = block?.series?.[key] || [];
        for (let i = vs.length - 1; i >= 0; i -= 1) if (vs[i] != null) return { v: vs[i], date: block.dates[i] };
        return null;
    };
    const cur = data?.currency || currency || "";
    const asOf = latest("revenue")?.date;
    const fmtDate = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "";
    return (
        <div className="border border-black bg-white last:flex-1" data-testid="company-figures">
            <div className="p-4 border-b border-black">
                <div className="overline text-[#4A4A4A]">Cifras de la empresa</div>
                <div className="font-serif text-2xl">Últimos 12 meses</div>
                <div className="text-[10px] font-mono text-[#4A4A4A] mt-0.5 min-h-[14px]">
                    {hist?.status === "loading" ? "Cargando…"
                        : asOf ? `Publicadas${data?.origin ? ` (${data.origin})` : ""} hasta ${fmtDate(asOf)}${cur ? ` · en ${cur}` : ""}`
                        : hist && hist.status !== "ok" ? "Sin cifras publicadas disponibles" : ""}
                </div>
            </div>
            <table className="w-full text-sm">
                <tbody>
                    {figureRows.map(([label, key, unit, info]) => {
                        const last = key === "price" ? null : latest(key);
                        // A figure whose latest value is much older than the rest (Alibaba's
                        // CapEx stops in 2020: it no longer tags it with a standard concept) is
                        // not shown as current.
                        const old = last && asOf && Date.parse(asOf) - Date.parse(last.date) > 120 * 864e5;
                        const why = data?.unavailable?.[key]
                            || (old ? `Sin cifra en el último periodo publicado: el último dato es de ${fmtDate(last.date)} (la empresa dejó de publicarlo con una etiqueta estándar o no lo desglosa)` : null);
                        const v = key === "price" ? price : old ? null : last?.v;
                        const display = v == null ? (why && data ? "n/a" : "—")
                            : unit === "price" ? fmtPrice(v, cur)
                            : fmtBig(v);
                        return <RatioRow key={key} rowKey={key} label={label} display={display} value={v}
                            info={why && key !== "price" ? `${info}\n\n${why}.` : info} />;
                    })}
                </tbody>
            </table>
        </div>
    );
}

function RatioRow({ rowKey, label, info, display, value }) {
    const h = ratioHealth(rowKey, value);
    // Rows with a history can be dragged into the metric chart (MetricHistoryChart).
    const chartable = METRIC_KEYS.has(rowKey);
    return (
        <tr className={`border-b border-black/10 hover:bg-[#F5E4D4] group ${chartable ? "cursor-grab active:cursor-grabbing" : ""}`}
            {...(chartable ? metricDragProps(rowKey) : {})} data-testid={`row-${rowKey}`}>
            <td className="px-4 py-2 text-[#4A4A4A]">
                {chartable && <GripVertical size={12} className="hidden lg:inline-block -ml-3.5 mr-0.5 align-[-1px] opacity-0 group-hover:opacity-50" aria-hidden />}
                <HoverTip text={info}>
                    <span className="underline decoration-dotted underline-offset-2 cursor-help" data-testid={`ratio-label-${rowKey}`}>{label}</span>
                </HoverTip>
            </td>
            <td className="px-4 py-2 text-right font-mono" data-testid={`ratio-${rowKey}`}>
                <span className="inline-flex items-center gap-2 justify-end">
                    {h && (
                        <HoverTip text={h.label}>
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: h.color }} data-testid={`health-${rowKey}`} />
                        </HoverTip>
                    )}
                    <span>{display}</span>
                </span>
            </td>
        </tr>
    );
}

function BreakevenRow({ label, year, isProfitable, history, info }) {
    let display, color = null, sublabel = null;
    const currentYear = new Date().getUTCFullYear();
    if (isProfitable) {
        display = `Rentable (${year ?? "—"})`;
        color = "#1D7044";
    } else if (year == null) {
        // Either no history or trajectory not converging
        const hasHistory = Array.isArray(history) && history.length > 0;
        display = hasHistory ? "No converge" : "Sin datos";
        color = hasHistory ? "#B32A22" : "#4A4A4A";
        sublabel = hasHistory ? "La tendencia reciente es plana o se deteriora" : null;
    } else {
        const delta = year - currentYear;
        display = `${year}`;
        if (delta <= 1) color = "#1D7044";
        else if (delta <= 3) color = "#D97706";
        else color = "#B32A22";
        sublabel = delta <= 0 ? "Ya o en el año actual" : `≈ ${delta} año${delta > 1 ? "s" : ""} si la tendencia se mantiene`;
    }
    return (
        <tr className="border-b border-black/10 hover:bg-[#F5E4D4]" data-testid={`breakeven-row-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <td className="px-4 py-2 text-[#4A4A4A]">
                <HoverTip text={info}>
                    <span className="underline decoration-dotted underline-offset-2 cursor-help">{label}</span>
                </HoverTip>
                {sublabel && <div className="text-[10px] text-[#4A4A4A] mt-0.5 font-mono">{sublabel}</div>}
            </td>
            <td className="px-4 py-2 text-right font-mono" style={{ color }}>{display}</td>
        </tr>
    );
}

function Stat({ label, value, tooltip }) {
    return (
        <HoverTip text={tooltip}>
            <div className="grid-cell p-2 cursor-help" data-testid="stat-cell">
                <div className="overline text-[#4A4A4A] underline decoration-dotted underline-offset-2">{label}</div>
                <div className="text-base">{value}</div>
            </div>
        </HoverTip>
    );
}

function CompactTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    // Find the first non-null value across all series at this point
    const p = payload.find(x => x && x.value != null) || payload[0];
    const v = p?.value;
    return (
        <div className="bg-white border border-black px-2 py-1 font-mono text-xs">
            <div className="text-[#4A4A4A]">{label}</div>
            <div className="text-black">{fmtCompact(v)}</div>
        </div>
    );
}

// Revenue / FCF history + projection (+2 years), with the look of the metric chart
// (MetricHistoryChart: palette, light grid, axis labels that always show the last date,
// dark tooltip): a line for revenue, bars for FCF. Annual or quarterly TTM view.
function SeriesTip({ active, payload, label, projLabel }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    const name = row.kind === "proj" ? projLabel : row.kind === "approx" ? "Aproximado (interpolado)" : "Real";
    const v = row.kind === "proj" ? row.projection ?? row.value : row.historical ?? row.approx ?? row.value;
    return (
        <div className="bg-[#111111] font-mono text-xs leading-relaxed px-3 py-2 border border-black shadow-md" style={{ color: "#fff" }}>
            <div className="text-[#A89E8E] mb-0.5">{qLabel(label)}</div>
            <div className="flex items-center gap-3"><span>{name}</span><span className="ml-auto">{fmtCompact(v)}</span></div>
        </div>
    );
}

// Axis labels of the revenue/FCF charts (values in billions): short, on one line.
const nfAxis = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 });
const fmtAxisB = (v) => {
    if (!v) return "0";
    const a = Math.abs(v);
    return a >= 1000 ? `${nfAxis.format(v / 1000)}T` : a >= 1 ? `${nfAxis.format(v)}B` : `${nfAxis.format(v * 1000)}M`;
};

// "2024Q3" → "3T'24" (and "2028Q1E" → "1T'28E") on the time axis; years stay as they are.
const qLabel = (l) => String(l).replace(/^(\d{2})(\d{2})Q(\d)(E?)$/, "$3T'$2$4");

function ChartBlock({ title, data, qData, qLoading, onRequestQuarterly, unit, color, type = "line", testid, userEdited = false, method = null, onMethodToggle = null }) {
    const [mode, setMode] = useState("annual");
    const [expanded, setExpanded] = useState(false);
    const chartRef = React.useRef(null);
    const [cardRef, cardW] = useWidth();
    const [bigRef, bigW] = useWidth();
    const dark = useIsDark();
    const [jpgBusy, setJpgBusy] = useState(false);
    const downloadJpg = async () => {
        setJpgBusy(true);
        try { await downloadSvgJpg(chartRef.current, `grafico-${testid}`); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setJpgBusy(false); }
    };
    const isTTM = mode === "ttm";
    const setView = (next) => {
        setMode(next);
        if (next === "ttm" && onRequestQuarterly) onRequestQuarterly();
    };
    const toggleMode = () => setView(isTTM ? "annual" : "ttm");
    const chartData = isTTM ? (qData || []) : (data || []);
    const pal = dark ? PALETTE.dark : PALETTE.light;
    // Revenue blue, FCF green; the projection orange (purple when it is the user's own).
    const seriesColor = type === "bar" ? pal[2] : pal[0];
    const projColor = userEdited ? pal[6] : pal[1];
    const projLabel = userEdited ? "Estimado por usuario" : "Proyección";
    const surface = dark ? "#1C1813" : "#ffffff";
    const hasApprox = isTTM && chartData.some(p => p.kind === "approx");
    const Icon = type === "bar" ? BarChart3 : LineChartIcon;
    const info = `${type === "bar" ? "Flujo de caja libre (flujo operativo − CapEx)" : "Ingresos"} publicados y su proyección a 2 años (la de la fuente de datos o la que hayas escrito tú).\n\n• Anual: cada ejercicio fiscal. TTM: los 12 meses que acaban en cada trimestre; la parte antigua puede ser aproximada (interpolada desde los datos anuales, en trazo tenue).\n• En miles de millones (${unit}).`;

    const titleEl = (
        <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
            <Icon size={14} className="shrink-0" />
            <span className="cursor-pointer select-none" onClick={toggleMode} data-testid={`${testid}-title-toggle`}>
                {title}{" "}
                <HoverTip maxWidth={340} text={info}><span className="cursor-help normal-case whitespace-nowrap">ⓘ</span></HoverTip>
            </span>
        </div>
    );
    const methodEl = method ? (
        onMethodToggle ? (
            <button onClick={onMethodToggle} title="Cambiar método de proyección (TTM ⇄ Anual)"
                className="border border-black px-2 py-1 text-[11px] font-mono uppercase tracking-wide hover:bg-[#F5E4D4] whitespace-nowrap inline-flex items-center gap-1"
                data-testid={`${testid}-method`}>
                Método: {method} <ArrowLeftRight size={10} />
            </button>
        ) : (
            <span className="border border-black/40 px-2 py-1 text-[11px] font-mono uppercase tracking-wide whitespace-nowrap" data-testid={`${testid}-method`}>
                Método: {method}
            </span>
        )
    ) : null;
    const modeToggle = (
        <div className="inline-flex border border-black text-[11px] font-mono uppercase tracking-wide" role="group" aria-label="Periodicidad">
            {[["annual", "Anual"], ["ttm", "TTM"]].map(([m, lbl]) => (
                <button key={m} type="button" onClick={() => setView(m)}
                    className={`px-2.5 py-1 ${(m === "ttm") === isTTM ? "bg-black text-white" : "hover:bg-[#F5E4D4]"}`}
                    data-testid={`${testid}-mode-${m}`}>{lbl}</button>
            ))}
        </div>
    );
    const controls = <div className="flex flex-wrap items-center gap-2">{modeToggle}{methodEl}</div>;
    const swatch = (style) => <span className="inline-block w-2.5 h-2.5 shrink-0" style={style} />;
    const legendEl = (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#4A4A4A]" data-testid={`${testid}-legend`}>
            <span className="inline-flex items-center gap-1.5">{swatch({ background: seriesColor })}Real</span>
            {hasApprox && <span className="inline-flex items-center gap-1.5">{swatch({ background: seriesColor, opacity: 0.35 })}Aproximado</span>}
            <span className="inline-flex items-center gap-1.5">{swatch({ boxShadow: `inset 0 0 0 1.5px ${projColor}`, background: type === "bar" ? `${projColor}66` : "transparent" })}{projLabel}</span>
        </div>
    );
    const header = (big) => (
        <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
            <div className="min-w-0">
                {titleEl}
                <div className={`font-serif ${big ? "text-lg sm:text-2xl" : "text-xl"}`}>{isTTM ? "Últimos 12 meses (TTM)" : "Evolución anual"}</div>
            </div>
            {big ? (
                <button onClick={() => setExpanded(false)} className="text-[#4A4A4A] hover:text-black transition-colors shrink-0" title="Cerrar" data-testid={`${testid}-modal-close`}>
                    <X size={22} />
                </button>
            ) : (
                <button onClick={() => setExpanded(true)} disabled={!chartData.length} className="text-[#4A4A4A] hover:text-black transition-colors shrink-0 disabled:opacity-30" title="Ampliar gráfico" data-testid={`${testid}-expand`}>
                    <Maximize2 size={16} />
                </button>
            )}
        </div>
    );

    if (!chartData || chartData.length === 0) {
        return (
            <div className="border border-black bg-white p-4 flex-1" data-testid={testid}>
                {header(false)}
                {controls}
                <div className="h-[160px] flex items-center justify-center text-center text-xs font-mono text-[#4A4A4A] px-4">
                    {isTTM ? (qLoading ? "Cargando datos trimestrales…" : "Sin datos trimestrales disponibles") : "Sin datos disponibles"}
                </div>
            </div>
        );
    }

    const renderChart = (height, big = false) => {
        const labels = chartData.map((d) => d.year);
        const last = labels[labels.length - 1];
        const ticks = axisTicks(labels, (big ? bigW : cardW) || 300, isTTM ? 54 : 40);
        const xAxis = (
            <XAxis dataKey="year" ticks={ticks} interval={0} tick={<EdgeTick last={last} format={qLabel} />} tickLine={false}
                axisLine={{ stroke: "#7A7A7A", strokeOpacity: 0.4 }} />
        );
        const yAxis = <YAxis tickFormatter={fmtAxisB} tick={{ fontSize: 10, fill: "#7A7A7A" }} width={Y_AXIS_W} tickLine={false} axisLine={false} />;
        const grid = <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />;
        const tip = <Tooltip content={<SeriesTip projLabel={projLabel} />} cursor={type === "bar" ? { fill: "#7A7A7A", fillOpacity: 0.12 } : { stroke: "#7A7A7A", strokeOpacity: 0.5 }} />;
        const dots = chartData.length <= 16;
        const dotR = big ? 5 : 4;
        // The projection's dot only on projected points, not on the bridge point it
        // shares with the last real one.
        const ProjDot = (props) => {
            const { cx, cy, payload } = props;
            if (payload?.kind !== "proj" || cx == null || cy == null) return null;
            return <circle cx={cx} cy={cy} r={dotR} fill={projColor} stroke={surface} strokeWidth={2} />;
        };
        return (
            <ResponsiveContainer width="100%" height={height}>
                {type === "bar" ? (
                    <BarChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                        {grid}{xAxis}{yAxis}{tip}
                        <Bar dataKey="value" name="FCF" isAnimationActive={false}>
                            {chartData.map((entry, i) => (
                                <Cell key={i}
                                      fill={entry.kind === "proj" ? projColor : seriesColor}
                                      fillOpacity={entry.kind === "proj" ? 0.4 : entry.kind === "approx" ? 0.35 : 1}
                                      stroke={entry.kind === "proj" ? projColor : "none"}
                                      strokeWidth={entry.kind === "proj" ? 1.5 : 0}
                                      strokeDasharray={entry.kind === "proj" ? "3 3" : "0"} />
                            ))}
                        </Bar>
                    </BarChart>
                ) : (
                    <LineChart data={chartData} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                        {grid}{xAxis}{yAxis}{tip}
                        {hasApprox && <Line type="monotone" dataKey="approx" stroke={seriesColor} strokeOpacity={0.4} strokeWidth={2} strokeDasharray="2 3" dot={false} name="Aprox." connectNulls={false} isAnimationActive={false} />}
                        <Line type="monotone" dataKey="historical" stroke={seriesColor} strokeWidth={2}
                            dot={dots ? { r: dotR, strokeWidth: 2, stroke: surface, fill: seriesColor } : false}
                            activeDot={{ r: dotR + 1, strokeWidth: 2, stroke: surface }} name="Real" connectNulls={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="projection" stroke={projColor} strokeWidth={2} strokeDasharray="5 4" dot={<ProjDot />}
                            activeDot={{ r: dotR + 1, strokeWidth: 2, stroke: surface }} name={projLabel} connectNulls={false} isAnimationActive={false} />
                    </LineChart>
                )}
            </ResponsiveContainer>
        );
    };

    return (
        <div className="border border-black bg-white p-4 flex-1 flex flex-col" data-testid={testid}>
            {header(false)}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">{controls}{legendEl}</div>
            {/* Grows with its column so the ficha's three columns end level (visual harmony, CLAUDE.md);
                the chart sits absolutely so its own height never props the row open */}
            <div ref={cardRef} className="relative w-full flex-1 min-h-[220px]"><div className="absolute inset-0">{renderChart("100%")}</div></div>
            {expanded && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-8" style={{ background: "rgba(17,17,17,0.55)" }} data-testid={`${testid}-modal`} onClick={() => setExpanded(false)}>
                    <div className="bg-white border border-black w-full max-w-5xl p-4 sm:p-6 flex flex-col max-h-[95vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        {header(true)}
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 shrink-0">
                            {controls}
                            <div className="flex items-center gap-2 shrink-0">
                                <ShareMenu size="md" title={title} testidPrefix={`${testid}-share`}
                                    createShare={async () => shareUpload(await getSvgJpgBlob(chartRef.current), "jpg", title)} />
                                <button onClick={downloadJpg} disabled={jpgBusy} className="text-[#4A4A4A] hover:text-black transition-colors shrink-0 inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" title="Descargar en JPG" data-testid={`${testid}-modal-download-jpg`}>
                                    {jpgBusy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                                </button>
                            </div>
                        </div>
                        <div className="mb-2 shrink-0">{legendEl}</div>
                        <div ref={chartRef} className="w-full bg-white">
                            <div ref={bigRef} className="w-full h-[55vh] min-h-[240px] max-h-[520px]">{renderChart("100%", true)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// Custom tooltip used by the historical POC/POV vs price chart.
const DS_REASONS = {
    not_in_plan: "FMP no cubre esta empresa con la clave gratuita actual (haría falta un plan de pago)",
    no_key: "el servidor no tiene configurada la clave de FMP (FMP_API_KEY)",
    no_data: "FMP no tiene datos suficientes para esta empresa (estimaciones o trimestres)",
    error: "FMP no respondió correctamente",
};

// One line above the Ratio Compra/Venta: which data stack the figures come from (header
// Gratis/Pago switch), and why when "Pago" was requested but couldn't be served.
const FS_REASONS = {
    edgar_missing: "la empresa no presenta sus cuentas en la SEC (cotiza fuera de EE. UU. o no es una empresa con estados financieros estándar)",
    edgar_incomplete: "la SEC no tiene el flujo de caja completo de esta empresa (p. ej. bancos o capex con etiqueta propia)",
    edgar_stale: "la última cifra publicada en la SEC tiene más de 200 días (empresa extranjera con solo informe anual, o informe trimestral con retraso)",
    edgar_inconsistent: "las cifras de la SEC no cuadran entre sí (probable problema de etiquetado)",
    fx: "no se pudo obtener el tipo de cambio",
    no_price: "no hay precio",
    error: "no se pudo leer la SEC",
};

// Name of the official source behind "edgar" figures: the SEC, the European annual
// reports (ESEF) for companies listed in Europe, the Korean ones (DART) or the Brazilian
// ones (CVM), all read like the SEC's.
const officialSrc = (o) => o === "esef"
    ? { short: "ESEF", long: "los informes oficiales europeos (anuales ESEF; en España, también los semestrales de la CNMV)" }
    : o === "dart"
        ? { short: "DART", long: "los informes oficiales coreanos (DART: anual, semestral y trimestrales)" }
        : o === "cvm"
            ? { short: "CVM", long: "los informes oficiales brasileños (CVM: anual y trimestrales)" }
            : { short: "SEC EDGAR", long: "la SEC (EDGAR)" };
// Official sources other than the SEC (no earnings release to read the guidance from).
const NON_SEC = ["esef", "dart", "cvm"];
const COUNTRY_ADJ = { esef: "europeas", dart: "coreanas", cvm: "brasileñas" };
// The official history's name in running text ("Histórico de …").
const historySrc = (o) => o === "esef" ? "los informes oficiales europeos (ESEF)"
    : o === "dart" ? "los informes oficiales coreanos (DART)" : o === "cvm" ? "los informes oficiales brasileños (CVM)" : "la SEC";
const fsReason = (fs, reason) => (fs?.origin === "esef" && (reason || fs?.reason) === "edgar_stale")
    ? "el último informe anual oficial europeo (ESEF) tiene más de 200 días: en Europa solo se publica etiquetado el informe anual"
    : (fs?.origin === "dart" && (reason || fs?.reason) === "edgar_stale")
        ? "el último informe oficial coreano (DART) tiene más de 200 días"
        : (fs?.origin === "cvm" && (reason || fs?.reason) === "edgar_stale")
            ? "el último informe oficial brasileño (CVM) tiene más de 200 días"
        : FS_REASONS[reason || fs?.reason];

const FS_FIELD_LABELS = {
    gross_margin: "margen bruto", operating_margin: "margen operativo", net_debt: "deuda neta",
    shares: "nº de acciones", fcf_projection: "proyección de FCF (cambio de signo)",
};

// Approximations behind the SEC figures of the last 12 months (services.edgar_overlay
// and, for «Nuestro», services.own_projection), shown as a visible warning.
// Industries whose groups usually own a finance arm (car loans and leases, equipment
// financing): the consolidated cash includes it, its funding isn't fully tagged as debt.
const CAPTIVE_FINANCE_INDUSTRIES = ["Auto Manufacturers", "Farm & Heavy Construction Machinery"];

const fmtBn = (v, cur) => v == null ? "—" : `${(v / 1e9).toLocaleString("es-ES", { maximumFractionDigits: 1 })} B ${cur || ""}`.trim();

// The finance arm itself (services.captive_finance): its size — a liability that is its raw
// material, and the loans it will collect, with the risk of customers not paying — and its
// share of the group's profit. Only what the report gives.
function captiveFinanceText(f, cur) {
    if (!f) return "";
    const name = f.name || "su financiera";
    const out = [];
    if (f.debt != null) {
        const times = f.market_cap > 0 ? ` (${(f.debt / f.market_cap).toFixed(1).replace(".", ",")} veces lo que vale la empresa en bolsa)` : "";
        out.push(`${name} debe ${fmtBn(f.debt, cur)}${times}${f.date ? ` a ${f.date}` : ""}. Esa deuda es su materia prima: un pasivo, dinero que debe y con el que presta y alquila a clientes y concesionarios; por eso no cuenta como deuda en la fórmula.`);
    }
    if (f.loans != null) out.push(`A cambio tiene ${fmtBn(f.loans, cur)} en préstamos y alquileres a clientes: un activo que en teoría irá cobrando a medida que se los devuelvan. El riesgo es que los clientes no paguen (impagos), que se traslada a las cuentas del grupo.`);
    const p = f.profit;
    if (p && p.finance != null && p.group != null) {
        const per = `${p.months ? `en los ${p.months} meses` : "en el periodo"}${p.end ? ` a ${p.end}` : ""}, ${p.basis === "operating" ? "resultado operativo" : "antes de impuestos"}`;
        if (p.finance > 0 && p.industrial != null && p.industrial < 0) {
            out.push(`En beneficio, ${name} ganó ${fmtBn(p.finance, cur)} mientras la parte industrial perdía ${fmtBn(-p.industrial, cur)} (${per}).`);
        } else if (p.finance > 0 && p.group > 0) {
            out.push(`En beneficio, ${name} aporta el ${Math.round(p.finance / p.group * 100)} % del grupo: ${fmtBn(p.finance, cur)} de ${fmtBn(p.group, cur)} (${per}).`);
        } else if (p.finance < 0) {
            out.push(`En beneficio, ${name} perdió ${fmtBn(-p.finance, cur)} (${per}).`);
        }
    }
    if (f.consolidated === false) out.push(`Parte de su financiación es una empresa conjunta que el grupo no consolida: estas cifras no la incluyen.`);
    return out.length ? ` ${out.join(" ")}` : "";
}

// What the ficha says about a manufacturer's industrial net debt (services.captive_finance).
function captiveNote(cf, cur) {
    if (!cf) return null;
    if (cf.status === "pending") return "Fabricante con financiera propia (préstamos y alquileres a clientes, que es un banco): su balance mezcla la caja y la deuda de la financiera. Se está leyendo en sus resultados la deuda neta de su parte industrial, que se usará al volver a cargar; mientras, la del grupo.";
    const group = cf.group_net_debt != null ? ` La del grupo, con la financiera, es ${fmtBn(cf.group_net_debt, cur)}.` : "";
    if (cf.status === "read") {
        const net = cf.net_debt <= 0 ? `caja neta de ${fmtBn(-cf.net_debt, cur)}` : `deuda neta de ${fmtBn(cf.net_debt, cur)}`;
        const parts = cf.cash != null && cf.debt != null ? ` (caja ${fmtBn(cf.cash, cur)}, deuda ${fmtBn(cf.debt, cur)})` : "";
        const where = cf.origin === "sec" ? `su comunicado de resultados en la SEC${cf.filing_date ? ` (${cf.filing_date})` : ""}` : "su web";
        return `Fabricante con financiera propia (préstamos y alquileres a clientes, que es un banco): la deuda neta es la de su parte industrial${cf.segment ? ` («${cf.segment}»)` : ""}, sin la financiera, a ${cf.date}: ${net}${parts}, leída con IA de ${where}.${group}${captiveFinanceText(cf.finance, cur)} Ingresos, márgenes y FCF siguen siendo los del grupo.`;
    }
    return `Fabricante con financiera propia (préstamos y alquileres a clientes, que es un banco): su balance mezcla la caja y la deuda de la financiera y no se ha podido leer la de su parte industrial${cf.pending ? " (se está buscando en sus resultados; se usará al volver a cargar)" : ""}, así que se toma deuda neta 0 (neutral).${group}${captiveFinanceText(cf.finance, cur)}`;
}

function approxNotes(fs, d, industry, currency) {
    const out = [];
    const bank = d?.bank || fs?.bank;
    if (bank) out.push(`Banco: la fórmula usa sus equivalentes. Ingresos = margen bruto del banco (intereses netos + comisiones netas + dividendos); margen bruto = margen de intereses ÷ intereses cobrados; margen operativo = beneficio antes de impuestos ÷ ingresos; «FCF» = beneficio neto atribuido × ${bank.payout != null ? `${Math.round(bank.payout * 100)} %` : "la parte"} que reparte (dividendos + recompras, media de 2 años${bank.assumed ? "; no etiquetado: se supone un 50 %" : ""}); deuda neta 0 (depósitos y bonos son su materia prima, no deuda).`);
    if (fs?.sec_ticker) out.push(`Cifras de la SEC de su cotización en EE. UU. (${fs.sec_ticker}): su bolsa local no publica cuentas etiquetadas, así que se usan las que la empresa presenta allí${fs.history_currency && fs.history_currency !== currency ? `, convertidas de ${fs.history_currency} a ${currency} al tipo de hoy` : ""}${fs.ads_ratio ? ` y pasadas a acciones de aquí (1 ADR = ${fs.ads_ratio} acciones)` : ""}. El precio es el de esta cotización: la valoración puede diferir de la de ${fs.sec_ticker}.`);
    if (fs?.leases_carried) out.push(`Últimos 12 meses del informe semestral de la CNMV, que no desglosa los arrendamientos: la deuda incluye los del último informe anual. Su CapEx incluye intangibles; al anual (solo inmovilizado material) se le suma la diferencia entre semestres.`);
    const eu = d?.origin === "esef";
    if (d?.interim_6k) out.push(eu
        ? `Últimos 12 meses a ${d.interim_6k.date}: en Europa solo se etiqueta el informe anual; ingresos, márgenes y FCF se han leído con IA del último informe de resultados de la web de la empresa, no de datos etiquetados. Deuda, caja y acciones son las del informe anual (${d.annual_base}).`
        : `Últimos 12 meses a ${d.interim_6k.date}: la empresa (extranjera) solo etiqueta en la SEC su informe anual; ingresos, márgenes y FCF se han leído con IA de su comunicado de resultados (6-K del ${d.interim_6k.filing_date}), no de datos etiquetados. Deuda, caja y acciones son las del informe anual (${d.annual_base}).`);
    else if (d?.annual_base) out.push(eu
        ? `En Europa solo se etiqueta el informe anual: se parte de él (${d.annual_base}) y se proyecta hasta dentro de 2 años desde hoy. Su último informe de resultados se busca y se lee en segundo plano y se usará al volver a cargar.`
        : `La empresa (extranjera) solo etiqueta en la SEC su informe anual: se parte de él (${d.annual_base}) y se proyecta hasta dentro de 2 años desde hoy. Su comunicado trimestral más reciente se lee en segundo plano y se usará al volver a cargar.`);
    if (d?.fcf_margin_annual) out.push("El comunicado no trae flujo de caja: FCF de 12 meses = margen FCF del último ejercicio × ingresos de 12 meses.");
    if (d?.fcf_from_net_income) out.push("Sin flujo de caja de los últimos 12 meses en la SEC: se usa el beneficio neto en su lugar (válido para negocios con poco CapEx).");
    if (fs?.fcf_estimated_ttm) out.push("La empresa dejó de publicar su CapEx trimestral en formato estándar: FCF con el CapEx estimado a su propio % de ingresos histórico.");
    if (fs?.op_from_pretax) out.push("La empresa no etiqueta su resultado operativo: margen operativo = beneficio antes de impuestos − resultado no operativo.");
    if (fs?.ttm_from_ytd) out.push("Falta un informe trimestral en la SEC: los 12 meses se calculan con los acumulados del año (último ejercicio + acumulado de este año − el del año anterior).");
    if (fs?.debt_from_fin_liab) out.push("La empresa no etiqueta sus préstamos con el estándar: la deuda son sus líneas de «pasivos financieros» del balance (puede quedarse corta si etiqueta parte de su deuda con etiquetas propias).");
    if (d?.captive) out.push(captiveNote(d.captive, currency));
    else if (CAPTIVE_FINANCE_INDUSTRIES.includes(industry) && fs?.used === "edgar") out.push("Fabricante que suele tener financiera propia (préstamos y alquiler a clientes): la caja del grupo incluye la de su financiera y su financiación no siempre se etiqueta como deuda, así que la deuda neta puede salir mejor de lo real y el factor x, alto. Tómalo con cautela.");
    if (fs?.gross_margin_no_cogs) out.push("La empresa no tiene coste de ventas en sus cuentas (todos sus costes son gastos operativos): margen bruto 100 %.");
    return out;
}

function ApproxWarning({ notes }) {
    if (!notes.length) return null;
    return (
        <div className="mb-3 border border-[#D97706] bg-[#FFF7ED] px-3 py-2 text-xs text-[#7C2D12] break-words" data-testid="data-approx-warning">
            <span className="font-semibold">Aviso sobre los datos:</span>
            <ul className="list-disc pl-4 mt-0.5">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </div>
    );
}

// «Nuestro»: how our own +2y projection was built, layer by layer (guidance, model, AI).
function OwnProjectionDetail({ d, currency }) {
    const fmtB = (v) => v == null ? "—" : `${(v / 1e9).toLocaleString("es-ES", { maximumFractionDigits: 1 })} B ${currency}`;
    const pct = (v, sign = false) => v == null ? "—" : `${sign && v >= 0 ? "+" : ""}${(v * 100).toFixed(1).replace(".", ",")} %`;
    const g = d.guidance || {};
    const ai = d.ai || {};
    const pending = d.enrichment === "pending";
    const gy = d.growth_years || [];
    // The model's own result (V8's rule and banks' payout included), not recomputed here.
    const fcfm2y = d.revenue_2y ? d.fcf_2y / d.revenue_2y
        : d.op_margin_2y != null && d.op_margin_ttm != null ? d.fcf_margin_ttm + (d.op_margin_2y - d.op_margin_ttm) * (1 - d.tax) : d.fcf_margin_ttm;
    return (
        <details className="mb-3 border border-black/10 bg-[#FAF6EE] px-3 py-2 text-xs text-[#4A4A4A]" data-testid="own-projection-detail">
            <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-wider text-[#111]">
                Cómo se ha hecho la proyección propia{pending ? " · guía e IA en preparación" : ""}
            </summary>
            <div className="mt-2 space-y-2 break-words">
                <div>
                    <div className="font-semibold text-[#111]">1 · Guía de la empresa{d.guidance_used && g.origin === "press" ? " (dada en la llamada de resultados, vía prensa)" : NON_SEC.includes(g.origin) ? "" : " (comunicado de resultados en la SEC)"}</div>
                    {pending ? <div>Leyendo el último comunicado… Vuelve a cargar la ficha en un minuto.</div>
                        : d.enrichment === "no_llm" ? <div>El servidor no tiene IA configurada: se usa solo el modelo.</div>
                            : d.guidance_used ? (
                                <div>
                                    {g.revenue_low != null
                                        ? <>Ingresos previstos {fmtB(g.revenue_low)} – {fmtB(g.revenue_high)}</>
                                        : <>Crecimiento previsto {pct(g.revenue_growth_low, true)} – {pct(g.revenue_growth_high, true)}{g.growth_basis === "constant_currency" ? " a tipo de cambio constante" : g.growth_basis === "organic" ? " orgánico" : ""} sobre {fmtB(d.anchor?.prior)} del mismo periodo del año anterior (SEC) → {fmtB(d.anchor?.period_revenue)}</>}
                                    {" "}para el {g.period_type === "year" ? "ejercicio" : "trimestre"} que acaba ~{d.anchor?.date}{g.under_review ? " (la empresa dice que la está revisando; se toma la vigente)" : ""}
                                    {g.gross_margin != null ? `, margen bruto ${pct(g.gross_margin)}` : ""}
                                    {d.op_margin_start != null && d.op_margin_start !== d.op_margin_ttm ? ` → margen operativo guiado ${pct(d.op_margin_start)}` : ""}.
                                    {" "}Ingresos de 12 meses a esa fecha: {fmtB(d.anchor?.revenue)}.
                                    {g.quote ? <div className="italic mt-0.5">«{g.quote}»</div> : null}
                                    {g.source_url ? <a href={g.source_url} target="_blank" rel="noreferrer" className="underline">{g.origin === "press" ? `${g.source_title || "Fuente"}${g.source_date ? ` (${g.source_date})` : ""}` : `Comunicado del ${g.filing_date}`}</a> : null}
                                    {g.origin === "press" ? <div className="mt-0.5">La empresa no la incluye en lo que presenta a la SEC: se toma de la prensa que recoge sus declaraciones (menos fiable que un documento oficial).</div> : null}
                                </div>
                            ) : g.has_guidance && g.revenue_scope === "partial" ? (
                                <div>La empresa solo guía una parte de sus ingresos (un producto o segmento), que no sirve como previsión del total: se parte de los últimos 12 meses.{g.quote ? <span className="italic"> «{g.quote}»</span> : null}</div>
                            ) : NON_SEC.includes(g.origin) ? <div>No se lee: las empresas {COUNTRY_ADJ[g.origin]} no presentan a la SEC el comunicado de resultados del que se toma la guía. Se parte de los últimos 12 meses de sus informes oficiales.</div>
                            : <div>Ni el comunicado (y sus anexos) ni la prensa recogen una previsión de ingresos de la empresa que encaje tras las últimas cifras de la SEC: se parte de los últimos 12 meses.</div>}
                    {d.guidance_used && g.margin_basis === "non_gaap" ? <div className="mt-0.5">El margen que guía es «non-GAAP» (sin pagos en acciones y otros gastos): no se usa, porque el histórico de la SEC es GAAP; el margen parte del de los últimos 12 meses.</div> : null}
                </div>
                <div>
                    <div className="font-semibold text-[#111]">2 · Modelo sobre el histórico {d.origin === "esef" ? "de sus informes oficiales europeos" : d.origin === "dart" ? "de sus informes oficiales coreanos (DART)" : d.origin === "cvm" ? "de sus informes oficiales brasileños (CVM)" : "de la SEC"}</div>
                    <div>Ingresos: crecimiento de partida {pct(d.g0, true)} ({d.g0_launch ? `hace 2 años la empresa facturaba menos de un 25 % de lo actual (lanzamiento o auge), así que la media simple no es representativa: pesa un 75 % el último año (${pct(d.g0_1y, true)}) y un 25 % el anterior, sin pasar de la media de 2 años` : d.g0_years === 2 ? `media anual de los 2 últimos años, para que un solo año de auge o de bache no marque toda la proyección${d.g0_1y != null ? `; solo el último año: ${pct(d.g0_1y, true)}` : ""}` : "último año: no hay cifra de hace 2 años"}); cada año conserva el {Math.round((d.phi || 0) * 100)} % de lo que excede del {pct(d.g_lt)} de largo plazo {d.qual != null ? `(= tu combinado cualitativo de esta empresa, ${pct(d.qual / 100)})` : "(tasa base: la empresa no tiene combinado cualitativo en tus tesis)"}{d.brake_delay ? `; el trimestre que guía la empresa crece un ${pct(d.g0_guided, true)} interanual, más que su crecimiento de partida (está acelerando según sus propias cifras), así que el primer año no se frena y el freno empieza en el año 2` : ""} → año 1 {pct(gy[0], true)}, año 2 {pct(gy[1], true)}{ai.status === "ok" ? " (con el ajuste de la IA)" : ""}. Ingresos +2a: <b>{fmtB(d.revenue_2y)}</b>.</div>
                    {d.amort_excluded && (
                        <div>Margen operativo sin la amortización de intangibles adquiridos ({pct(d.amort_excluded.share)} de los ingresos hoy, {pct(d.amort_excluded.last_share)} en el último ejercicio): no es caja y se va apagando (la compra ya está en las acciones y la deuda neta), así que el margen de hoy y su mediana se miden sin ella para que la vuelta a la mediana no reste FCF que no se va a perder.</div>
                    )}
                    <div>Margen operativo{d.amort_excluded ? " (sin esa amortización)" : ""}: hoy {pct(d.op_margin_ttm)}{d.om_loss_floor ? "; la empresa pierde dinero y su mediana de ejercicios comparables es peor que hoy, así que no vuelve hacia pérdidas mayores: parte del actual" : d.op_margin_years >= 2 ? <>, mediana de {d.op_margin_years} ejercicios comparables {pct(d.op_margin_median)}; cada año conserva el {Math.round((d.psi || 0) * 100)} % de su distancia a la mediana {d.qual != null ? `(70 % + 10 % × tu combinado cualitativo ${pct(d.qual / 100)})` : "(tasa base, sin combinado cualitativo)"}</> : "; sin ejercicios comparables (con ingresos de al menos un 25 % de los actuales) para una mediana, así que no vuelve hacia ninguna y parte del actual"}{(d.ai_margin_adj || 0) !== 0 ? `, más el ajuste de la IA (${pct(d.ai_margin_adj, true)})` : ""} → +2a {pct(d.op_margin_2y)}.</div>
                    {d.fcf_noncash_drop ? (
                        <div>Margen FCF +2a: se parte del margen FCF de hoy, {pct(d.fcf_margin_ttm)}. Su margen operativo de hoy ({pct(d.op_margin_ttm)}) está por debajo de su mediana ({pct(d.op_margin_median)}), pero su caja no cayó: el margen FCF de hoy supera en más de un punto el suyo habitual ({pct(d.fcf_margin_median)}). La caída fue contable (deterioros, pérdidas por ventas de negocios), no de caja, así que la vuelta del margen operativo hasta su mediana ya está en el FCF de hoy y no se suma otra vez: solo cuenta lo que pase de la mediana, tras impuestos.
                            {" "}Margen FCF +2a = {pct(d.fcf_margin_ttm)} + (margen operativo +2a {pct(d.op_margin_2y)} − mediana {pct(d.op_margin_median)}, si es positivo) × (1 − {Math.round((d.tax || 0) * 100)} %) = {pct(fcfm2y)} → FCF +2a: <b>{fmtB(d.fcf_2y)}</b>.</div>
                    ) : (
                        <div>Margen FCF +2a: se parte del margen FCF de hoy, {pct(d.fcf_margin_ttm)}, y se le suma lo que mejore (o se le resta lo que empeore) el margen operativo, tras impuestos: cada punto de margen operativo de más es caja de más.
                            {" "}Margen FCF +2a = {pct(d.fcf_margin_ttm)} + (margen operativo +2a {pct(d.op_margin_2y)} − hoy {pct(d.op_margin_ttm)}) × (1 − {Math.round((d.tax || 0) * 100)} %) = {pct(fcfm2y)} → FCF +2a = ingresos +2a × margen FCF +2a: <b>{fmtB(d.fcf_2y)}</b>.</div>
                    )}
                    <div>Las partidas que no son caja (amortizaciones, pagos en acciones) y el CapEx mantienen su peso actual sobre los ingresos.</div>
                </div>
                <div>
                    <div className="font-semibold text-[#111]">3 · Ajuste con IA y búsqueda web (sin estimaciones de analistas)</div>
                    {pending ? <div>En preparación…</div>
                        : ai.status === "ok" ? (
                            <div>
                                Calculado sobre el modelo base (freno 35 % / 70 %){!d.ai_net_of_brake ? (d.ai_weighted ? " y aplicado según su evidencia (abajo)" : " y sumado entero") : ""}: crecimiento año 1 {pct(ai.growth_adj_y1, true)}, año 2 {pct(ai.growth_adj_y2, true)}, margen operativo {pct(ai.margin_adj, true)} (sin límite: lo que justifiquen los hechos). {ai.rationale}
                                {d.qual != null && d.ai_net_of_brake && Array.isArray(d.ai_growth_adj) && (
                                    <div className="mt-0.5">
                                        Aplicado con tu combinado: año 1 {pct(d.ai_growth_adj[0], true)}, año 2 {pct(d.ai_growth_adj[1], true)}, margen {pct(d.ai_margin_adj, true)}. Tu combinado ya frena menos que el modelo base, y un ajuste al alza de la IA corrige lo mismo (un modelo demasiado prudente): al ajuste se le descuenta lo que tu freno ya ha subido sobre el modelo base (crecimiento = el mayor entre tu modelo y el modelo base + IA). Si tu freno no sube la cifra (empresa que decrece: un combinado alto alarga la caída) o el ajuste es a la baja (hechos que el freno no conoce), se aplica entero.
                                    </div>
                                )}
                                {d.ai_weighted && ai.evidence && (() => {
                                    const LV = { firme: "firme", moderada: "moderada", debil: "débil", ninguna: "sin evidencia válida" };
                                    const KIND = { official: "oficial", press: "prensa", forecast: "web de previsiones (no cuenta)", unlisted: "fuera de la búsqueda (no cuenta)" };
                                    const TYPE = { guia: "guía", cartera: "cartera", contrato: "contrato", resultado: "resultado publicado (ya en el modelo, no cuenta)", declaracion: "declaración", sector: "sector" };
                                    const rows = [["Crecimiento año 1", "growth_y1", ai.growth_adj_y1, d.ai_growth_adj?.[0]],
                                                  ["Crecimiento año 2", "growth_y2", ai.growth_adj_y2, d.ai_growth_adj?.[1]],
                                                  ["Margen operativo", "margin", ai.margin_adj, d.ai_margin_adj]];
                                    return (
                                        <div className="mt-0.5">
                                            Peso según la evidencia (firme por cartera o contratos al alza: × hasta 2; firme a la baja: × 1,25; moderada: entero; débil: la mitad; sin evidencia válida: 0):
                                            <ul className="list-disc pl-4">
                                                {rows.map(([label, key, raw, applied]) => {
                                                    const e = ai.evidence[key] || {};
                                                    return (
                                                        <li key={key} className="break-words">
                                                            {label}: IA {pct(raw, true)} · evidencia {LV[e.level] || "—"} → aplicado <b>{pct(applied, true)}</b>
                                                            {(e.facts || []).length > 0 && (
                                                                <ul className="list-[circle] pl-4">
                                                                    {e.facts.map((f, i) => (
                                                                        <li key={i} className={f.kind === "official" || f.kind === "press" ? (f.type === "resultado" ? "opacity-60" : "") : "opacity-60 line-through"}>
                                                                            {f.fact} <span className="text-[#666]">({TYPE[f.type] || f.type || "—"}, {KIND[f.kind] || f.kind}{f.url && f.url.startsWith("http") ? <>, <a href={f.url} target="_blank" rel="noreferrer" className="underline">fuente</a></> : f.url === "sec:backlog" ? ", cartera de la SEC" : ""})</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    );
                                })()}
                                {ai.backlog && (
                                    <div className="mt-0.5">
                                        Cartera de pedidos firmada (SEC) que se le ha dado a la IA: {fmtB(ai.backlog.rpo)} a {ai.backlog.date}{ai.backlog.rpo_1y ? ` (${pct(ai.backlog.rpo / ai.backlog.rpo_1y - 1, true)} en un año)` : ""}{ai.backlog.pct != null ? `; la empresa espera facturar el ${Math.round(ai.backlog.pct * 100)} % en los próximos ${ai.backlog.pct_months || 12} meses (${fmtB(ai.backlog.rpo * ai.backlog.pct)})` : "; sin dato de qué parte se factura en 12 meses"}.
                                    </div>
                                )}
                                {(ai.sources || []).length > 0 && (
                                    <ul className="list-disc pl-4 mt-0.5">
                                        {ai.sources.map((s2, i) => <li key={i}><a href={s2.url} target="_blank" rel="noreferrer" className="underline">{s2.title || s2.url}</a></li>)}
                                    </ul>
                                )}
                            </div>
                        ) : <div>{d.enrichment === "no_llm" ? "Sin IA configurada en el servidor." : ai.status === "no_sources" ? "La búsqueda no devolvió fuentes: sin ajuste." : "Sin ajuste disponible."}</div>}
                </div>
            </div>
        </details>
    );
}

function DataSourceNote({ ds, fs, currency, industry }) {
    if (!ds) return null;
    if ((ds.requested === "fmp" || ds.requested === "own") && ds.used !== ds.requested) {
        return (
            <div className="mb-3 border border-[#D97706] bg-[#FFF7ED] px-3 py-2 text-xs text-[#7C2D12]" data-testid="data-source-fallback">
                {ds.requested === "own"
                    ? <><span className="font-semibold">Modo Nuestro: sin datos suficientes.</span> {fsReason(fs, ds.reason) || DS_REASONS.error}. Nuestro no calcula Ratio Compra/Venta para esta empresa (cambia a Gratis o Pago para ver el de los analistas); el resto de la ficha muestra los datos Gratis.</>
                    : <><span className="font-semibold">Modo Pago:</span> {DS_REASONS[ds.reason] || DS_REASONS.error}. Se muestran los datos Gratis.</>}
            </div>
        );
    }
    if (ds.used === "own") {
        const d = ds.detail || {};
        return (
            <>
                <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#4A4A4A]" data-testid="data-source-note">
                    <span>Fuente de datos: <span className="font-semibold text-[#111]">{officialSrc(d.origin).short} + proyección propia · Nuestro</span></span>
                    <HoverTip text={`Fuente: Nuestro.\n\n• Cifras publicadas hasta hoy: ${officialSrc(d.origin).long} a ${d.base_date || "—"}${NON_SEC.includes(d.origin) ? "" : ", igual que en Gratis"}. Precio: mercado.\n• Proyecciones a +2 años (${d.target_date || "—"}): sistema propio sin analistas — guía de la empresa, modelo sobre su histórico y ajuste con IA a partir de hechos publicados. Despliega el detalle debajo.`} maxWidth={380}>
                        <button type="button" className="text-[#9A9A9A] hover:text-[#052049]" aria-label="Cómo se obtienen los datos"><Info size={13} /></button>
                    </HoverTip>
                </div>
                <ApproxWarning notes={approxNotes(fs, d, industry, currency)} />
                <OwnProjectionDetail d={d} currency={currency} />
            </>
        );
    }
    const d = ds.detail || {};
    const fmtB = (v) => v == null ? "—" : `${(v / 1e9).toLocaleString("es-ES", { maximumFractionDigits: 1 })} B ${currency}`;
    const pct = (v) => v == null ? "—" : `${(v * 100).toFixed(1).replace(".", ",")} %`;
    const tip = ds.used === "fmp"
        ? `Fuente: FMP (modo Pago).\n\nHorizonte: 2 años tras el último trimestre publicado (${d.last_reported_quarter || "—"} → ${d.target_date || "—"}), siguiendo la senda de estimaciones de analistas de FMP.\n\n• Ingresos a +2 años: ${fmtB(d.revenue_2y_analysts)} según analistas (${pct(d.revenue_growth_analysts)}/año)${d.revenue_growth_extreme ? " (fuera de −50 %/+100 % anual: ver avisos)" : ""}.\n• Beneficio neto a +2 años: ${fmtB(d.net_income_2y_analysts)} según analistas${d.net_income_growth_extreme ? " (fuera de −50 %/+100 % anual)" : ""}.\n• FCF a +2 años: el mismo método que en Gratis (beneficio neto previsto × conversión a caja de la empresa − CapEx con la intensidad actual), con los datos de FMP.\n• Márgenes: últimos 12 meses. Deuda neta: último balance trimestral. Precio y capitalización: FMP.`
        : fs?.used === "edgar"
            ? `Fuente: Gratis.\n\n• Cifras publicadas (ingresos y FCF de los últimos 12 meses e histórico anual, márgenes, deuda, caja y nº de acciones): ${officialSrc(fs.origin).long}, a ${fs.as_of || "—"}${fs.history_currency ? `, presentadas en ${fs.history_currency} y convertidas al tipo de cambio actual` : ""}${fs.ads_ratio ? `; 1 ADR = ${fs.ads_ratio} acciones ordinarias` : ""}${fs.sec_ticker ? ` (la empresa también cotiza en EE. UU. como ${fs.sec_ticker} y presenta allí sus cuentas)` : ""}.\n• Previsiones (crecimiento de analistas) y precio: Yahoo Finance. Las proyecciones a +2 años aplican el crecimiento de los analistas entre la fecha de la última cifra de la SEC y 2 años después, sobre las cifras de la SEC.\n• FCF = flujo de caja operativo − capex, como en el histórico.\n• Deuda: financiera (incluidos convertibles) + arrendamientos cuyo pago queda fuera del flujo operativo (financieros en US GAAP; todos con IFRS 16), para no contar dos veces ni omitir pagos respecto al FCF. Caja = efectivo + inversiones a corto plazo.${fs.share_unit_fix ? "\n• La empresa etiquetó en la SEC algunos nº de acciones en miles; corregido con su propio beneficio por acción." : ""}${fs.debt_assumed_zero ? "\n• La empresa no declara deuda financiera en la SEC desde hace más de un año: se toma deuda = 0 (más arrendamientos, si los hay)." : ""}${fs.yahoo_fields?.length ? `\n• Sin dato en la SEC, se usa Yahoo para: ${fs.yahoo_fields.map(k => FS_FIELD_LABELS[k] || k).join(", ")}.` : ""}`
            : `Fuente: Gratis. Todo de Yahoo Finance: ${fsReason(fs) || "sin datos de la SEC"}.`;
    return (
        <>
            <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#4A4A4A]" data-testid="data-source-note">
                <span>Fuente de datos: <span className="font-semibold text-[#111]">{ds.used === "fmp" ? "FMP · Pago" : fs?.used === "edgar" ? `${officialSrc(fs.origin).short} + Yahoo · Gratis` : "Yahoo · Gratis"}</span></span>
                <HoverTip text={tip} maxWidth={380}>
                    <button type="button" className="text-[#9A9A9A] hover:text-[#052049]" aria-label="Cómo se obtienen los datos"><Info size={13} /></button>
                </HoverTip>
            </div>
            {ds.used !== "fmp" && fs?.used === "edgar" && <ApproxWarning notes={approxNotes(fs, null, industry, currency)} />}
        </>
    );
}

const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
// "2024-09" -> { label: "sep'24" (axis), tip: "sep 2024" (tooltip) }
const monthLabels = (ym) => {
    const m = /^(\d{4})-(\d{2})$/.exec(ym || "");
    if (!m) return { label: ym || "", tip: ym || "" };
    const mon = MONTHS_ES[parseInt(m[2], 10) - 1];
    return { label: `${mon}'${m[1].slice(2)}`, tip: `${mon} ${m[1]}` };
};

function PriceHeaderTooltip({ active, payload, currency }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const up = d.close >= d.open;
    return (
        <div className="bg-white border border-black px-3 py-2 font-mono text-xs">
            <div className="text-[#4A4A4A] mb-1">{d.tip}</div>
            <div>Apertura: {fmtPrice(d.open, currency)}</div>
            <div>Máximo: {fmtPrice(d.high, currency)}</div>
            <div>Mínimo: {fmtPrice(d.low, currency)}</div>
            <div>Cierre: <span style={{ color: up ? "#1D7044" : "#B32A22" }} className="font-semibold">{fmtPrice(d.close, currency)}</span></div>
        </div>
    );
}

// Draws one OHLC candle — Recharts has no built-in candlestick mark, so this is
// the standard workaround: a <Bar> bound to a `range: [low, high]` tuple dataKey (which
// makes Recharts compute the axis domain and hand this shape a `y`/`height` spanning
// exactly [low, high] in pixels, low->high baseline included), then the open/close body
// is placed by linearly interpolating within that same [y, y+height] span — no direct
// scale access needed, so it can't go stale/blank like a <Customized> overlay can when
// nothing else on the chart is bound to a real dataKey. Small green/red bodies + a thin
// high-low wick, no dots — reads as a compact trading-panel widget, not a line chart.
const CandleShape = (props) => {
    const { x, y, width, height, payload } = props;
    if (payload.open == null || payload.close == null) return null;
    const { open, close, high, low } = payload;
    const up = close >= open;
    const color = up ? "#1D7044" : "#B32A22";
    const span = (high - low) || 1;
    const yFor = (v) => y + ((high - v) / span) * height;
    const cx = x + width / 2;
    const candleW = Math.max(1, Math.min(4, width * 0.55));
    const yOpen = yFor(open), yClose = yFor(close);
    const bodyTop = Math.min(yOpen, yClose);
    const bodyH = Math.max(1.5, Math.abs(yClose - yOpen));
    return (
        <g>
            <line x1={cx} x2={cx} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
            <rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={bodyH} fill={color} />
        </g>
    );
};

// Monthly candles for the most recent HEADER_YEARS calendar years (~120 bars) — dense
// enough to read as a continuous trading chart.
const HEADER_YEARS = 10;

// Compact candlestick chart for the company header (monthly OHLC from /price-history) —
// a quick visual reference for the price trend right where you see the company overview.
function PriceHeaderChart({ ticker, name, months, currency }) {
    const [expanded, setExpanded] = useState(false);
    const chartRef = React.useRef(null);
    const [jpgBusy, setJpgBusy] = useState(false);
    const downloadJpg = async () => {
        setJpgBusy(true);
        try { await downloadSvgJpg(chartRef.current, `precio-${ticker}`); }
        catch { toast.error("No se pudo exportar el gráfico"); }
        finally { setJpgBusy(false); }
    };
    const allData = (months || [])
        .filter((s) => s.close != null && s.month)
        .map((s) => {
            const low = s.low ?? s.close, high = s.high ?? s.close;
            return { period: s.month, year: parseInt(s.month.slice(0, 4), 10), ...monthLabels(s.month), open: s.open, high, low, close: s.close, range: [low, high] };
        });
    const maxYear = allData.length ? Math.max(...allData.map((d) => d.year)) : null;
    const data = maxYear == null ? [] : allData.filter((d) => d.year >= maxYear - HEADER_YEARS + 1);
    if (!data.length) return null;

    // Axis labels at a fixed whole-year step counted BACK from the last candle, so every
    // gap between labels is identical (Recharts' own interval="preserveStartEnd" forces
    // the last label in regardless of step, leaving an odd-sized final gap).
    const tickLabels = (maxTicks) => {
        const step = 12 * Math.max(1, Math.ceil(data.length / 12 / maxTicks));
        const out = [];
        for (let i = data.length - 1; i >= 0; i -= step) out.unshift(data[i].label);
        return out;
    };

    const lows = data.map((d) => d.low);
    const highs = data.map((d) => d.high);
    const yMin = Math.min(...lows), yMax = Math.max(...highs);
    const pad = (yMax - yMin) * 0.08 || Math.abs(yMax) * 0.08 || 1;
    const yDomain = [yMin - pad, yMax + pad];

    const titleEl = (
        <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
            <TrendingUp size={13} className="text-[#052049]" /> Precio
            <HoverTip text="Velas mensuales (apertura/máximo/mínimo/cierre) de los últimos 10 años — el mismo histórico que alimenta el gráfico POC/POV vs Precio más abajo, aquí aislado para una vista rápida. Verde = cierre del mes por encima de su apertura; rojo = por debajo.">
                <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" aria-label="Más información" data-testid="price-header-info">
                    <Info size={13} />
                </button>
            </HoverTip>
        </div>
    );

    const renderChart = (height, maxTicks) => (
        <div className="rounded-sm h-full" style={{ background: "#FDF0EC" }}>
            <ResponsiveContainer width="100%" height={height}>
                <ComposedChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }} barCategoryGap="2%">
                    <CartesianGrid stroke="#00000010" vertical={false} />
                    <XAxis dataKey="label" type="category" ticks={tickLabels(maxTicks)} interval={0} tick={{ fontSize: 10, fill: "#7A7A7A", fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#00000022" }} tickLine={false} />
                    <YAxis domain={yDomain} tick={{ fontSize: 10, fill: "#7A7A7A", fontFamily: "IBM Plex Mono" }} width={42} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v)} />
                    <Tooltip content={<PriceHeaderTooltip currency={currency} />} cursor={{ stroke: "#00000022" }} />
                    <Bar dataKey="range" shape={CandleShape} isAnimationActive={false} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div className="border border-black/20 bg-white p-3 w-full lg:w-[30rem] lg:self-start shrink-0" data-testid="price-header-chart">
            <div className="flex items-start justify-between gap-2 mb-1">
                {titleEl}
                <button onClick={() => setExpanded(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" title="Ampliar" data-testid="price-header-expand">
                    <Maximize2 size={14} />
                </button>
            </div>
            {renderChart(170, 5)}
            {expanded && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-8" style={{ background: "rgba(17,17,17,0.55)" }} data-testid="price-header-modal" onClick={() => setExpanded(false)}>
                    <div className="bg-white border border-black w-full max-w-3xl p-4 sm:p-6 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-2 mb-2 shrink-0">
                            <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                                <TrendingUp size={20} className="shrink-0" /> <span className="truncate">{name} · Precio</span>
                            </h2>
                            <button onClick={() => setExpanded(false)} className="text-[#7A7A7A] hover:text-[#052049] shrink-0" title="Cerrar" data-testid="price-header-modal-close"><X size={20} /></button>
                        </div>
                        <div className="flex items-center justify-end gap-3 mb-2 shrink-0">
                            <ShareMenu size="md" title={`Precio ${ticker} · VegaValor`} testidPrefix="price-header-share"
                                createShare={async () => shareUpload(await getSvgJpgBlob(chartRef.current), "jpg", `Precio ${ticker}`)} />
                            <button onClick={downloadJpg} disabled={jpgBusy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" title="Descargar en JPG" data-testid="price-header-modal-download-jpg">
                                {jpgBusy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                            </button>
                        </div>
                        <div ref={chartRef} className="w-full h-[50vh] min-h-[240px] max-h-[420px]">
                            {renderChart("100%", 10)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const SENS_AXES = [
    { key: "revenue_2y", label: "Ingresos 2y", isPercent: false },
    { key: "fcf_2y", label: "FCF 2y", isPercent: false },
    { key: "gross_margin", label: "Margen bruto", isPercent: true },
    { key: "operating_margin", label: "Margen operativo", isPercent: true },
    { key: "revenue_cagr_4y", label: "CAGR Ingresos 4y", isPercent: true },
    { key: "fcf_cagr_4y", label: "CAGR FCF 4y", isPercent: true },
];

function SensitivityMatrix({ inputs, basePoc, basePov, basePrice, currency, convertCur }) {
    const [axisX, setAxisX] = useState("fcf_cagr_4y");
    const [axisY, setAxisY] = useState("revenue_cagr_4y");
    const [delta, setDelta] = useState(10); // ±%
    const [metric, setMetric] = useState("ratio_compra"); // "ratio_compra" | "ratio_venta" | "poc" | "pov"

    if (axisX === axisY) {
        // Auto-pick a different default to avoid degenerate 1D matrix
        const fallback = SENS_AXES.find(a => a.key !== axisX)?.key || axisX;
        if (axisY !== fallback) setAxisY(fallback);
    }

    const steps = [-delta, 0, +delta];

    // Bump an input value by a relative %.
    const bump = (val, pct) => (val == null ? null : val * (1 + pct / 100));

    const cells = steps.map(dy => steps.map(dx => {
        const next = { ...inputs };
        next[axisX] = bump(inputs[axisX], dx);
        next[axisY] = bump(inputs[axisY], dy);
        const r = computeCustomRatios(next);
        return { dx, dy, r };
    }));

    const valFor = (r) => {
        if (!r) return null;
        if (metric === "ratio_compra") return r.ratio_compra_pct;
        if (metric === "ratio_venta") return r.ratio_venta_pct;
        if (metric === "poc") return r.poc;
        if (metric === "pov") return r.pov;
        return null;
    };
    const fmtCell = (v) => {
        if (v == null || isNaN(v)) return "—";
        if (metric === "ratio_compra" || metric === "ratio_venta") return fmtPctSigned(v);
        const conv = convertCur ? convertCur(v) : v;
        return fmtPrice(conv, currency);
    };
    const cellColor = (v) => {
        if (v == null || isNaN(v)) return "#4A4A4A";
        if (metric === "ratio_compra") return ratioColor(v, "compra");
        if (metric === "ratio_venta") return ratioColor(v, "venta");
        // POC/POV vs base price: green if above price, red if below
        if (basePrice == null) return "#111";
        const pct = (v / basePrice - 1) * 100;
        return ratioColor(pct, metric === "pov" ? "venta" : "compra");
    };

    // Base value to highlight the center cell
    const baseVal = metric === "ratio_compra" ? (basePoc != null && basePrice ? (basePoc / basePrice - 1) * 100 : null)
                  : metric === "ratio_venta" ? (basePov != null && basePrice ? (basePov / basePrice - 1) * 100 : null)
                  : metric === "poc" ? basePoc
                  : basePov;

    const labelFor = (key) => SENS_AXES.find(a => a.key === key)?.label || key;
    const stepFmt = (s) => s === 0 ? "Base" : `${s > 0 ? "+" : ""}${s}%`;

    return (
        <div className="border border-black bg-white p-4 mt-6" data-testid="sensitivity-matrix">
            <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                    <div className="overline text-[#B32A22]">Modo Sensibilidad</div>
                    <div className="font-serif text-2xl">¿Cuán frágil es tu valoración?</div>
                </div>
                <div className="ml-auto grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <label className="flex flex-col">
                        <span className="overline text-[#4A4A4A]">Eje X</span>
                        <select value={axisX} onChange={e => setAxisX(e.target.value)} className="input-paper font-mono" data-testid="sens-axis-x">
                            {SENS_AXES.map(a => <option key={a.key} value={a.key} disabled={a.key === axisY}>{a.label}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col">
                        <span className="overline text-[#4A4A4A]">Eje Y</span>
                        <select value={axisY} onChange={e => setAxisY(e.target.value)} className="input-paper font-mono" data-testid="sens-axis-y">
                            {SENS_AXES.map(a => <option key={a.key} value={a.key} disabled={a.key === axisX}>{a.label}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col">
                        <span className="overline text-[#4A4A4A]">Métrica</span>
                        <select value={metric} onChange={e => setMetric(e.target.value)} className="input-paper font-mono" data-testid="sens-metric">
                            <option value="ratio_compra">Ratio Compra</option>
                            <option value="ratio_venta">Ratio Venta</option>
                            <option value="poc">POC</option>
                            <option value="pov">POV</option>
                        </select>
                    </label>
                    <label className="flex flex-col">
                        <span className="overline text-[#4A4A4A]">Δ {delta}%</span>
                        <input type="range" min="2" max="30" step="1" value={delta} onChange={e => setDelta(parseInt(e.target.value, 10))} data-testid="sens-delta" />
                    </label>
                </div>
            </div>

            <p className="text-xs text-[#4A4A4A] mb-3 max-w-3xl">
                Muestra cómo cambia <span className="font-mono font-semibold">{({ ratio_compra: "Ratio Compra", ratio_venta: "Ratio Venta", poc: "POC", pov: "POV" })[metric]}</span> al mover <span className="font-mono">{labelFor(axisX)}</span> y <span className="font-mono">{labelFor(axisY)}</span> ±{delta}%. Si los extremos varían mucho respecto a la celda central, tu tesis es <span className="text-[#B32A22]">frágil</span>. Si apenas cambian, la valoración es <span className="text-[#1D7044]">robusta</span>.
            </p>

            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="sens-table">
                    <thead>
                        <tr>
                            <th className="px-2 py-2 text-left overline text-[#4A4A4A] border-b border-r border-black/20"></th>
                            {steps.map(s => (
                                <th key={s} className="px-2 py-2 text-center overline text-[#4A4A4A] border-b border-black/20">
                                    {labelFor(axisX)} {stepFmt(s)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {cells.map((row, i) => (
                            <tr key={steps[i]}>
                                <td className="px-2 py-2 overline text-[#4A4A4A] border-r border-black/20 whitespace-nowrap">
                                    {labelFor(axisY)} {stepFmt(steps[i])}
                                </td>
                                {row.map((c, j) => {
                                    const v = valFor(c.r);
                                    const isBase = c.dx === 0 && c.dy === 0;
                                    return (
                                        <td key={j}
                                            className={`px-3 py-3 text-center font-mono ${isBase ? "bg-[#FAF6EE] font-semibold" : ""} border border-black/10`}
                                            data-testid={`sens-cell-${i}-${j}`}
                                            style={{ color: cellColor(v) }}>
                                            {fmtCell(v)}
                                            {isBase && baseVal != null && (
                                                <div className="text-[10px] text-[#4A4A4A] font-mono mt-1 font-normal">(base)</div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
